// src/modulos/TUV/TUVReportPage.js

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import ExcelJS from 'exceljs';
import * as pdfjsLib from 'pdfjs-dist';
import {
    getBlob, ref, uploadBytes, getDownloadURL, deleteObject,
    uploadBytesResumable, listAll
} from 'firebase/storage';
import {
    getFirestore, collection, addDoc, getDocs, deleteDoc, doc,
    query, orderBy, getDoc, updateDoc, setDoc, where
} from 'firebase/firestore';
import { firebaseApp, auth as firebaseAuth, storage as firebaseStorage, db as firebaseDb } from '../../firebaseConfig';
import styles from '../Registers/Registers.module.css';
import PendingEvaluationsList from '../../components/global/PendingEvaluations/PendingEvaluationsList';
import { webhookService } from '../../services/webhookService';
import { HiOutlineDocumentDownload, HiOutlineTrash, HiOutlineCheckCircle, HiOutlineClock } from 'react-icons/hi';
import { getFunctions, httpsCallable } from 'firebase/functions';

// --------------------------------------------------------------
// Firebase initialization
// --------------------------------------------------------------
const storage = firebaseStorage;
const db = firebaseDb;
const auth = firebaseAuth;
const functions = getFunctions(firebaseApp);

// --------------------------------------------------------------
// PDF.js worker setup
// --------------------------------------------------------------
let pdfWorkerReady = null;
const ensurePdfWorker = async () => {
    if (pdfWorkerReady !== null) return pdfWorkerReady;

    try {
        const res = await fetch('/pdf.worker.min.mjs', { method: 'HEAD' });
        if (res.ok) {
            pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';
            pdfWorkerReady = true;
        } else {
            console.warn(
                'PDF worker (/pdf.worker.min.mjs) não encontrado em public/. ' +
                'Extração de PDF desabilitada. Copie o arquivo de ' +
                'node_modules/pdfjs-dist/build/pdf.worker.min.mjs para public/.'
            );
            pdfWorkerReady = false;
        }
    } catch (err) {
        console.warn('Falha ao verificar PDF worker:', err);
        pdfWorkerReady = false;
    }
    return pdfWorkerReady;
};

// --------------------------------------------------------------
// API Configuration
// --------------------------------------------------------------
const CHECKLIST_API_BASE = 'https://integration.checklistfacil.com.br';
const CHECKLIST_API_KEY = 'XBH0PhGXxSQ1ve3Kaf5PT2s6bLDOYD8o6LbexfJDz5kfVBqcfFsktHobZXkhTyzuJeTiF0EZC76I1EwXV4syCSrEtWw0QeBSjL9uMIqbiepfFFwmO3AMsiHOFJ6kNsn0';

// --------------------------------------------------------------
// Cache e Rate Limiting
// --------------------------------------------------------------
const evaluationCache = new Map();
const requestTimestamps = new Map();
const CACHE_DURATION = 5 * 60 * 1000;
const RATE_LIMIT_INTERVAL = 61000;

const checkRateLimit = (evaluationId) => {
    const lastRequest = requestTimestamps.get(evaluationId);
    if (lastRequest) {
        const timeSinceLastRequest = Date.now() - lastRequest;
        if (timeSinceLastRequest < RATE_LIMIT_INTERVAL) {
            const remainingSeconds = Math.ceil((RATE_LIMIT_INTERVAL - timeSinceLastRequest) / 1000);
            throw new Error(`RATE_LIMIT: Aguarde ${remainingSeconds} segundos antes de buscar esta avaliação novamente.`);
        }
    }
};

const recordRequest = (evaluationId) => {
    requestTimestamps.set(evaluationId, Date.now());
};

const fetchWithRetry = async (url, options, maxRetries = 3, delay = 60000) => {
    for (let i = 0; i < maxRetries; i++) {
        try {
            const response = await fetch(url, options);
            if (response.status === 429) {
                const waitTime = delay * (i + 1);
                await new Promise(resolve => setTimeout(resolve, waitTime));
                continue;
            }
            return response;
        } catch (error) {
            if (i === maxRetries - 1) throw error;
            await new Promise(resolve => setTimeout(resolve, delay * (i + 1)));
        }
    }
    throw new Error('Max retries reached');
};

// --------------------------------------------------------------
// Generic bounded-concurrency mapper
// --------------------------------------------------------------
const mapWithConcurrency = async (items, limit, worker) => {
    const results = new Array(items.length);
    let nextIndex = 0;

    const runners = Array.from(
        { length: Math.min(Math.max(1, limit), items.length) },
        async () => {
            while (true) {
                const current = nextIndex++;
                if (current >= items.length) return;
                results[current] = await worker(items[current], current);
            }
        }
    );

    await Promise.all(runners);
    return results;
};

// --------------------------------------------------------------
// ZIP signature validation helper.
//
// Every .xlsx / .docx file is a ZIP archive, and every ZIP archive
// starts with the bytes "PK\x03\x04" (0x50 0x4B 0x03 0x04). JSZip
// and ExcelJS both throw the very unhelpful
// "Can't find end of central directory : is this a zip file ?"
// error when handed something else (HTML error page, truncated file,
// wrong format renamed to .xlsx, etc.). This helper turns that into
// an actionable error message.
// --------------------------------------------------------------
const assertValidZipBuffer = (buffer, sourceLabel) => {
    if (!buffer || buffer.byteLength < 4) {
        throw new Error(
            `O arquivo "${sourceLabel}" está vazio ou corrompido (${buffer ? buffer.byteLength : 0} bytes). ` +
            `Verifique se ele existe e não foi truncado.`
        );
    }

    const head = new Uint8Array(buffer.slice(0, 4));
    const isZip = head[0] === 0x50 && head[1] === 0x4B &&
                  head[2] === 0x03 && head[3] === 0x04;

    if (!isZip) {
        // Show a short preview of what we actually received. In the
        // SPA-fallback case this will be "<!doctype html>..." which
        // makes the problem obvious at a glance.
        let preview = '';
        try {
            const decoder = new TextDecoder('utf-8', { fatal: false });
            preview = decoder
                .decode(buffer.slice(0, 80))
                .replace(/\s+/g, ' ')
                .trim();
        } catch {
            preview = '(não foi possível decodificar)';
        }

        const hexHead = Array.from(head)
            .map(b => b.toString(16).padStart(2, '0'))
            .join(' ');

        throw new Error(
            `O arquivo "${sourceLabel}" não é um XLSX/DOCX válido — ` +
            `os primeiros bytes são [${hexHead}] em vez de [50 4b 03 04] ("PK.."). ` +
            `Conteúdo recebido começa com: "${preview}". ` +
            `Isso normalmente significa que o arquivo não existe no servidor ` +
            `(o SPA fallback devolveu o index.html) ou está corrompido.`
        );
    }

    return buffer;
};

// --------------------------------------------------------------
// Enhanced Parser
// --------------------------------------------------------------
const parseProductDetailsSimple = (text) => {
    if (!text || text.trim() === '') {
        return [];
    }

    const lines = text.split(/\r?\n/);
    const products = [];

    const isDate = (str) => /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/.test(str);
    const isQuantity = (str) => /^[\d,.]+$/.test(str);

    const extractProductName = (text) => {
        const match = text.match(/^(.+?)\s*\(/);
        return match ? match[1].trim() : text.trim();
    };

    for (const line of lines) {
        const trimmedLine = line.trim();
        if (!trimmedLine) continue;

        const parenMatch = trimmedLine.match(/\(([^)]+)\)/);
        if (!parenMatch) continue;

        const contentInsideParens = parenMatch[1];
        const afterParens = trimmedLine.substring(trimmedLine.indexOf(')') + 1).trim();

        let batches = [];
        if (contentInsideParens.includes(',')) {
            batches = contentInsideParens.split(',').map(b => b.trim()).filter(Boolean);
        } else {
            batches = contentInsideParens.split(/\s+/).filter(Boolean);
        }

        const afterParts = afterParens.split(/\s+/);
        let quantity = '';
        let productionDate = '';
        let expiryDate = '';

        let dateCount = 0;
        for (const part of afterParts) {
            if (isQuantity(part) && !quantity) {
                quantity = part;
            } else if (isDate(part)) {
                if (dateCount === 0) {
                    productionDate = part;
                    dateCount++;
                } else if (dateCount === 1) {
                    expiryDate = part;
                    dateCount++;
                }
            }
        }

        const productName = extractProductName(trimmedLine);
        const isPattern1 = batches.length > 1;

        for (let i = 0; i < batches.length; i++) {
            const batch = batches[i];
            if (batch) {
                products.push({
                    description: (isPattern1 && i !== 0) ? '' : productName,
                    batchNumber: batch,
                    quantity: (isPattern1 && i !== 0) ? '' : quantity,
                    productionDate: (isPattern1 && i !== 0) ? '' : productionDate,
                    expiryDate: (isPattern1 && i !== 0) ? '' : expiryDate
                });
            }
        }
    }

    return products;
};

// --------------------------------------------------------------
// Fetch evaluation data from Checklist Fácil API
// --------------------------------------------------------------
const fetchEvaluationData = async (evaluationId, forceRefresh = false) => {
    if (!forceRefresh && evaluationCache.has(evaluationId)) {
        const cached = evaluationCache.get(evaluationId);
        const cacheAge = Date.now() - cached.timestamp;
        if (cacheAge < CACHE_DURATION) {
            return cached.data;
        }
    }

    checkRateLimit(evaluationId);

    const versions = ['v2', 'v3', 'v4'];
    let lastError = null;

    for (const version of versions) {
        try {
            const url = `${CHECKLIST_API_BASE}/${version}/evaluations/${evaluationId}`;

            const response = await fetchWithRetry(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${CHECKLIST_API_KEY}`,
                    'Accept-Language': 'pt-br',
                    'Content-Type': 'application/json'
                }
            });

            if (response.status === 404) {
                lastError = new Error(`Avaliação não encontrada na ${version}`);
                continue;
            }

            if (!response.ok) {
                const errorText = await response.text();
                lastError = new Error(`Erro ${response.status}: ${errorText}`);
                continue;
            }

            const data = await response.json();
            console.log(`API JSON Response (Version ${version}):`, data);

            const statusCode = data.status || data.situacao;
            if (statusCode !== 6) {
                const statusMap = { 1: 'Não Iniciado', 2: 'Em Andamento', 3: 'Em Análise', 4: 'Reprovado', 5: 'Reaberto', 6: 'Concluído' };
                throw new Error(`A avaliação está com status "${statusMap[statusCode] || 'Desconhecido'}". A API só retorna dados de avaliações CONCLUÍDAS (status 6).`);
            }

            recordRequest(evaluationId);
            evaluationCache.set(evaluationId, { data: data, timestamp: Date.now(), version: version });
            return data;
        } catch (error) {
            console.error(`Erro na ${version}:`, error);
            lastError = error;
        }
    }

    throw lastError || new Error(`Avaliação ${evaluationId} não encontrada. Verifique se o ID está correto e se a avaliação está concluída.`);
};

// --------------------------------------------------------------
// Checkbox helpers
// --------------------------------------------------------------
const CB_CHECKED = '☒';
const CB_UNCHECKED = '☐';

const evaluativeToYesNo = (evaluative) => {
    if (evaluative === 3 || evaluative === 8) return 'yes';
    if (evaluative === 2 || evaluative === 7) return 'no';
    if (evaluative === 1 || evaluative === 6) return 'na';
    return null;
};

// --------------------------------------------------------------
// Format date from API
// --------------------------------------------------------------
const formatApiDate = (dateStr) => {
    if (!dateStr) return '';
    try {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return dateStr;
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    } catch (e) {
        return dateStr;
    }
};

// --------------------------------------------------------------
// Extract just the date part from a date string for grouping
// --------------------------------------------------------------
const extractDatePart = (dateStr) => {
    if (!dateStr) return '';
    const match = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
    if (match) {
        const day = match[1].padStart(2, '0');
        const month = match[2].padStart(2, '0');
        const year = match[3].padStart(4, '0');
        return `${day}/${month}/${year}`;
    }
    if (dateStr.match(/^\d{1,2}:\d{2}/)) {
        const now = new Date();
        const day = now.getDate().toString().padStart(2, '0');
        const month = (now.getMonth() + 1).toString().padStart(2, '0');
        const year = now.getFullYear();
        return `${day}/${month}/${year}`;
    }
    return dateStr;
};

// --------------------------------------------------------------
// Attachment helpers
// --------------------------------------------------------------
const getUrlExtension = (url) => {
    try {
        const pathname = new URL(url).pathname;
        const match = pathname.match(/\.([a-zA-Z0-9]+)$/);
        return match ? match[1].toLowerCase() : 'jpg';
    } catch (e) {
        const clean = String(url).split('?')[0];
        const match = clean.match(/\.([a-zA-Z0-9]+)$/);
        return match ? match[1].toLowerCase() : 'jpg';
    }
};

const sanitizeFileNamePart = (str) => {
    const normalized = String(str || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
    const cleaned = normalized
        .replace(/[^a-zA-Z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .substring(0, 60);
    return cleaned || 'Item';
};

// Safety net: any step that never settles becomes a visible error instead of an infinite spinner.
const withTimeout = (promise, ms, label) => {
    let timer;
    const timeout = new Promise((_, reject) => {
        timer = setTimeout(
            () => reject(new Error(`Tempo esgotado em: ${label} (${Math.round(ms / 1000)}s)`)),
            ms
        );
    });
    return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
};

const fetchPhotoAttachments = async (photoAttachments, onProgress) => {
    const results = [];
    const failed = [];

    for (let i = 0; i < photoAttachments.length; i++) {
        const att = photoAttachments[i];
        try {
            const response = await fetch(att.url);
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const blob = await response.blob();

            const categoryFolder = sanitizeFileNamePart(att.categoryName);
            const itemLabel = sanitizeFileNamePart(att.itemName);
            const ext = getUrlExtension(att.url);
            const fileName = `${itemLabel}_${String(att.index).padStart(2, '0')}.${ext}`;

            results.push({
                path: `Fotos/${categoryFolder}/${fileName}`,
                blob
            });
        } catch (err) {
            console.error('Falha ao baixar anexo:', att.url, err);
            failed.push(att);
        }

        if (onProgress) onProgress(i + 1, photoAttachments.length);
    }

    return { results, failed };
};

// --------------------------------------------------------------
// Item-name normalisation.
// The checklist is edited by hand, so the same question can come back with
// different casing / trailing colons / suffixes. Names listed here are mapped
// to the canonical name used by the switch() in parseNewApiFormat.
// --------------------------------------------------------------
const normalizeItemName = (str) =>
    String(str || '').toLowerCase().replace(/\s+/g, ' ').trim().replace(/:$/, '');

const ITEM_NAME_ALIASES = {
    'client name (importer)': 'Client Name (IMPORTER):',
    'client name (export)': 'Client Name (EXPORT):',
    'name surname (inspector approval)': 'Name Surname (Inspector Approval)',
    'name surname (inspector approval sds)': 'Name Surname (Inspector Approval)',
};

const canonicalItemName = (raw) => {
    const trimmed = String(raw || '').trim();
    return ITEM_NAME_ALIASES[normalizeItemName(trimmed)] || trimmed;
};

// --------------------------------------------------------------
// Parse NEW API format
// --------------------------------------------------------------
const parseNewApiFormat = (apiData) => {
    const d = {
        perishableProducts: [],
        documents: [],
        shipmentType: '',
        boardingType: '',
        containerType: '',
        countryOfOriginName: '',
        countryOfOriginOption: '',
        numberOfSamplings: '',
        photoAttachments: [],
        samplingRepresentative: null,
        samplingRepresentativeRemark: '',
        samplingTransportOk: null,
        samplingTransportRemark: '',
        samplingLabAccredited: null,
        samplingLabAccreditedRemark: '',
        inspectorApprovalName: '',
        inspectorApprovalSignatureUrl: '',
        manufacturerApprovalName: '',
        manufacturerApprovalSignatureUrl: '',
        labApprovalName: '',
        labApprovalSignatureUrl: '',
        finalApprovalName: '',
        finalApprovalSignatureUrl: '',
        fullDescriptionOptions: [],
        clientLicensed: null,
        licenseReferenceNumber: '',
        numberOfContainers: '',
    };

    d.inspectorName = apiData.user?.name || '';
    d.unitName = apiData.unit?.name || '';
    d.dateOfInspection = formatApiDate(apiData.startedAt);
    d.dateOfReport = formatApiDate(apiData.concludedAt) || formatApiDate(apiData.approvedAt) || new Date().toLocaleDateString('pt-BR');

    if (apiData.perimeter?.start?.address) {
        d.placeOfInspection = apiData.perimeter.start.address;
    }

    if (apiData.signatures && apiData.signatures.length > 0) {
        d.signatureUrl = apiData.signatures[0].url || '';
        d.signatureRole = apiData.signatures[0].role || '';
    }

    const getText = (item) => item?.answer?.text || '';
    const getOption = (item) => item?.answer?.selectedOptions?.[0]?.text || '';
    const getOptions = (item) => (item?.answer?.selectedOptions || []).map(o => o.text);
    const getEval = (item) => evaluativeToYesNo(item?.answer?.evaluative);
    const getComment = (item) => item?.comment || '';
    const getNumber = (item) => item?.answer?.number !== null && item?.answer?.number !== undefined ? String(item.answer.number) : '';

    let countryOfOriginOption = '';
    let countryOfOriginName = '';
    let currentSamplingRow = null;
    d.samplingRows = [];
    let currentProductRow = null;
    let productStartNewRow = true;
    d.perishableProducts = [];

    const pendingOtherTargets = {};
    const registerOtherTarget = (item, fieldName) => {
        const unlockId = (item.dependencies || [])
            .flatMap(dep => dep.unlocks || [])
            .find(u => (u?.name || '').trim() === 'Other')?.id;
        if (unlockId) pendingOtherTargets[unlockId] = fieldName;
    };

    for (const cat of (apiData.categories || [])) {
        for (const item of (cat.items || [])) {
            const name = canonicalItemName(item.name);

            if (Object.prototype.hasOwnProperty.call(pendingOtherTargets, item.id)) {
                const targetField = pendingOtherTargets[item.id];
                d[targetField] = getText(item);
                delete pendingOtherTargets[item.id];
                continue;
            }

            if (Array.isArray(item.attachments) && item.attachments.length > 0) {
                item.attachments.forEach((att, idx) => {
                    if (!att?.url) return;
                    d.photoAttachments.push({
                        url: att.url,
                        attachmentId: att.id,
                        subtitle: att.subtitle || '',
                        categoryName: cat.name || 'Geral',
                        itemName: name || `Item_${item.id}`,
                        itemId: item.id,
                        index: idx + 1
                    });
                });
            }

            if (name === 'Main country of origin:') {
                countryOfOriginOption = getOption(item);
                continue;
            }
            if (name === 'Main Country of Origin') {
                countryOfOriginName = getOption(item);
                continue;
            }
            if (name.startsWith('Are the samples collected representative')) {
                d.samplingRepresentative = getEval(item);
                d.samplingRepresentativeRemark = getComment(item);
                continue;
            }
            if (name.startsWith('Are the samples transported according to the conditions specified')) {
                d.samplingTransportOk = getEval(item);
                d.samplingTransportRemark = getComment(item);
                continue;
            }
            if (name.startsWith('Are all test parameters performed covered under the laboratory')) {
                d.samplingLabAccredited = getEval(item);
                d.samplingLabAccreditedRemark = getComment(item);
                continue;
            }

            switch (name) {
                case 'Project No (INVOICE)':
                    d.projectNo = getText(item);
                    break;
                case 'Client Name (EXPORT):':
                    d.clientNameExport = getOption(item);
                    break;
                case 'Client Name (IMPORTER):':
                    if (getOption(item) === 'Other') {
                        registerOtherTarget(item, 'clientName');
                    } else {
                        d.clientName = getOption(item) || getText(item);
                    }
                    break;
                case 'Country of Import:':
                    d.countryOfImport = getOption(item) || getText(item);
                    break;
                case 'Client Address:':
                    if (getOption(item) === 'Other') {
                        registerOtherTarget(item, 'clientAddress');
                    } else {
                        d.clientAddress = getOption(item) || getText(item);
                    }
                    break;
                case 'Date of inspection:':
                    d.dateOfInspection = getText(item);
                    break;
                case 'Inspection Valid Till':
                    d.inspectionValidTill = getText(item);
                    break;
                case 'Arrive Insp. Location:':
                    d.arriveInspectionLocation = getText(item);
                    break;
                case 'Date of Report:':
                    d.dateOfReport = getText(item);
                    break;
                case 'Total time:':
                    d.totalTime = getText(item);
                    break;
                case 'Travel time:':
                    d.travelTime = getText(item);
                    break;
                case 'Location (address):':
                    if (!d.placeOfInspection) {
                        d.placeOfInspection = getText(item);
                    }
                    break;
                case 'Full Description:':
                    // Multi-select: both boxes can be ticked at the same time.
                    d.fullDescriptionOptions = getOptions(item);
                    d.fullDescriptionOption = d.fullDescriptionOptions[0] || '';
                    break;
                case 'Condition of good':
                    d.conditionOfGoods = getOption(item);
                    break;
                case 'Shipment and packing':
                    d.shipmentType = getOption(item);
                    break;
                case 'Type of boarding':
                    d.boardingType = getOption(item);
                    break;
                case 'Type of container':
                    d.containerType = getOption(item);
                    break;
                case 'Number of containers on this invoice':
                    d.numberOfContainers = getNumber(item) || getText(item);
                    break;
                case 'The client has been licenced by TUV AUSTRIA':
                    d.clientLicensed = getEval(item);
                    break;
                case 'License reference number':
                    d.licenseReferenceNumber = getText(item);
                    break;
                case 'Container No.':
                    d.containerNo = getText(item);
                    break;
                case 'Agent Seal No.':
                    d.agentSealNo = getNumber(item) || getText(item);
                    break;
                case 'TUV AUSTRIA Seal No.':
                    d.tuvSealNo = getNumber(item) || getText(item);
                    break;
                case 'Storage conditions appropriate:':
                    d.storageConditions = getEval(item);
                    break;
                case 'Are goods presented as per inspection ref. doc?':
                    d.goodsPresentedAsRef = getEval(item);
                    break;
                case 'Was visual identification of goods satifactory?':
                    d.visualIdentification = getEval(item);
                    break;
                case 'Was visual examination of goods  satisfactory?':
                    d.visualExamination = getEval(item);
                    break;
                case 'Packing/Shipping Marks conform to applicable to CAI and/or AIR:':
                    d.packingMarksConform = getEval(item);
                    break;
                case 'Container Details Completed in Attachment':
                    d.containerDetailsAttached = getEval(item);
                    break;
                case 'Gross Weight (kg):':
                    d.grossWeight = getNumber(item) || getText(item);
                    break;
                case 'Net Weight (kg):':
                    d.netWeight = getNumber(item) || getText(item);
                    break;
                case 'Packing Number':
                    d.packingType = getOption(item);
                    d.packingNumber = getComment(item);
                    break;
                case 'Quality Check':
                    d.qualityCheck = getOption(item);
                    break;
                case 'Has non-radioactive certificate been collected?':
                    d.nonRadioactiveCertificate = getEval(item);
                    break;
                case 'Has exporter`s quality system been collected?':
                    d.exporterQualitySystem = getEval(item);
                    break;
                case 'Photographs':
                    d.photographs = getEval(item);
                    break;
                case 'Total Quantity:':
                    d.totalQuantity = getText(item);
                    break;
                case 'Sampling Techniques Used:':
                    d.samplingTechnique = getOption(item);
                    break;
                case 'Number of items spot checked (state quantity or %):':
                    d.itemsSpotChecked = getText(item);
                    break;
                case 'Number of samplings':
                    d.numberOfSamplings = getText(item);
                    break;
                case '1. Is te container clean, free of any foreign impurities, odor, or damage (such as cracks or holes) that may affect the integrity os shipment, and photographed wihle empty?':
                    d.loading_containerClean = getEval(item);
                    d.loading_containerCleanRemark = getComment(item);
                    break;
                case 'Has the operation of the cooling system (if required) been verified according to the temperatures in the AIR/CAI?':
                    d.loading_coolingVerified = getEval(item);
                    d.loading_coolingVerifiedRemark = getComment(item);
                    break;
                case 'Has the container been photographed afther the first quarter, half and full load of the shipment?':
                    d.loading_photographed = getEval(item);
                    d.loading_photographedRemark = getComment(item);
                    break;
                case 'Are loading conditions e.g., temperature and humidity (if required) being monitored during the process according to the conditions specified in the AIR/CAI?':
                    d.loading_conditionsMonitored = getEval(item);
                    d.loading_conditionsMonitoredRemark = getComment(item);
                    break;
                case 'Document':
                    d.documents = getOptions(item);
                    break;
                case 'Details (Product Description/ Batch Number/ Quantity/ Production Date/ Expiry Date)':
                    const rawDetails = getText(item);
                    d.productDetails = rawDetails;

                    if (rawDetails) {
                        const parsedProducts = parseProductDetailsSimple(rawDetails);
                        d.perishableProducts = parsedProducts;

                        if (parsedProducts.length > 0) {
                            d.parsedDescription = parsedProducts[0].description;
                            d.parsedBatchNumber = parsedProducts[0].batchNumber;
                            d.parsedQuantity = parsedProducts[0].quantity;
                            d.parsedProductionDate = parsedProducts[0].productionDate;
                            d.parsedExpiryDate = parsedProducts[0].expiryDate;
                            d.parsedProductsCount = parsedProducts.length;
                        }
                    }
                    break;

                case 'Product Description':
                    if (productStartNewRow || !currentProductRow) {
                        currentProductRow = { description: '', batchNumber: '', quantity: '', productionDate: '', expiryDate: '' };
                        d.perishableProducts.push(currentProductRow);
                        productStartNewRow = false;
                    }
                    currentProductRow.description = getOption(item) || getText(item);
                    break;
                case 'Batch Number':
                    if (currentProductRow) currentProductRow.batchNumber = getNumber(item) || getText(item);
                    break;
                case 'Quantity':
                    if (currentProductRow) currentProductRow.quantity = getNumber(item) || getText(item);
                    break;
                case 'Production Date':
                    if (currentProductRow) currentProductRow.productionDate = getText(item);
                    break;
                case 'Expiry Date':
                    if (currentProductRow) currentProductRow.expiryDate = getText(item);
                    break;
                case 'New registration?':
                    productStartNewRow = (getEval(item) === 'yes');
                    break;
                case 'Was sealling and locking of the containers carried out immediately after the loading inspection was completed?':
                    d.sealing_sealingLocking = getEval(item);
                    d.sealing_sealingLockingRemark = getComment(item);
                    break;
                case 'Has the AIR checklist been completed and attached to the inspection report?':
                    d.sealing_airChecklist = getEval(item);
                    d.sealing_airChecklistRemark = getComment(item);
                    break;
                case 'Have all photographs related to the sealing process been attached to the inspection report?':
                    d.sealing_sealingPhotos = getEval(item);
                    d.sealing_sealingPhotosRemark = getComment(item);
                    break;
                case 'Have all photographs related to the inspection been attached to the inspection report?':
                    d.sealing_inspectionPhotos = getEval(item);
                    d.sealing_inspectionPhotosRemark = getComment(item);
                    break;
                case 'Inspection Results':
                    d.inspectionResult = getOption(item);
                    break;
                case 'Manufacturer Name / Address':
                    d.manufacturerNameAddress = getText(item);
                    break;
                case 'Time':
                    d.samplingTime = getText(item);
                    break;
                case 'Data':
                    break;
                case 'Product Name':
                    currentSamplingRow = {
                        data: '',
                        productName: getOption(item) || getText(item),
                        sampleQuantity: '',
                        temperature: '',
                        caiNumber: '',
                        testStandard: '',
                        siteSample: '',
                        tools: '',
                        description: '',
                        sampleCodes: '',
                        labName: ''
                    };
                    d.samplingRows.push(currentSamplingRow);
                    break;
                case 'Sample  Quantity':
                    if (currentSamplingRow) currentSamplingRow.sampleQuantity = getNumber(item) || getText(item);
                    break;
                case 'Sampling temperature':
                    if (currentSamplingRow) currentSamplingRow.temperature = getText(item) || getNumber(item);
                    break;
                case 'Sampling tools/Equipment':
                    if (currentSamplingRow) currentSamplingRow.tools = getText(item) || getOption(item);
                    break;
                case 'Applicable CAI  Number (link)':
                    if (currentSamplingRow) currentSamplingRow.caiNumber = getOption(item) || getText(item);
                    break;
                case 'Test Standard':
                    if (currentSamplingRow) currentSamplingRow.testStandard = getOption(item) || getText(item);
                    break;
                case 'Site Sample':
                    if (currentSamplingRow) currentSamplingRow.siteSample = getOption(item) || getText(item);
                    break;
                case 'Sampling  description':
                    if (currentSamplingRow) currentSamplingRow.description = getText(item);
                    break;
                case 'Sample Codes /  Sticker / Seal':
                    if (currentSamplingRow) currentSamplingRow.sampleCodes = getText(item);
                    break;
                case 'Lab Name':
                    if (currentSamplingRow) currentSamplingRow.labName = getText(item);
                    break;
                case 'Name Surname (Inspector Approval)':
                    d.inspectorApprovalName = getText(item);
                    d.inspectorApprovalSignatureUrl = item.attachments?.[0]?.url || '';
                    break;
                case 'Name Surname (Manufacturer/Exporter Approval)':
                    d.manufacturerApprovalName = getText(item);
                    d.manufacturerApprovalSignatureUrl = item.attachments?.[0]?.url || '';
                    break;
                case 'Name Surname (Laboratory Approval)':
                    d.labApprovalName = getText(item);
                    d.labApprovalSignatureUrl = item.attachments?.[0]?.url || '';
                    break;
                case 'Name Surname (Final Approval)':
                    d.finalApprovalName = getText(item);
                    d.finalApprovalSignatureUrl = item.attachments?.[0]?.url || '';
                    break;
                default:
                    break;
            }
        }
    }

    let samplingRowIndex = 0;
    for (const cat of (apiData.categories || [])) {
        for (const item of (cat.items || [])) {
            const name = item.name?.trim() || '';
            if (name === 'Data') {
                const dataValue = getText(item);
                if (samplingRowIndex < d.samplingRows.length) {
                    d.samplingRows[samplingRowIndex].data = dataValue;
                    samplingRowIndex++;
                }
            }
        }
    }

    d.countryOfOriginOption = countryOfOriginOption;
    d.countryOfOriginName = countryOfOriginName;

    // "Applicable AIR/CAI Number" on the inspection report = every distinct CAI
    // referenced by the sampling rows.
    d.applicableAirCaiNumber = [...new Set(
        d.samplingRows.map(r => (r.caiNumber || '').trim()).filter(Boolean)
    )].join(', ');

    if (!d.projectNo) d.projectNo = '';
    if (!d.countryOfImport) d.countryOfImport = '';

    return d;
};

// --------------------------------------------------------------
// Group sampling rows by date
// --------------------------------------------------------------
const groupSamplingRowsByDate = (samplingRows) => {
    const groups = new Map();

    for (const row of samplingRows) {
        const dateKey = extractDatePart(row.data || '');
        if (!groups.has(dateKey)) {
            groups.set(dateKey, []);
        }
        groups.get(dateKey).push(row);
    }

    const result = [];
    for (const [date, rows] of groups) {
        result.push({ date, rows });
    }

    result.sort((a, b) => {
        const dateA = parseDateString(a.date);
        const dateB = parseDateString(b.date);
        if (dateA && dateB) {
            return dateB - dateA;
        }
        return a.date.localeCompare(b.date);
    });

    return result;
};

// --------------------------------------------------------------
// Parse date string for sorting
// --------------------------------------------------------------
const parseDateString = (dateStr) => {
    if (!dateStr) return null;
    try {
        const parts = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
        if (parts) {
            const day = parseInt(parts[1], 10);
            const month = parseInt(parts[2], 10) - 1;
            const year = parseInt(parts[3], 10);
            return new Date(year, month, day).getTime();
        }
        const timeParts = dateStr.match(/^(\d{1,2}):(\d{2})/);
        if (timeParts) {
            const now = new Date();
            const hours = parseInt(timeParts[1], 10);
            const minutes = parseInt(timeParts[2], 10);
            return new Date(now.getFullYear(), now.getMonth(), now.getDate(), hours, minutes).getTime();
        }
        return null;
    } catch (e) {
        return null;
    }
};

// --------------------------------------------------------------
// Column-letter helpers
// --------------------------------------------------------------
const colLetterToIndex = (letters) => {
    let idx = 0;
    for (let i = 0; i < letters.length; i += 1) {
        idx = idx * 26 + (letters.charCodeAt(i) - 64);
    }
    return idx;
};

const colLetterFromIndex = (n) => {
    let s = '';
    let num = n;
    while (num > 0) {
        const m = (num - 1) % 26;
        s = String.fromCharCode(65 + m) + s;
        num = Math.floor((num - 1) / 26);
    }
    return s;
};

const parseCellRange = (range) => {
    const [from, to] = range.split(':');
    const fromMatch = from.match(/([A-Z]+)(\d+)/);
    const toMatch = to.match(/([A-Z]+)(\d+)/);
    return {
        fromCol: colLetterToIndex(fromMatch[1]),
        toCol: colLetterToIndex(toMatch[1]),
        fromRow: parseInt(fromMatch[2], 10),
        toRow: parseInt(toMatch[2], 10),
    };
};

const colWidthToPx = (chars) => Math.round((chars || 8.43) * 7 + 5);
const ptToPx = (pt) => Math.round((pt || 15) * 96 / 72);

const rangeSizePx = (ws, range) => {
    const { fromCol, toCol, fromRow, toRow } = parseCellRange(range);
    let width = 0;
    for (let c = fromCol; c <= toCol; c += 1) {
        width += colWidthToPx(ws.getColumn(c).width);
    }
    let height = 0;
    for (let r = fromRow; r <= toRow; r += 1) {
        height += ptToPx(ws.getRow(r).height);
    }
    return { fromCol, fromRow, width, height };
};

const SAMPLING_ROW_LINE_HEIGHT_PT = 13.5;
const MAX_SAMPLING_ROW_LINES = 8;

const growSamplingRowForText = (ws, rowNum, fieldsWithCols, setTopAlign = true) => {
    let maxLines = 1;
    fieldsWithCols.forEach(({ text, cols }) => {
        if (setTopAlign) {
            const anchorCoord = `${cols[0]}${rowNum}`;
            const cell = ws.getCell(anchorCoord);
            cell.alignment = { ...cell.alignment, vertical: 'top' };
        }

        if (!text) return;
        const widthChars = cols.reduce((sum, col) => sum + (ws.getColumn(col).width || 8.43), 0);
        const segments = String(text).split('\n');
        const lines = segments.reduce(
            (total, seg) => total + Math.max(1, Math.ceil(seg.length / Math.max(1, widthChars))),
            0
        );
        if (lines > maxLines) maxLines = lines;
    });

    const cappedLines = Math.min(maxLines, MAX_SAMPLING_ROW_LINES);
    const neededHeight = cappedLines * SAMPLING_ROW_LINE_HEIGHT_PT;
    const row = ws.getRow(rowNum);
    if (!row.height || row.height < neededHeight) {
        row.height = neededHeight;
    }
};

// Column layout of the "Sampling Details" table in Sampling_Data_sheet.xlsx
// (rows 30-39): B product | D qty | G CAI | J standard | L site sample |
// O tools/equipment | S description | W codes/seal | Z lab.
const SAMPLING_ROW_FIELD_COLS = [
    { key: 'productName', cols: ['B', 'C'] },
    { key: 'caiNumber', cols: ['G', 'H', 'I'] },
    { key: 'testStandard', cols: ['J', 'K'] },
    { key: 'siteSample', cols: ['L', 'M', 'N'] },
    { key: 'tools', cols: ['O', 'P'] },
    { key: 'description', cols: ['S', 'T', 'U', 'V'] },
    { key: 'sampleCodes', cols: ['W', 'X', 'Y'] },
    { key: 'labName', cols: ['Z'] },
];

const applySamplingRowHeight = (ws, rowNum, row) => {
    growSamplingRowForText(
        ws,
        rowNum,
        SAMPLING_ROW_FIELD_COLS.map(({ key, cols }) => ({ text: row[key], cols }))
    );
};

const SIGNATURE_IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif']);
const SIGNATURE_DOCX_EXTENSIONS = new Set(['docx', 'dotx']);
const RASTER_MEDIA_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp']);

// Loads an image from a blob URL. Always settles (onload / onerror / timeout),
// so a non-image file can never hang the flow.
const loadImageElement = (blobUrl, timeoutMs = 10000) =>
    new Promise((resolve, reject) => {
        const img = new Image();
        const timer = setTimeout(
            () => reject(new Error('Timeout ao carregar imagem')),
            timeoutMs
        );
        img.onload = () => { clearTimeout(timer); resolve(img); };
        img.onerror = () => { clearTimeout(timer); reject(new Error('Arquivo não é uma imagem válida')); };
        img.src = blobUrl;
    });

const mimeForExtension = (ext) => {
    switch (ext) {
        case 'png': return 'image/png';
        case 'gif': return 'image/gif';
        case 'bmp': return 'image/bmp';
        case 'webp': return 'image/webp';
        default: return 'image/jpeg';
    }
};

// Decodes any browser-supported raster image and re-encodes it as PNG.
// Used for images pulled out of .docx files (normalises bmp/webp/odd jpegs
// and guarantees ExcelJS gets a clean, valid image).
const rasterToPng = async (arrayBuffer, ext, maxDim = 900) => {
    const blobUrl = URL.createObjectURL(new Blob([arrayBuffer], { type: mimeForExtension(ext) }));
    try {
        const img = await loadImageElement(blobUrl);
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        const pngBlob = await new Promise((resolve, reject) =>
            canvas.toBlob(b => (b ? resolve(b) : reject(new Error('Falha ao converter imagem'))), 'image/png')
        );
        return { buffer: await pngBlob.arrayBuffer(), width: w, height: h };
    } finally {
        URL.revokeObjectURL(blobUrl);
    }
};

// Pulls the signature picture out of a .docx.
// 1) Follows the <a:blip r:embed="rIdX"> references in word/document.xml in the
//    order they appear (so header/footer logos are ignored).
// 2) Falls back to the largest raster file in word/media/.
const extractImageFromDocx = async (arrayBuffer) => {
    const zip = await JSZip.loadAsync(arrayBuffer);

    const candidates = [];

    try {
        const docXml = await zip.file('word/document.xml')?.async('string');
        const relsXml = await zip.file('word/_rels/document.xml.rels')?.async('string');

        if (docXml && relsXml) {
            const relMap = {};
            const relRegex = /<Relationship\b[^>]*>/g;
            let m;
            while ((m = relRegex.exec(relsXml)) !== null) {
                const tag = m[0];
                const id = /\bId="([^"]+)"/.exec(tag)?.[1];
                const target = /\bTarget="([^"]+)"/.exec(tag)?.[1];
                if (id && target) relMap[id] = target;
            }

            const embedRegex = /r:(?:embed|id)="([^"]+)"/g;
            const seen = new Set();
            while ((m = embedRegex.exec(docXml)) !== null) {
                const target = relMap[m[1]];
                if (!target || seen.has(target)) continue;
                seen.add(target);
                const path = target.startsWith('/')
                    ? target.slice(1)
                    : `word/${target}`.replace(/\/\.\//g, '/');
                const ext = (path.split('.').pop() || '').toLowerCase();
                if (RASTER_MEDIA_EXTENSIONS.has(ext) && zip.file(path)) {
                    candidates.push({ path, ext });
                }
            }
        }
    } catch (err) {
        console.warn('Falha ao ler referências de imagem do docx:', err);
    }

    if (candidates.length === 0) {
        const mediaFiles = [];
        zip.forEach((relPath, file) => {
            if (file.dir || !relPath.startsWith('word/media/')) return;
            const ext = (relPath.split('.').pop() || '').toLowerCase();
            if (RASTER_MEDIA_EXTENSIONS.has(ext)) mediaFiles.push({ path: relPath, ext, file });
        });

        const sized = await Promise.all(mediaFiles.map(async (f) => {
            const buf = await f.file.async('arraybuffer');
            return { path: f.path, ext: f.ext, size: buf.byteLength };
        }));
        sized.sort((a, b) => b.size - a.size);
        candidates.push(...sized);
    }

    for (const { path, ext } of candidates) {
        try {
            const raw = await zip.file(path).async('arraybuffer');
            return await rasterToPng(raw, ext);
        } catch (err) {
            console.warn(`Imagem do docx ignorada (${path}):`, err);
        }
    }

    return null;
};

// Cache so the same signature URL is only downloaded/decoded once per session
// (the manufacturer signature is reused in the Sampling sheets and the RFC).
const signatureSourceCache = new Map();

// Resolves a signature URL (image OR .docx) into { buffer, extension, width, height }.
const resolveSignatureSource = async (url) => {
    if (signatureSourceCache.has(url)) return signatureSourceCache.get(url);

    const promise = (async () => {
        const ext = (getUrlExtension(url) || '').toLowerCase();
        const isImage = SIGNATURE_IMAGE_EXTENSIONS.has(ext);
        const isDocx = SIGNATURE_DOCX_EXTENSIONS.has(ext);

        if (!isImage && !isDocx) {
            console.warn(`Assinatura ignorada (formato não suportado: .${ext}):`, url);
            return null;
        }

        const controller = new AbortController();
        const fetchTimer = setTimeout(() => controller.abort(), 20000);
        let response;
        try {
            response = await fetch(url, { signal: controller.signal });
        } finally {
            clearTimeout(fetchTimer);
        }
        if (!response.ok) return null;

        const arrayBuffer = await response.arrayBuffer();

        if (isDocx) {
            const png = await extractImageFromDocx(arrayBuffer);
            if (!png) {
                console.warn('Nenhuma imagem encontrada dentro do docx de assinatura:', url);
                return null;
            }
            return { buffer: png.buffer, extension: 'png', width: png.width, height: png.height };
        }

        const extension = ext === 'png' ? 'png' : (ext === 'gif' ? 'gif' : 'jpeg');
        const blobUrl = URL.createObjectURL(new Blob([arrayBuffer], { type: `image/${extension}` }));
        try {
            const img = await loadImageElement(blobUrl); // validate BEFORE addImage
            return { buffer: arrayBuffer, extension, width: img.width, height: img.height };
        } finally {
            URL.revokeObjectURL(blobUrl);
        }
    })().catch((err) => {
        console.error('Falha ao resolver assinatura:', url, err);
        return null;
    });

    signatureSourceCache.set(url, promise);
    return promise;
};

const embedSignatureImage = async (wb, ws, url, range) => {
    if (!url) return false;

    try {
        const source = await resolveSignatureSource(url);
        if (!source) return false;

        const imageId = wb.addImage({ buffer: source.buffer, extension: source.extension });

        const { fromCol, fromRow, width, height } = rangeSizePx(ws, range);

        const MAX_WIDTH = 150;
        const MAX_HEIGHT = 60;

        const targetWidth = Math.min(width - 8, MAX_WIDTH);
        const targetHeight = Math.min(height - 8, MAX_HEIGHT);

        const aspectRatio = source.width / source.height;
        let finalWidth = targetWidth;
        let finalHeight = targetWidth / aspectRatio;

        if (finalHeight > targetHeight) {
            finalHeight = targetHeight;
            finalWidth = targetHeight * aspectRatio;
        }

        const offsetX = Math.max(0, (width - finalWidth) / 2);
        const offsetY = Math.max(0, (height - finalHeight) / 2);

        ws.addImage(imageId, {
            tl: {
                col: fromCol - 1,
                row: fromRow - 1,
                colOff: offsetX,
                rowOff: offsetY
            },
            ext: { width: finalWidth, height: finalHeight },
            editAs: 'oneCell',
        });
        return true;
    } catch (err) {
        console.error('Falha ao inserir imagem de assinatura na planilha:', url, err);
        return false;
    }
};

const applySamplingApprovals = async (wb, ws, d, checked, unchecked) => {
    // Unanswered questions keep the template's pre-ticked defaults
    // (N/A on row 42, Yes on rows 47 and 49).
    const setYesNo = (value, yesCell, noCell, naCell) => {
        if (!value) return;
        ws.getCell(yesCell).value = value === 'yes' ? checked : unchecked;
        ws.getCell(noCell).value = value === 'no' ? checked : unchecked;
        if (naCell) ws.getCell(naCell).value = value === 'na' ? checked : unchecked;
    };

    setYesNo(d.samplingRepresentative, 'T42', 'V42', 'W42');
    ws.getCell('Y42').value = d.samplingRepresentativeRemark || '';

    setYesNo(d.samplingTransportOk, 'T47', 'V47');
    ws.getCell('W47').value = d.samplingTransportRemark || '';

    setYesNo(d.samplingLabAccredited, 'T49', 'V49');
    ws.getCell('W49').value = d.samplingLabAccreditedRemark || '';

    ws.getCell('A60').value = d.inspectorApprovalName || d.inspectorName || '';
    ws.getCell('I60').value = d.manufacturerApprovalName || '';
    ws.getCell('O60').value = d.labApprovalName || '';
    ws.getCell('W60').value = d.finalApprovalName || '';

    await embedSignatureImage(wb, ws, d.inspectorApprovalSignatureUrl, 'A62:H62');
    await embedSignatureImage(wb, ws, d.manufacturerApprovalSignatureUrl, 'I62:N62');
    await embedSignatureImage(wb, ws, d.labApprovalSignatureUrl, 'O62:V62');
    await embedSignatureImage(wb, ws, d.finalApprovalSignatureUrl, 'W62:Z62');
};

// --------------------------------------------------------------
// Template loaders — each validates `response.ok` AND the ZIP
// signature before handing the buffer to ExcelJS/JSZip. This is
// what turns "Can't find end of central directory" into a message
// that tells you exactly which template is broken and what the
// fetch actually returned (usually the SPA index.html fallback).
// --------------------------------------------------------------
const loadTemplateBuffer = async (url, label) => {
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(
            `Falha ao carregar o template "${label}" (${url}): HTTP ${response.status}.`
        );
    }
    const buffer = await response.arrayBuffer();
    return assertValidZipBuffer(buffer, label);
};

const loadSamplingTemplateBuffer = () =>
    loadTemplateBuffer('/templates/Sampling_Data_sheet.xlsx', 'Sampling_Data_sheet.xlsx');

const loadRfcTemplateBuffer = () =>
    loadTemplateBuffer(
        '/templates/Request_For_Certification.xlsx',
        'Request_For_Certification.xlsx'
    );

const loadInspectionTemplateBuffer = () =>
    loadTemplateBuffer(
        '/templates/Empty_Inspection_report.xlsx',
        'Empty_Inspection_report.xlsx'
    );

const sumSampleQuantities = (rows) => {
    let total = 0;
    let sawNumber = false;

    for (const row of (rows || [])) {
        const raw = row?.sampleQuantity;
        if (raw === null || raw === undefined || raw === '') continue;

        const normalised = String(raw).replace(',', '.').replace(/[^0-9.-]/g, '');
        const value = parseFloat(normalised);
        if (!Number.isFinite(value)) continue;

        total += value;
        sawNumber = true;
    }

    if (!sawNumber) return null;
    return Math.round(total * 1000) / 1000;
};

const generateSingleSamplingDataSheet = async (d, dateGroup, preloadedTemplate = null) => {
    const checked = CB_CHECKED;
    const unchecked = CB_UNCHECKED;

    const templateArrayBuffer = preloadedTemplate || await loadSamplingTemplateBuffer();

    const wb = new ExcelJS.Workbook();
    await wb.xlsx.load(templateArrayBuffer);
    const ws = wb.getWorksheet('Table 1') || wb.worksheets[0];

    const set = (coord, value) => {
        ws.getCell(coord).value = value;
    };

    const inspectionDateTimePlace = [d.dateOfInspection, d.samplingTime, d.placeOfInspection]
        .filter(Boolean).join(' / ');

    set('H6', d.projectNo || '');
    set('X6', d.dateOfInspection || '');
    // SDS shows the Client (EXPORT) name (always BRF or LAR), not the importer.
    set('H7', d.clientNameExport || '');
    set('X7', d.manufacturerNameAddress || '');
    set('H8', d.inspectorName || '');
    set('X8', inspectionDateTimePlace);

    // Rows 7-8 are merged cells, so Excel will not auto-fit them.
    growSamplingRowForText(ws, 7, [
        { text: d.clientNameExport, cols: ['H', 'I', 'J', 'K', 'L'] },
        { text: d.manufacturerNameAddress, cols: ['X', 'Y', 'Z'] },
    ]);
    growSamplingRowForText(ws, 8, [{ text: inspectionDateTimePlace, cols: ['X', 'Y', 'Z'] }]);

    const rows = dateGroup.rows.slice(0, 10);

    const totalSamples = sumSampleQuantities(rows);
    set('F15', totalSamples !== null ? totalSamples : (d.numberOfSamplings || ''));

    rows.forEach((row, idx) => {
        const r = 30 + idx;
        set(`B${r}`, row.productName || '');
        set(`D${r}`, row.sampleQuantity || '');
        set(`G${r}`, row.caiNumber || '');
        set(`J${r}`, row.testStandard || '');
        set(`L${r}`, row.siteSample || '');
        set(`O${r}`, row.tools || '');
        set(`S${r}`, row.description || '');
        set(`W${r}`, row.sampleCodes || '');
        set(`Z${r}`, row.labName || '');
        applySamplingRowHeight(ws, r, row);
    });

    await applySamplingApprovals(wb, ws, d, checked, unchecked);

    return wb;
};

const generateSamplingDataSheetWorkbooks = async (d) => {
    const templateArrayBuffer = await loadSamplingTemplateBuffer();
    const groups = groupSamplingRowsByDate(d.samplingRows || []);

    if (groups.length === 0) {
        const wb = new ExcelJS.Workbook();
        await wb.xlsx.load(templateArrayBuffer);
        return [{ date: '', rowCount: 0, wb }];
    }

    const workbooks = [];
    for (const group of groups) {
        const wb = await generateSingleSamplingDataSheet(d, group, templateArrayBuffer);
        workbooks.push({ date: group.date, rowCount: group.rows.length, wb });
    }
    return workbooks;
};

const dateLabelForFileName = (date) => {
    const cleaned = String(date || '').trim();
    if (!cleaned) return 'sem_data';
    return cleaned.replace(/[^0-9A-Za-z]+/g, '-').replace(/^-+|-+$/g, '') || 'sem_data';
};

// =====================================================================
// REQUEST FOR CERTIFICATION (SFDA)  -  /templates/Request_For_Certification.xlsx
// Filled from the same checklist data as the Inspection Report and the
// Sampling Data Sheet.
// =====================================================================
// The checklist has no questions for these four, and every checklist that feeds
// this app is a food shipment certificate (S-CoC) requested by the exporter.
// Change them here if that stops being true.
const RFC_DEFAULTS = {
    program: 'food',            // 'cosmetics' | 'food' | 'tobacco' | 'other'
    certification: 'S-CoC',     // 'P-CoC' | 'S-CoC'
    applicationType: 'new',     // 'new' | 'renewal' | 'revision'
    applicantType: 'exporter',  // 'dealer' | 'exporter' | 'manufacturer' | 'importer' | 'other'
};

const rfcColRange = (from, to) => {
    const out = [];
    for (let i = colLetterToIndex(from); i <= colLetterToIndex(to); i += 1) out.push(colLetterFromIndex(i));
    return out;
};

// Template columns of the declaration table (one entry per merged block).
const RFC_PRODUCT_BLOCKS = [
    ['A', 'C'], ['D', 'E'], ['F', 'M'], ['N', 'S'], ['T', 'X'], ['Y', 'AF'], ['AG', 'AL'], ['AM', 'AR'],
];
const RFC_PRODUCT_FIRST_ROW = 77;
const RFC_PRODUCT_TEMPLATE_ROWS = 2;   // rows 77-78 exist in the template
const RFC_PRODUCT_SPACER_ROW = 79;     // first row below the table

// Moves every row from `fromRow` downward by `count` rows, keeping values,
// styles, heights and merged ranges (ExcelJS' own spliceRows drops merges).
const rfcShiftRowsDown = (ws, fromRow, count) => {
    if (count <= 0) return;
    const lastRow = ws.rowCount;
    const lastCol = Math.max(ws.columnCount, 44);

    const moved = [];
    [...ws.model.merges].forEach((range) => {
        const { fromRow: top, toRow: bottom, fromCol, toCol } = parseCellRange(range);
        if (top >= fromRow) {
            moved.push({ top, bottom, fromCol, toCol });
            ws.unMergeCells(range);
        }
    });

    for (let r = lastRow; r >= fromRow; r -= 1) {
        const src = ws.getRow(r);
        const dst = ws.getRow(r + count);
        dst.height = src.height;
        for (let c = 1; c <= lastCol; c += 1) {
            const from = src.getCell(c);
            const to = dst.getCell(c);
            to.value = from.value;
            to.style = JSON.parse(JSON.stringify(from.style || {}));
        }
    }

    moved.forEach(({ top, bottom, fromCol, toCol }) => {
        ws.mergeCells(top + count, fromCol, bottom + count, toCol);
    });
};

// Almost every row of this template has no fixed height. LibreOffice (used for
// the PDF export) sizes such rows only after it has placed images, so a
// signature anchored to a cell ends up several rows too high. Pinning every row
// to the height LibreOffice computes for the empty template avoids that and
// does not change how the sheet looks.
const RFC_DEFAULT_ROW_HEIGHT = 12.75;
const RFC_TWO_LINE_ROW_HEIGHT = 22.35;
const RFC_TWO_LINE_ROWS = [3, 6, 16, 29, 36, 42, 45, 47];
const RFC_EMPTY_ROW_HEIGHT = 15;

// Row growth sized for this template (8-9 pt text in very narrow grid columns).
const RFC_CHARS_PER_WIDTH_UNIT = 1.25;
const RFC_LINE_HEIGHT_PT = 11.5;
const RFC_MAX_LINES = 8;
const rfcGrowRow = (ws, rowNum, fieldsWithCols) => {
    let maxLines = 1;
    fieldsWithCols.forEach(({ text, cols }) => {
        if (!text) return;
        const widthChars = cols.reduce((sum, col) => sum + (ws.getColumn(col).width || 8.43), 0)
            * RFC_CHARS_PER_WIDTH_UNIT;
        const lines = String(text).split('\n').reduce(
            (total, seg) => total + Math.max(1, Math.ceil(seg.length / Math.max(1, widthChars))), 0);
        if (lines > maxLines) maxLines = lines;
    });
    const needed = Math.min(maxLines, RFC_MAX_LINES) * RFC_LINE_HEIGHT_PT + 2;
    const row = ws.getRow(rowNum);
    if (!row.height || row.height < needed) row.height = needed;
};

const rfcPinRowHeights = (ws) => {
    const last = ws.rowCount;
    for (let r = 1; r <= last; r += 1) {
        const row = ws.getRow(r);
        if (row.height) continue;
        let hasContent = false;
        row.eachCell({ includeEmpty: false }, () => { hasContent = true; });
        if (!hasContent) row.height = RFC_EMPTY_ROW_HEIGHT;
        else row.height = RFC_TWO_LINE_ROWS.includes(r) ? RFC_TWO_LINE_ROW_HEIGHT : RFC_DEFAULT_ROW_HEIGHT;
    }
};

const generateRequestForCertificationXlsx = async (d, preloadedTemplate = null) => {
    const templateArrayBuffer = preloadedTemplate || await loadRfcTemplateBuffer();
    const wb = new ExcelJS.Workbook();
    await wb.xlsx.load(templateArrayBuffer);
    const ws = wb.worksheets[0];
    rfcPinRowHeights(ws);

    const box = (on) => (on ? CB_CHECKED : CB_UNCHECKED);
    const put = (coord, value) => { ws.getCell(coord).value = value; };
    const tick = (coord, on) => { put(coord, box(on)); };
    const tickLabel = (coord, on, label) => { put(coord, `${box(on)} ${label}`); };
    const lower = (v) => String(v || '').toLowerCase();

    const D = RFC_DEFAULTS;

    // ---- Program (row 3) -------------------------------------------------
    tickLabel('A3', D.program === 'cosmetics', 'Cosmetics Program');
    tickLabel('I3', D.program === 'food', 'Food Program');
    tickLabel('T3', D.program === 'tobacco', 'Tobacco Program');
    tickLabel('AF3', D.program === 'other', 'Other');

    // ---- Date of application / initial check -------------------------------
    put('T4', d.dateOfReport || d.dateOfInspection || '');

    tickLabel('Q6', d.clientLicensed === 'yes', 'Yes');
    tickLabel('V6', d.clientLicensed === 'no', 'No');
    put('Z6', `Reference Number:\n${d.licenseReferenceNumber || ''}`);

    // ---- Type of application (rows 9-10: P-CoC / S-CoC) -------------------
    [['9', 'P-CoC'], ['10', 'S-CoC']].forEach(([row, cert]) => {
        const on = D.certification === cert;
        tick(`U${row}`, on && D.applicationType === 'new');
        tick(`W${row}`, on && D.applicationType === 'renewal');
        tick(`AB${row}`, on && D.applicationType === 'revision');
    });

    // ---- Applicant type (row 12) -----------------------------------------
    tick('A12', D.applicantType === 'dealer');
    tick('M12', D.applicantType === 'exporter');
    tick('R12', D.applicantType === 'manufacturer');
    tick('X12', D.applicantType === 'importer');
    tick('AI12', D.applicantType === 'other');

    // ---- Trade parties (rows 14-17) --------------------------------------
    put('G14', d.clientNameExport || '');
    put('AB14', d.clientName || '');
    put('AB15', d.clientAddress || '');
    rfcGrowRow(ws, 14, [
        { text: d.clientNameExport, cols: rfcColRange('G', 'T') },
        { text: d.clientName, cols: rfcColRange('AB', 'AQ') },
    ], false);
    rfcGrowRow(ws, 15, [
        { text: d.clientAddress, cols: rfcColRange('AB', 'AQ') },
    ], false);

    // ---- Manufacturer (row 23) -------------------------------------------
    put('G23', d.manufacturerNameAddress || '');
    rfcGrowRow(ws, 23, [
        { text: d.manufacturerNameAddress, cols: rfcColRange('G', 'T') },
    ], false);

    // ---- Product & delivery information (S-CoC) --------------------------
    // Type of delivery (row 28): the checklist does not record total/partial.
    tickLabel('E28', false, 'Total');
    tickLabel('P28', false, 'Partial');

    // Goods conditions (row 29)
    const cond = lower(d.conditionOfGoods);
    tickLabel('E29', cond.includes('new'), 'New');
    tickLabel('L29', cond.includes('used') || cond.includes('second') || cond.includes('recondition'), 'Second Hand (used)');
    tickLabel('U29', false, 'Assembled');
    tickLabel('X29', false, 'Partially assembled');
    tickLabel('AK29', false, 'Disassembled');

    // Shipment method (row 30)
    const ship = lower(d.shipmentType);
    const shipIsAir = ship.includes('air');
    const shipIsSea = ship.includes('sea');
    const shipIsRoad = ship.includes('road');
    const shipIsMulti = ship.includes('multi');
    const shipKnown = shipIsAir || shipIsSea || shipIsRoad || shipIsMulti;
    tickLabel('E30', shipIsAir, 'Air');
    tickLabel('L30', shipIsSea, 'Sea');
    tickLabel('U30', shipIsRoad, 'Road');
    tickLabel('X30', shipIsMulti, 'Multimodal');
    tickLabel('AK30', !!ship && !shipKnown, `Other:${ship && !shipKnown ? ' ' + d.shipmentType : ''}`);

    // Packing details (rows 31-32)
    const boarding = lower(d.boardingType);
    const isFcl = boarding.includes('fcl') || boarding.includes('full');
    const isLcl = boarding.includes('lcl') || boarding.includes('less');
    const isBulk = boarding.includes('bulk');
    const isGeneral = boarding.includes('conventional') || boarding.includes('general');
    const ctype = lower(d.containerType);
    const is20 = isFcl && ctype.includes('20');
    const is40 = isFcl && ctype.includes('40');
    const nContainers = d.numberOfContainers ? `${d.numberOfContainers} ` : '';
    tickLabel('S31', isFcl, 'FCL:');
    put('V31', `${is20 ? nContainers : ''} x 20” FCL`);
    put('AC31', `${is40 ? nContainers : ''} x 40” FCL`);
    tickLabel('S32', isLcl, 'LCL:');
    tickLabel('V32', isBulk, 'Bulk');
    tickLabel('AC32', isGeneral, 'General Cargo/Conventional');

    // Invoice / requested inspection (rows 33-34)
    put('A33', `Invoice Number: ${d.projectNo || ''}`);
    put('S33', `Invoice Date: ${d.dateOfInspection || ''}`);
    put('A34', `Requested Inspection Date: ${d.dateOfInspection || ''}`);
    put('S34', `Inspection Address: ${d.placeOfInspection || ''}`);
    rfcGrowRow(ws, 34, [
        { text: `Inspection Address: ${d.placeOfInspection || ''}`, cols: rfcColRange('S', 'AQ') },
    ], false);

    // ---- Clearance / establishment questions: not in the checklist ---------
    [42, 45, 46, 47].forEach((row) => {
        tick(`AD${row}`, false);
        tick(`AK${row}`, false);
    });

    // ---- Documents attached (rows 51-53) -----------------------------------
    // slot -> [box cell]; every slot has a label already printed in the template.
    const DOC_SLOTS = {
        sfda: { box: 'A51', test: /sfda|licen[cs]e/ },
        photos: { box: 'H51', test: /photo/ },
        pif: { box: 'S51', test: /\bpif\b|product information/ },
        halal: { box: 'Y51', test: /halal(?!.*slaughter)/ },
        slaughter: { box: 'AL51', test: /slaughter/ },
        invoice: { box: 'A52', test: /invoice/ },
        packing: { box: 'H52', test: /packing/ },
        ingredients: { box: 'S52', test: /ingredient/ },
        msds: { box: 'Y52', test: /msds|safety data/ },
        health: { box: 'AL52', test: /health/ },
        phyto: { box: 'A53', test: /phyto/ },
        iso: { box: 'H53', test: /\biso\b|haccp|gmp/ },
    };
    const docs = (d.documents || []).map(x => String(x));
    const slotOn = {};
    const otherDocs = [];
    docs.forEach((doc) => {
        const text = lower(doc);
        const slot = Object.keys(DOC_SLOTS).find(k => DOC_SLOTS[k].test.test(text));
        if (slot) slotOn[slot] = true; else otherDocs.push(doc);
    });
    // Product photos were taken and attached to the inspection report.
    if (d.photographs === 'yes' || (d.photoAttachments || []).length > 0) slotOn.photos = true;

    Object.entries(DOC_SLOTS).forEach(([key, slot]) => tick(slot.box, !!slotOn[key]));
    tick('S53', false);
    tick('Y53', otherDocs.length > 0);
    const othersText = otherDocs.join('; ');
    put('AL53', othersText);
    const othersCell = ws.getCell('AL53');
    othersCell.alignment = { ...othersCell.alignment, wrapText: true, vertical: 'top' };
    othersCell.font = { ...(othersCell.font || {}), size: 7 };

    // ---- Declaration of conformity -----------------------------------------
    put('A62', `Applicant Name: ${d.clientNameExport || ''}`);

    // Product table: the template has room for 2 rows, add more when needed.
    // Row 78 of the template carries stray formatting, so every product row is
    // given the style of row 77.
    {
        const styleRow77 = ws.getRow(RFC_PRODUCT_FIRST_ROW);
        const row78 = ws.getRow(RFC_PRODUCT_FIRST_ROW + 1);
        row78.height = styleRow77.height;
        for (let c = 1; c <= 44; c += 1) {
            row78.getCell(c).style = JSON.parse(JSON.stringify(styleRow77.getCell(c).style || {}));
        }
    }
    const products = (d.perishableProducts || []).filter(p => p && (p.description || p.batchNumber || p.quantity));
    const extraRows = Math.max(0, products.length - RFC_PRODUCT_TEMPLATE_ROWS);
    if (extraRows > 0) {
        rfcShiftRowsDown(ws, RFC_PRODUCT_SPACER_ROW, extraRows);
        const styleRow = ws.getRow(RFC_PRODUCT_FIRST_ROW);
        for (let i = 0; i < extraRows; i += 1) {
            const r = RFC_PRODUCT_FIRST_ROW + RFC_PRODUCT_TEMPLATE_ROWS + i;
            const row = ws.getRow(r);
            row.height = styleRow.height || 15;
            for (let c = 1; c <= 44; c += 1) {
                const cell = row.getCell(c);
                cell.value = null;
                cell.style = JSON.parse(JSON.stringify(styleRow.getCell(c).style || {}));
            }
            RFC_PRODUCT_BLOCKS.forEach(([a, b]) => ws.mergeCells(`${a}${r}:${b}${r}`));
        }
    }

    products.forEach((p, i) => {
        const r = RFC_PRODUCT_FIRST_ROW + i;
        const [item, hs, name, sfda, origin, brand, qty, batch] = RFC_PRODUCT_BLOCKS.map(([a]) => `${a}${r}`);
        put(item, i + 1);
        put(name, p.description || '');
        put(origin, d.countryOfOriginName || '');
        put(qty, p.quantity || '');
        put(batch, p.batchNumber || '');
        rfcGrowRow(ws, r, [
            { text: p.description, cols: rfcColRange('F', 'M') },
            { text: p.batchNumber, cols: rfcColRange('AM', 'AR') },
        ], false);
    });

    // Name / signature / date of the declaration (applicant = exporter approver)
    const sigRow = 81 + extraRows;
    put(`B${sigRow}`, d.manufacturerApprovalName || '');
    put(`AE${sigRow}`, d.dateOfReport || d.dateOfInspection || '');
    [sigRow, sigRow + 1].forEach((r) => {
        const row = ws.getRow(r);
        if (!row.height || row.height < 24) row.height = 24;
    });
    await embedSignatureImage(wb, ws, d.manufacturerApprovalSignatureUrl, `S${sigRow}:AA${sigRow + 1}`);

    // The template has no print setup: without this it prints across 4 pages
    // with the right-hand columns cut off.
    ws.pageSetup = {
        ...(ws.pageSetup || {}),
        paperSize: 9,
        orientation: 'portrait',
        fitToPage: true,
        fitToWidth: 1,
        fitToHeight: 0,
        horizontalCentered: true,
        printArea: `A1:AR${ws.rowCount}`,
    };

    return await wb.xlsx.writeBuffer();
};

const generateInspectionXlsx = async (d) => {
    const checked = CB_CHECKED;
    const unchecked = CB_UNCHECKED;
    const yn = (val) => val === 'yes' ? checked : unchecked;
    const no = (val) => val === 'no' ? checked : unchecked;
    const na = (val) => val === 'na' ? checked : unchecked;

    const fillCheckboxInText = (text, replacements) => {
        let count = -1;
        return text.replace(/☐/g, (match) => {
            count++;
            const r = replacements.find(x => x.index === count);
            if (r) return r.checked ? checked : unchecked;
            return match;
        });
    };

    const templateArrayBuffer = await loadInspectionTemplateBuffer();

    const wb = new ExcelJS.Workbook();
    await wb.xlsx.load(templateArrayBuffer);
    const ws = wb.getWorksheet('Sheet1');

    const ROW_OFFSET = 5;

    const set = (coord, value) => {
        const match = coord.match(/^([A-Z]+)(\d+)$/);
        const finalCoord = match
            ? `${match[1]}${parseInt(match[2], 10) + ROW_OFFSET}`
            : coord;

        const cell = ws.getCell(finalCoord);
        cell.value = value;
    };

    set('G1', d.projectNo || '');
    set('T1', d.countryOfImport || '');
    // IR shows the Client (EXPORT) name (always BRF or LAR), not the importer.
    set('G2', d.clientNameExport || '');
    set('T2', d.clientAddress || '');
    // Row 7 is made of merged cells (no auto-fit) and client name / address can be long.
    growSamplingRowForText(ws, 7, [
        { text: d.clientNameExport, cols: ['G', 'H', 'I', 'J', 'K', 'L'] },
        { text: d.clientAddress, cols: ['T', 'U', 'V', 'W'] },
    ], false);
    set('B5', `Date of Inspection: ${d.dateOfInspection || ''}`);
    set('H5', `Date of Report: ${d.dateOfReport || ''}`);
    set('B6', `Inspection Valid Till: ${d.inspectionValidTill || ''}`);
    set('B7', `Arrive Insp. Location: ${d.arriveInspectionLocation || ''}`);
    set('H7', `Total Time: ${d.totalTime || ''}`);
    set('B8', `Leave Insp. Location: ${d.arriveInspectionLocation || ''}`);
    set('H8', `Travel Time: ${d.travelTime || ''}`);
    set('O5', `Location (Address):\n\n${d.placeOfInspection || ''}`);
    set('H9', d.applicableAirCaiNumber || '');

    set('P11', `Invoice No:  ${d.projectNo || ''}`);
    set('U11', `Invoice Date: ${d.dateOfInspection || ''}`);

    const cellU11 = ws.getCell(`U${11 + ROW_OFFSET}`);
    cellU11.font = {
        name: 'Calibri',
        size: 9,
        bold: false
    };

    // Multi-select. Match on the specific phrases: the "available" option's
    // own label can end in the word "attachment" and must not tick the second box.
    const fullDescOptions = (d.fullDescriptionOptions && d.fullDescriptionOptions.length > 0
        ? d.fullDescriptionOptions
        : [d.fullDescriptionOption || '']).map(o => String(o).toLowerCase());
    const isAvailableOnDocs = fullDescOptions.some(o => o.includes('available') || o.includes('commercial'));
    const isCompletedInAttach = fullDescOptions.some(o => o.includes('completed in attachment'));

    set('B12', fillCheckboxInText(
        'Full Description:\n☐ Available/Completed on commercial documents    ☐  Completed in attachment',
        [
            { index: 0, checked: isAvailableOnDocs },
            { index: 1, checked: isCompletedInAttach },
        ]
    ));

    const condRaw = (d.conditionOfGoods || '').toLowerCase();
    set('O12', fillCheckboxInText(
        'Condition of goods: \n   ☐  New     ☐  Used   ☐ Reconditioned  ',
        [
            { index: 0, checked: condRaw === 'new' },
            { index: 1, checked: condRaw === 'used' },
            { index: 2, checked: condRaw === 'reconditioned' },
        ]
    ));

    const ship = (d.shipmentType || '').toLowerCase().trim();
    const boarding = (d.boardingType || '').toLowerCase().trim();
    const container = (d.containerType || '').toLowerCase().trim();

    const is20 = container === '20\'' || container === '20' || container === '20ft' || container.includes('20');
    const is40 = container === '40\'' || container === '40' || container === '40ft' || container.includes('40');
    const isBulk = container === 'bulk' || container.includes('bulk');

    const isFCL = boarding === 'fcl' || boarding.includes('fcl') || boarding.includes('full container load');
    const isLCL = boarding === 'lcl' || boarding.includes('lcl') || boarding.includes('less than container load');
    const isConventional = boarding === 'conventional' || boarding.includes('conventional');

    const isSea = ship === 'sea' || ship.includes('sea');
    const isRoad = ship === 'road' || ship.includes('road');
    const isAir = ship === 'air' || ship.includes('air');

    const row15Options = {
        'Sea': isSea,
        'Road': isRoad,
        'Air': isAir,
        'Conventional': isConventional,
        'LCL': isLCL,
        'FCL': isFCL,
        '20’': is20,
        '20\'': is20,
        '40’': is40,
        '40\'': is40,
        'Bulk': isBulk
    };

    const row15 = ws.getRow(15 + ROW_OFFSET);
    row15.eachCell((cell) => {
        let val = cell.value;
        if (!val) return;

        let text = '';
        if (typeof val === 'object' && val.richText) {
            text = val.richText.map(rt => rt.text).join('');
        } else {
            text = val.toString();
        }

        let newText = text;

        for (const [label, isChecked] of Object.entries(row15Options)) {
            const escapedLabel = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const regexBefore = new RegExp(`([☐☒])(\\s*${escapedLabel})`, 'gi');
            newText = newText.replace(regexBefore, (m, p1, p2) => (isChecked ? '☒' : '☐') + p2);
            const regexAfter = new RegExp(`(${escapedLabel}\\s*)([☐☒])`, 'gi');
            newText = newText.replace(regexAfter, (m, p1, p2) => p1 + (isChecked ? '☒' : '☐'));
        }

        if (newText !== text) {
            cell.value = newText;
        }
    });

    set('B17', d.containerNo || '');
    set('E17', d.agentSealNo || '');
    set('I17', d.tuvSealNo || '');

    set('L17', fillCheckboxInText(
        'Storage conditions appropriate:  ☐ Yes    ☐ No  ',
        [
            { index: 0, checked: d.storageConditions === 'yes' },
            { index: 1, checked: d.storageConditions === 'no' },
        ]
    ));
    set('L18', fillCheckboxInText(
        'Are goods presented as per inspection ref. doc?     ☐ Yes    ☐ No  ',
        [
            { index: 0, checked: d.goodsPresentedAsRef === 'yes' },
            { index: 1, checked: d.goodsPresentedAsRef === 'no' },
        ]
    ));
    set('L19', fillCheckboxInText(
        'Was visual identification of goods satisfactory?  ☐ Yes    ☐ No  ',
        [
            { index: 0, checked: d.visualIdentification === 'yes' },
            { index: 1, checked: d.visualIdentification === 'no' },
        ]
    ));
    set('L20', fillCheckboxInText(
        'Was visual examination of goods satisfactory?          ☐ Yes    ☐ No  ',
        [
            { index: 0, checked: d.visualExamination === 'yes' },
            { index: 1, checked: d.visualExamination === 'no' },
        ]
    ));
    set('L21', fillCheckboxInText(
        'Packing/Shipping Marks conform to applicable to CAI and/or AIR:   ☐ Yes    ☐ No  ',
        [
            { index: 0, checked: d.packingMarksConform === 'yes' },
            { index: 1, checked: d.packingMarksConform === 'no' },
        ]
    ));
    set('L22', fillCheckboxInText(
        'Container Details Completed in Attachment             ☐ Yes    ☐ No  ',
        [
            { index: 0, checked: d.containerDetailsAttached === 'yes' },
            { index: 1, checked: d.containerDetailsAttached === 'no' },
        ]
    ));

    const packType = (d.packingType || '').toLowerCase();
    const qCheck = (d.qualityCheck || '').toLowerCase();

    set('B25',
        `Quantity Check: \nGross Weight: ${d.grossWeight || ''}        Net Weight: ${d.netWeight || ''}\n\n` +
        `Packing Number: ${d.packingNumber || ''}\n` +
        fillCheckboxInText(
            '☐Boxes   ☐Rolls  ☐Pallet   ☐Bundles \n\n☐Bags    ☐Cartons     ☐Others: \n\nQuality Check: \n☐ Certificate of Analysis provided  ☐ Subjected to Analysis/Test\n' +
            'Has Non-radioactive certificate been collected?        ☐ Yes    ☐ No\n' +
            'Has exporter\'s quality system been collected?   ☐ Yes     ☐ No',
            [
                { index: 0, checked: packType === 'boxes' },
                { index: 1, checked: packType === 'rolls' },
                { index: 2, checked: packType === 'pallet' },
                { index: 3, checked: packType === 'bundles' },
                { index: 4, checked: packType === 'bags' },
                { index: 5, checked: packType === 'cartons' },
                { index: 6, checked: packType === 'others' },
                { index: 7, checked: qCheck.includes('certificate of analysis') },
                { index: 8, checked: qCheck.includes('subjected') || qCheck.includes('analysis/test') },
                { index: 9, checked: d.nonRadioactiveCertificate === 'yes' },
                { index: 10, checked: d.nonRadioactiveCertificate === 'no' },
                { index: 11, checked: d.exporterQualitySystem === 'yes' },
                { index: 12, checked: d.exporterQualitySystem === 'no' },
            ]
        )
    );

    const sampTech = (d.samplingTechnique || '').toLowerCase();
    set('M25',
        fillCheckboxInText(
            'Photographs:  ☐ Yes     ☐ No\n',
            [
                { index: 0, checked: d.photographs === 'yes' },
                { index: 1, checked: d.photographs === 'no' },
            ]
        ) +
        `Total Quantity: ${d.totalQuantity || ''}       / Checked Quantity on AQL (Link): \n\n` +
        fillCheckboxInText(
            'Sampling Techniques Used:\n100%   ☐  Spot check (Randomly)  ☐    Sampling plan (as per AQL)  ☐\n\n',
            [
                { index: 0, checked: sampTech.includes('100%') },
                { index: 1, checked: sampTech.includes('spot') },
                { index: 2, checked: sampTech.includes('aql') || sampTech.includes('sampling plan') },
            ]
        ) +
        `Number of items spot checked (state quantity or %) :\n${d.itemsSpotChecked || ''}`
    );

    set('R29', yn(d.loading_containerClean));
    set('S29', no(d.loading_containerClean));
    set('U29', d.loading_containerCleanRemark || '');
    set('R30', yn(d.loading_coolingVerified));
    set('S30', no(d.loading_coolingVerified));
    set('T30', na(d.loading_coolingVerified));
    set('U30', d.loading_coolingVerifiedRemark || '');
    set('R32', yn(d.loading_photographed));
    set('S32', no(d.loading_photographed));
    set('T32', na(d.loading_photographed));
    set('U32', d.loading_photographedRemark || '');
    set('R33', yn(d.loading_conditionsMonitored));
    set('S33', no(d.loading_conditionsMonitored));
    set('T33', na(d.loading_conditionsMonitored));
    set('U33', d.loading_conditionsMonitoredRemark || '');

    const originOpt = (d.countryOfOriginOption || '').toLowerCase();
    const originName = d.countryOfOriginName || '';

    let originText = `Main country of origin: ${originName}\n`;
    originText += `☐ Available/Completed on commercial documents\n`;
    originText += `☐ Completed in attachment`;

    set('B36', fillCheckboxInText(
        originText,
        [
            { index: 0, checked: originOpt.includes('available') || originOpt.includes('commercial') },
            { index: 1, checked: originOpt.includes('attachment') },
        ]
    ));

    const docs = d.documents || [];
    const hasDoc = (keyword) => docs.some(doc => doc.toLowerCase().includes(keyword.toLowerCase()));
    set('M36', fillCheckboxInText(
        '☐ Invoice    ☐ Packing List   ☐ Price List   ☐ Technical Sheet\n☐ Catalogues  ☐ Certificate of Analysis ☐ Analysis results\n☐ Special product report  ☐ Description of Goods Attachment\n☐ Equipment\'s calibration certificate (if any)',
        [
            { index: 0, checked: hasDoc('Invoice') },
            { index: 1, checked: hasDoc('Packing List') },
            { index: 2, checked: hasDoc('Price List') },
            { index: 3, checked: hasDoc('Technical Sheet') },
            { index: 4, checked: hasDoc('Catalogues') },
            { index: 5, checked: hasDoc('Certificate of Analysis') },
            { index: 6, checked: hasDoc('Analysis results') },
            { index: 7, checked: hasDoc('Special product report') },
            { index: 8, checked: hasDoc('Description of Goods') },
            { index: 9, checked: hasDoc('calibration') },
        ]
    ));

    const maxRows = 13;
    const flatProducts = d.perishableProducts || [];

    for (let i = 0; i < maxRows; i++) {
        const rowNum = 39 + i;
        if (i < flatProducts.length) {
            const prod = flatProducts[i];
            set(`B${rowNum}`, prod.description || '');
            set(`F${rowNum}`, prod.batchNumber || '');
            set(`J${rowNum}`, prod.quantity || '');
            set(`Q${rowNum}`, prod.productionDate || '');
            set(`U${rowNum}`, prod.expiryDate || '');
            growSamplingRowForText(ws, rowNum + ROW_OFFSET, [
                { text: prod.description, cols: ['B', 'C', 'D', 'E'] },
                { text: prod.batchNumber, cols: ['F', 'G', 'H', 'I'] },
            ], false);
        } else {
            set(`B${rowNum}`, '');
            set(`F${rowNum}`, '');
            set(`J${rowNum}`, '');
            set(`Q${rowNum}`, '');
            set(`U${rowNum}`, '');
        }
    }

    set('R53', yn(d.sealing_sealingLocking));
    set('S53', no(d.sealing_sealingLocking));
    set('T53', d.sealing_sealingLockingRemark || '');
    set('R54', yn(d.sealing_airChecklist));
    set('S54', no(d.sealing_airChecklist));
    set('T54', d.sealing_airChecklistRemark || '');
    set('R55', yn(d.sealing_sealingPhotos));
    set('S55', no(d.sealing_sealingPhotos));
    set('T55', d.sealing_sealingPhotosRemark || '');
    set('R56', yn(d.sealing_inspectionPhotos));
    set('S56', no(d.sealing_inspectionPhotos));
    set('T56', d.sealing_inspectionPhotosRemark || '');

    const result = (d.inspectionResult || '').toLowerCase();
    set('B59', fillCheckboxInText(
        '☐ Satisfactory    ☐ Unsatisfactory    ☐ Deferred\n\nAdditional Information / Reason for Result Other Than Satisfactory:  \n',
        [
            { index: 0, checked: result === 'satisfactory' },
            { index: 1, checked: result === 'unsatisfactory' },
            { index: 2, checked: result === 'deferred' },
        ]
    ));

    // Section 10 signature block.
    //   row 66: Inspector Name (B) | Inspector Sign (M66:W66)
    //   row 68: Reviewed / Approved by (B) | Date (G) | Signature (M68:W68)
    const inspectorSigName = d.inspectorApprovalName || d.inspectorName || '';
    set('B61', inspectorSigName);
    const inspectorSigUrl = d.inspectorApprovalSignatureUrl || d.signatureUrl || '';
    const inspectorSigEmbedded = await embedSignatureImage(wb, ws, inspectorSigUrl, 'M66:W66');
    if (!inspectorSigEmbedded) set('M61', inspectorSigName);

    set('B63', d.finalApprovalName || '');
    set('G63', d.finalApprovalName ? (d.dateOfReport || '') : '');
    await embedSignatureImage(wb, ws, d.finalApprovalSignatureUrl, 'M68:W68');

    const buffer = await wb.xlsx.writeBuffer();
    return buffer;
};

const generateMonthlyReportXlsx = async (monthReports, year, month) => {
    const wb = new ExcelJS.Workbook();
    wb.creator = 'TÜV AUSTRIA Reports';
    wb.created = new Date();

    const monthName = new Date(year, month - 1, 1)
        .toLocaleString('en-US', { month: 'long' });

    const summary = wb.addWorksheet('Summary');
    summary.columns = [
        { header: 'Item', key: 'label', width: 40 },
        { header: 'Value', key: 'value', width: 24 },
    ];
    const sumHead = summary.getRow(1);
    sumHead.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    sumHead.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0D47A1' } };

    const total = monthReports.length;
    const downloaded = monthReports.filter(r => r.downloaded).length;
    const notDownloaded = total - downloaded;
    const satisf = monthReports.filter(r =>
        (r.inspectionResult || '').toLowerCase() === 'satisfactory').length;
    const unsat = monthReports.filter(r =>
        (r.inspectionResult || '').toLowerCase() === 'unsatisfactory').length;
    const deferred = monthReports.filter(r =>
        (r.inspectionResult || '').toLowerCase() === 'deferred').length;
    const totalFiles = monthReports.reduce((s, r) => s + r.filesCount, 0);
    const totalPhotos = monthReports.reduce((s, r) => s + r.photosCount, 0);

    summary.addRow({ label: 'Period', value: `${monthName} ${year}` });
    summary.addRow({ label: 'Total Reports', value: total });
    summary.addRow({ label: 'Downloaded', value: downloaded });
    summary.addRow({ label: 'Not Downloaded', value: notDownloaded });
    summary.addRow({ label: 'Satisfactory', value: satisf });
    summary.addRow({ label: 'Unsatisfactory', value: unsat });
    summary.addRow({ label: 'Deferred', value: deferred });
    summary.addRow({ label: 'Total Files', value: totalFiles });
    summary.addRow({ label: 'Total Photos', value: totalPhotos });

    const details = wb.addWorksheet('Reports');
    details.columns = [
        { header: 'Project No', key: 'projectNo', width: 18 },
        { header: 'Client', key: 'clientName', width: 28 },
        { header: 'Country', key: 'countryOfImport', width: 16 },
        { header: 'Container', key: 'containerNo', width: 16 },
        { header: 'Inspector', key: 'inspectorName', width: 22 },
        { header: 'Result', key: 'inspectionResult', width: 16 },
        { header: 'Date of Inspection', key: 'dateOfInspection', width: 18 },
        { header: 'Report Date', key: 'dateOfReport', width: 18 },
        { header: 'Created By', key: 'createdBy', width: 22 },
        { header: 'Created At', key: 'createdAt', width: 20 },
        { header: 'Files', key: 'filesCount', width: 8 },
        { header: 'Photos', key: 'photosCount', width: 8 },
        { header: 'Downloaded?', key: 'downloaded', width: 14 },
        { header: 'Download Count', key: 'downloadCount', width: 15 },
        { header: 'Last Download', key: 'lastDownloadAt', width: 20 },
        { header: 'Last Download By', key: 'lastDownloadBy', width: 24 },
        { header: 'Last Format', key: 'lastFormat', width: 12 },
    ];

    const hdr = details.getRow(1);
    hdr.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    hdr.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0D47A1' } };
    hdr.alignment = { vertical: 'middle', horizontal: 'center' };
    details.views = [{ state: 'frozen', ySplit: 1 }];

    const fmtDateTime = (iso) => {
        if (!iso) return '';
        const d = new Date(iso);
        if (isNaN(d.getTime())) return String(iso);
        return d.toLocaleDateString('pt-BR') + ' ' +
            d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    };

    monthReports.forEach(r => {
        const row = details.addRow({
            projectNo: r.projectNo || '',
            clientName: r.clientName || '',
            countryOfImport: r.countryOfImport || '',
            containerNo: r.containerNo || '',
            inspectorName: r.inspectorName || '',
            inspectionResult: r.inspectionResult || '',
            dateOfInspection: r.dateOfInspection || '',
            dateOfReport: r.dateOfReport || '',
            createdBy: r.createdBy || r.uploadedBy || '',
            createdAt: fmtDateTime(r.createdAt),
            filesCount: r.filesCount,
            photosCount: r.photosCount,
            downloaded: r.downloaded ? 'Yes' : 'No',
            downloadCount: r.downloadCount,
            lastDownloadAt: fmtDateTime(r.lastDownloadAt),
            lastDownloadBy: r.lastDownloadBy || '',
            lastFormat: r.lastDownloadFormat ? r.lastDownloadFormat.toUpperCase() : '',
        });

        const color = r.downloaded ? 'FFECFDF5' : 'FFFEF3F2';
        row.eachCell(cell => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: color } };
        });
    });

    details.autoFilter = {
        from: 'A1',
        to: { row: 1, column: details.columnCount },
    };

    return await wb.xlsx.writeBuffer();
};

const generateDateRangeReportXlsx = async (rangeReports, startDate, endDate) => {
    const wb = new ExcelJS.Workbook();
    wb.creator = 'TÜV AUSTRIA Reports';
    wb.created = new Date();

    const fmtInputDate = (d) => {
        if (!d) return '';
        const date = new Date(d);
        if (isNaN(date.getTime())) return String(d);
        return date.toLocaleDateString('pt-BR');
    };

    const periodLabel = `${fmtInputDate(startDate)} - ${fmtInputDate(endDate)}`;

    const summary = wb.addWorksheet('Summary');
    summary.columns = [
        { header: 'Item', key: 'label', width: 40 },
        { header: 'Value', key: 'value', width: 24 },
    ];
    const sumHead = summary.getRow(1);
    sumHead.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    sumHead.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0D47A1' } };

    const total = rangeReports.length;
    const downloaded = rangeReports.filter(r => r.downloaded).length;
    const notDownloaded = total - downloaded;
    const satisf = rangeReports.filter(r =>
        (r.inspectionResult || '').toLowerCase() === 'satisfactory').length;
    const unsat = rangeReports.filter(r =>
        (r.inspectionResult || '').toLowerCase() === 'unsatisfactory').length;
    const deferred = rangeReports.filter(r =>
        (r.inspectionResult || '').toLowerCase() === 'deferred').length;
    const totalFiles = rangeReports.reduce((s, r) => s + r.filesCount, 0);
    const totalPhotos = rangeReports.reduce((s, r) => s + r.photosCount, 0);

    summary.addRow({ label: 'Period', value: periodLabel });
    summary.addRow({ label: 'Total Reports', value: total });
    summary.addRow({ label: 'Downloaded', value: downloaded });
    summary.addRow({ label: 'Not Downloaded', value: notDownloaded });
    summary.addRow({ label: 'Satisfactory', value: satisf });
    summary.addRow({ label: 'Unsatisfactory', value: unsat });
    summary.addRow({ label: 'Deferred', value: deferred });
    summary.addRow({ label: 'Total Files', value: totalFiles });
    summary.addRow({ label: 'Total Photos', value: totalPhotos });

    const details = wb.addWorksheet('Reports');
    details.columns = [
        { header: 'Project No', key: 'projectNo', width: 18 },
        { header: 'Client', key: 'clientName', width: 28 },
        { header: 'Country', key: 'countryOfImport', width: 16 },
        { header: 'Container', key: 'containerNo', width: 16 },
        { header: 'Inspector', key: 'inspectorName', width: 22 },
        { header: 'Result', key: 'inspectionResult', width: 16 },
        { header: 'Date of Inspection', key: 'dateOfInspection', width: 18 },
        { header: 'Report Date', key: 'dateOfReport', width: 18 },
        { header: 'Created By', key: 'createdBy', width: 22 },
        { header: 'Created At', key: 'createdAt', width: 20 },
        { header: 'Files', key: 'filesCount', width: 8 },
        { header: 'Photos', key: 'photosCount', width: 8 },
        { header: 'Downloaded?', key: 'downloaded', width: 14 },
        { header: 'Download Count', key: 'downloadCount', width: 15 },
        { header: 'Last Download', key: 'lastDownloadAt', width: 20 },
        { header: 'Last Download By', key: 'lastDownloadBy', width: 24 },
        { header: 'Last Format', key: 'lastFormat', width: 12 },
    ];

    const hdr = details.getRow(1);
    hdr.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    hdr.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0D47A1' } };
    hdr.alignment = { vertical: 'middle', horizontal: 'center' };
    details.views = [{ state: 'frozen', ySplit: 1 }];

    const fmtDateTime = (iso) => {
        if (!iso) return '';
        const d = new Date(iso);
        if (isNaN(d.getTime())) return String(iso);
        return d.toLocaleDateString('pt-BR') + ' ' +
            d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    };

    rangeReports.forEach(r => {
        const row = details.addRow({
            projectNo: r.projectNo || '',
            clientName: r.clientName || '',
            countryOfImport: r.countryOfImport || '',
            containerNo: r.containerNo || '',
            inspectorName: r.inspectorName || '',
            inspectionResult: r.inspectionResult || '',
            dateOfInspection: r.dateOfInspection || '',
            dateOfReport: r.dateOfReport || '',
            createdBy: r.createdBy || r.uploadedBy || '',
            createdAt: fmtDateTime(r.createdAt),
            filesCount: r.filesCount,
            photosCount: r.photosCount,
            downloaded: r.downloaded ? 'Yes' : 'No',
            downloadCount: r.downloadCount,
            lastDownloadAt: fmtDateTime(r.lastDownloadAt),
            lastDownloadBy: r.lastDownloadBy || '',
            lastFormat: r.lastDownloadFormat ? r.lastDownloadFormat.toUpperCase() : '',
        });

        const color = r.downloaded ? 'FFECFDF5' : 'FFFEF3F2';
        row.eachCell(cell => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: color } };
        });
    });

    details.autoFilter = {
        from: 'A1',
        to: { row: 1, column: details.columnCount },
    };

    return await wb.xlsx.writeBuffer();
};

// --------------------------------------------------------------
// Deterministic report ID helper
// --------------------------------------------------------------
const buildReportDocId = (projectNo, containerNo) => {
    const cleanProject = String(projectNo || 'unknown')
        .trim()
        .replace(/[^a-z0-9]/gi, '_') || 'unknown';
    const cleanContainer = String(containerNo || 'unknown')
        .trim()
        .replace(/[^a-z0-9]/gi, '_') || 'unknown';
    return `${cleanProject}__${cleanContainer}`;
};

// --------------------------------------------------------------
// Low-level Excel cell reader
// --------------------------------------------------------------
const readCellValue = (ws, coord) => {
    const cell = ws.getCell(coord);
    const val = cell.value;
    if (val === null || val === undefined) return '';
    if (typeof val === 'object' && val.richText) {
        return val.richText.map(rt => rt.text).join('');
    }
    if (typeof val === 'object' && val.result !== undefined) {
        return String(val.result);
    }
    return String(val);
};

// --------------------------------------------------------------
// Placeholder / sanity validators for extracted metadata.
// --------------------------------------------------------------
const WORD_FORM_TOKEN_RE = /\b(?:FORMTEXT|FORMDROPDOWN|FORMCHECKBOX|MERGEFIELD)\b/i;

const LABEL_BLACKLIST = new Set([
    'agent', 'name', 'approval', 'formtext', 'form text', 'form-text',
    'client', 'client name', 'client (exporter)', 'client (importer)',
    'exporter', 'importer', 'inspector', 'container', 'container no',
    'container no.', 'country', 'country of import', 'sif', 'sif - unit',
    'unit', 'invoice', 'invoice no', 'invoice no.', 'project no',
    'project no (invoice)', 'result', 'inspection result', 'report date',
    'date of report', 'date of inspection', 'inspection date',
    'valid till', 'inspection valid till', 'tuv austria',
    'satisfactory', 'unsatisfactory', 'deferred',
    'n/a', 'na', 'none', 'null', 'undefined', '-', '--', '---',
    'insert', 'insert here', 'enter', 'enter value', 'value',
    'placeholder', 'to be filled', 'tbd', 'xx', 'xxx', 'xxxx',
    'address', 'sign', 'signature', 'date', 'of', 'of import', 'import',
    'export', 'importer name', 'exporter name', 'name surname',
    'name surname (inspector approval)',
    'name surname (manufacturer/exporter approval)',
    'name surname (laboratory approval)',
    'location (address)', 'place of inspection',
]);

const COMMON_SINGLE_WORDS = new Set([
    'address', 'sign', 'date', 'name', 'value', 'text', 'number',
    'city', 'state', 'country', 'zip', 'code', 'email', 'phone',
    'ref', 'note', 'info', 'data', 'type', 'item', 'unit',
    'street', 'avenue', 'road', 'box',
]);

const looksLikePlaceholder = (value) => {
    if (value === null || value === undefined) return true;
    const raw = String(value).trim();
    if (!raw) return true;

    if (WORD_FORM_TOKEN_RE.test(raw)) return true;

    const cleaned = raw
        .toLowerCase()
        .replace(/[:\-–—_.]+$/g, '')
        .replace(/\s+/g, ' ')
        .trim();

    if (!cleaned) return true;
    if (cleaned.length < 3) return true;

    if (LABEL_BLACKLIST.has(cleaned)) return true;
    const withoutParens = cleaned.replace(/[()]/g, '').trim();
    if (LABEL_BLACKLIST.has(withoutParens)) return true;

    const stripped = cleaned.replace(/^(of|the|and|to|for|in|on|by|from)\s+/i, '').trim();
    if (stripped && LABEL_BLACKLIST.has(stripped)) return true;

    if (/^[a-z]+$/.test(cleaned) && cleaned.length <= 10 && COMMON_SINGLE_WORDS.has(cleaned)) {
        return true;
    }

    if (!/[a-z0-9]/i.test(cleaned)) return true;

    return false;
};

const looksLikeContainerNo = (value) => {
    if (!value) return false;
    const v = String(value).trim().toUpperCase().replace(/\s+/g, '');
    if (WORD_FORM_TOKEN_RE.test(v)) return false;
    return /^[A-Z]{4}\d{7}$/.test(v) || /^[A-Z]{4}\d{6,7}[A-Z0-9]?$/.test(v);
};

const looksLikeProjectNo = (value) => {
    if (!value) return false;
    const v = String(value).trim();
    if (v.length < 3) return false;
    if (v.length > 60) return false;
    if (WORD_FORM_TOKEN_RE.test(v)) return false;
    if (!/[0-9]/.test(v)) return false;
    return /^[A-Z0-9][A-Z0-9._\-\/\s#]*$/i.test(v);
};

const sanitizeExtractedMetadata = (meta) => {
    const cleaned = { ...meta };

    for (const k of Object.keys(cleaned)) {
        if (typeof cleaned[k] === 'string' && WORD_FORM_TOKEN_RE.test(cleaned[k])) {
            cleaned[k] = '';
        }
    }

    if (looksLikePlaceholder(cleaned.projectNo) || !looksLikeProjectNo(cleaned.projectNo)) {
        cleaned.projectNo = '';
    }
    if (looksLikePlaceholder(cleaned.containerNo) || !looksLikeContainerNo(cleaned.containerNo)) {
        cleaned.containerNo = '';
    }
    for (const key of ['clientName', 'clientNameExport', 'inspectorName',
                       'countryOfImport', 'placeOfInspection', 'arriveInspectionLocation',
                       'unitName']) {
        if (looksLikePlaceholder(cleaned[key])) cleaned[key] = '';
    }
    for (const key of ['dateOfInspection', 'dateOfReport', 'inspectionValidTill']) {
        if (looksLikePlaceholder(cleaned[key]) || (cleaned[key] && !/\d/.test(cleaned[key]))) {
            cleaned[key] = '';
        }
    }
    if (cleaned.inspectionResult &&
        !['satisfactory', 'unsatisfactory', 'deferred'].includes(cleaned.inspectionResult.toLowerCase())) {
        cleaned.inspectionResult = '';
    }
    return cleaned;
};

// --------------------------------------------------------------
// Extract plain text from a .docx file (it's a ZIP of XML)
// --------------------------------------------------------------
const extractDocxText = async (arrayBuffer) => {
    try {
        // Validate the ZIP signature before handing the buffer to JSZip,
        // otherwise it throws "Can't find end of central directory".
        assertValidZipBuffer(arrayBuffer, 'documento .docx');

        const zip = await JSZip.loadAsync(arrayBuffer);

        const docXmlFile = zip.file('word/document.xml');
        if (!docXmlFile) return '';

        const xml = await docXmlFile.async('string');

        const rawText = xml
            .replace(/<w:p[ >]/g, '\n<w:p ')
            .replace(/<w:tab[^>]*\/>/g, '\t')
            .replace(/<w:br[^>]*\/>/g, '\n')
            .replace(/<[^>]+>/g, '')
            .replace(/&amp;/g, '&')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&quot;/g, '"')
            .replace(/&apos;/g, "'")
            .replace(/[ \t]+/g, ' ')
            .replace(/\n{3,}/g, '\n\n')
            .trim();

        const cleanedText = rawText
            .replace(/\bFORMTEXT\b/gi, '')
            .replace(/\bFORMDROPDOWN\b/gi, '')
            .replace(/\bFORMCHECKBOX\b/gi, '')
            .replace(/\bMERGEFIELD\b/gi, '')
            .replace(/\bREF\s+_Ref\w+/gi, '')
            .replace(/\bPAGEREF\s+_Toc\w+/gi, '')
            .replace(/[ \t]{2,}/g, ' ')
            .trim();

        const extraParts = [];
        for (const name of ['word/header1.xml', 'word/header2.xml', 'word/footer1.xml', 'word/footer2.xml']) {
            const f = zip.file(name);
            if (f) {
                const raw = await f.async('string');
                const clean = raw
                    .replace(/<[^>]+>/g, ' ')
                    .replace(/\bFORMTEXT\b/gi, '')
                    .replace(/\s+/g, ' ')
                    .trim();
                if (clean) extraParts.push(clean);
            }
        }

        return [cleanedText, ...extraParts].filter(Boolean).join('\n');
    } catch (err) {
        console.error('Erro ao extrair texto do .docx:', err);
        return '';
    }
};

// --------------------------------------------------------------
// Extract plain text from a .pdf file
// --------------------------------------------------------------
const extractPdfText = async (arrayBuffer) => {
    try {
        const ready = await ensurePdfWorker();
        if (!ready) {
            console.warn('Extração de PDF ignorada — worker não disponível.');
            return '';
        }

        const pdf = await pdfjsLib.getDocument({
            data: arrayBuffer,
            disableAutoFetch: true,
            disableStream: true,
        }).promise;

        const pageTexts = [];
        const pageCount = Math.min(pdf.numPages, 10);

        for (let i = 1; i <= pageCount; i++) {
            const page = await pdf.getPage(i);
            const content = await page.getTextContent();
            const pageText = content.items.map(it => it.str).join(' ');
            pageTexts.push(pageText);
        }

        return pageTexts.join('\n');
    } catch (err) {
        console.error('Erro ao extrair texto do .pdf:', err);
        return '';
    }
};

// --------------------------------------------------------------
// Stricter free-text extraction. Only accepts label:value pairs on
// the SAME line, rejects placeholder values, and validates IDs.
// --------------------------------------------------------------
const extractMetadataFromText = (text) => {
    const out = {
        projectNo: '',
        clientName: '',
        clientNameExport: '',
        countryOfImport: '',
        inspectorName: '',
        dateOfInspection: '',
        dateOfReport: '',
        inspectionValidTill: '',
        arriveInspectionLocation: '',
        containerNo: '',
        inspectionResult: '',
        placeOfInspection: '',
        unitName: '',
    };

    if (!text) return out;

    const norm = text.replace(/\r/g, '');
    const lines = norm
        .split('\n')
        .map(l => l.replace(/[ \t]+/g, ' ').trim())
        .filter(Boolean);

    const grabLine = (labelPatterns) => {
        for (const line of lines) {
            for (const pat of labelPatterns) {
                const re = new RegExp(`^\\s*${pat}\\s*[:\\-–]?\\s*(.+?)\\s*$`, 'i');
                const m = line.match(re);
                if (m && m[1] !== undefined) {
                    const candidate = m[1].trim();
                    if (WORD_FORM_TOKEN_RE.test(candidate)) continue;
                    if (!looksLikePlaceholder(candidate)) {
                        return candidate;
                    }
                }
            }
        }
        return '';
    };

    // Word forms usually put "Label:" and its value in separate table
    // cells, which extractDocxText turns into separate lines. This looks
    // at the line(s) AFTER a label-only line. `skipRe` lets us hop over
    // sibling header cells (e.g. "Inspector Sign" sits between the
    // "Inspector Name" header and the actual name in the row below).
    const LABEL_LIKE_RE = /^(?:Client\s*Address|Client\s*Name|Inspector\s*Sign|Inspector\s*Name|Country\s*of\s*Import|Project\s*No)\b/i;
    const grabNext = (labelPatterns, skipRe = null, maxSkips = 2) => {
        for (let i = 0; i < lines.length; i++) {
            const isLabelOnly = labelPatterns.some(pat =>
                new RegExp(`^\\s*${pat}\\s*[:\\-–]?\\s*$`, 'i').test(lines[i])
            );
            if (!isLabelOnly) continue;

            let skips = 0;
            for (let j = i + 1; j < lines.length; j++) {
                const cand = lines[j].trim();
                if (skipRe && skipRe.test(cand) && skips < maxSkips) { skips++; continue; }
                if (/:\s*$/.test(cand) || LABEL_LIKE_RE.test(cand)) break;
                if (WORD_FORM_TOKEN_RE.test(cand) && !cand.replace(WORD_FORM_TOKEN_RE, '').trim()) break;
                if (looksLikePlaceholder(cand)) break;
                return cand;
            }
        }
        return '';
    };

    const projectRaw = grabLine([
        'Invoice\\s*No\\.?',
        'Project\\s*No\\.?\\s*(?:\\(INVOICE\\))?',
        'Project\\s*N[oº]\\.?',
        'Invoice',
    ]);
    if (looksLikeProjectNo(projectRaw)) out.projectNo = projectRaw;

    const containerRaw = grabLine([
        'Container\\s*No\\.?',
        'Container',
    ]);
    if (looksLikeContainerNo(containerRaw)) out.containerNo = containerRaw;
    else if (containerRaw && !looksLikePlaceholder(containerRaw) && containerRaw.length >= 6) {
        out.containerNo = containerRaw;
    }

    const clientImport = grabLine([
        'Client\\s*Name\\s*\\(IMPORTER\\)',
        'Client\\s*\\(Importer\\)',
        'Importer\\s*Name',
        'Importer',
        'Client\\s*Name',
        'Client',
    ]) || grabNext([
        'Client\\s*Name\\s*\\(IMPORTER\\)',
        'Client\\s*\\(Importer\\)',
        'Importer\\s*Name',
        'Client\\s*Name',
    ]);
    if (!looksLikePlaceholder(clientImport)) out.clientName = clientImport;

    const clientExport = grabLine([
        'Client\\s*Name\\s*\\(EXPORT\\)',
        'Client\\s*\\(Export\\)',
        'Exporter\\s*Name',
        'Exporter',
    ]);
    if (!looksLikePlaceholder(clientExport)) out.clientNameExport = clientExport;

    const country = grabLine([
        'Country\\s*of\\s*Import',
        'Country',
    ]) || grabNext(['Country\\s*of\\s*Import']);
    if (!looksLikePlaceholder(country)) out.countryOfImport = country;

    const inspector = grabLine([
        'Inspector\\s*Name',
        'Inspection\\s*performed\\s*by',
        'Inspector',
    ]) || grabNext(['Inspector\\s*Name'], /^Inspector\s*Sign\b/i);
    if (!looksLikePlaceholder(inspector)) out.inspectorName = inspector;

    const dateInsp = grabLine([
        'Date\\s*of\\s*Inspection',
        'Inspection\\s*Date',
    ]);
    if (!looksLikePlaceholder(dateInsp) && /\d/.test(dateInsp)) out.dateOfInspection = dateInsp;

    const dateRep = grabLine([
        'Date\\s*of\\s*Report',
        'Report\\s*Date',
    ]);
    if (!looksLikePlaceholder(dateRep) && /\d/.test(dateRep)) out.dateOfReport = dateRep;

    const validTill = grabLine([
        'Inspection\\s*Valid\\s*Till',
        'Valid\\s*Till',
    ]);
    if (!looksLikePlaceholder(validTill) && /\d/.test(validTill)) out.inspectionValidTill = validTill;

    const arriveLoc = grabLine([
        'Arrive\\s*Insp\\.?\\s*Location',
        'Arrive\\s*Inspection\\s*Location',
    ]);
    if (!looksLikePlaceholder(arriveLoc)) out.arriveInspectionLocation = arriveLoc;

    const place = grabLine([
        'Location\\s*\\(Address\\)',
        'Place\\s*of\\s*Inspection',
    ]);
    if (!looksLikePlaceholder(place)) out.placeOfInspection = place;

    const unit = grabLine([
        'SIF\\s*[-–]?\\s*Unit',
        'Unit',
    ]);
    if (!looksLikePlaceholder(unit) && /\d/.test(unit)) out.unitName = unit;

    const flat = lines.join('\n');
    const resultMatch =
        flat.match(/☒\s*(Satisfactory|Unsatisfactory|Deferred)/i) ||
        flat.match(/\bInspection\s*Result[s]?\s*[:\-]\s*(Satisfactory|Unsatisfactory|Deferred)/i) ||
        flat.match(/\bResult\s*[:\-]\s*(Satisfactory|Unsatisfactory|Deferred)/i);

    if (resultMatch) {
        const raw = resultMatch[1].toLowerCase();
        if (raw === 'satisfactory') out.inspectionResult = 'Satisfactory';
        else if (raw === 'unsatisfactory') out.inspectionResult = 'Unsatisfactory';
        else if (raw === 'deferred') out.inspectionResult = 'Deferred';
    }

    return sanitizeExtractedMetadata(out);
};

// --------------------------------------------------------------
// Derive a container number candidate from the uploaded folder name.
// --------------------------------------------------------------
const extractContainerFromFolder = (rootFolder) => {
    if (!rootFolder) return '';
    const upper = String(rootFolder).toUpperCase();

    const normalized = upper.replace(/[\s\-_]+/g, '');
    const isoMatch = normalized.match(/([A-Z]{4}\d{7})/);
    if (isoMatch) return isoMatch[1];

    const compact = upper.replace(/[\s\-_]+/g, '');
    if (/^[A-Z0-9]{6,15}$/.test(compact)) return compact;

    return '';
};

// --------------------------------------------------------------
// Label-scan fallback for XLSX.
// --------------------------------------------------------------
const XLSX_LABEL_MAP = {
    'invoice no': 'projectNo',
    'invoice no.': 'projectNo',
    'invoice': 'projectNo',
    'project no': 'projectNo',
    'project no.': 'projectNo',
    'project no (invoice)': 'projectNo',
    'project n°': 'projectNo',
    'project no (invoice):': 'projectNo',

    'client name (importer)': 'clientName',
    'client (importer)': 'clientName',
    'client name': 'clientName',
    'importer name': 'clientName',
    'importer': 'clientName',
    'client': 'clientName',

    'client name (export)': 'clientNameExport',
    'client (export)': 'clientNameExport',
    'client name (exporter)': 'clientNameExport',
    'exporter name': 'clientNameExport',
    'exporter': 'clientNameExport',

    'country of import': 'countryOfImport',
    'country of import:': 'countryOfImport',
    'country': 'countryOfImport',

    'container no': 'containerNo',
    'container no.': 'containerNo',
    'container': 'containerNo',

    'inspector name': 'inspectorName',
    'inspector': 'inspectorName',
    'inspection performed by': 'inspectorName',

    'date of inspection': 'dateOfInspection',
    'inspection date': 'dateOfInspection',

    'date of report': 'dateOfReport',
    'report date': 'dateOfReport',

    'inspection valid till': 'inspectionValidTill',
    'valid till': 'inspectionValidTill',

    'arrive insp. location': 'arriveInspectionLocation',
    'arrive inspection location': 'arriveInspectionLocation',

    'location (address)': 'placeOfInspection',
    'place of inspection': 'placeOfInspection',

    'sif - unit': 'unitName',
    'sif – unit': 'unitName',
    'sif unit': 'unitName',
    'unit': 'unitName',
};

const normalizeLabel = (str) =>
    String(str || '')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .replace(/[:.\-–—]+$/g, '')
        .trim();

const scanSheetForLabels = (ws) => {
    const out = {};
    if (!ws) return out;

    ws.eachRow({ includeEmpty: false }, (row) => {
        row.eachCell({ includeEmpty: false }, (cell, colNumber) => {
            const raw = readCellValue(ws, cell.address).trim();
            if (!raw) return;

            const labelKey = normalizeLabel(raw);
            const field = XLSX_LABEL_MAP[labelKey];
            if (!field || out[field]) return;

            for (let c = colNumber + 1; c <= colNumber + 8; c += 1) {
                const coord = `${colLetterFromIndex(c)}${row.number}`;
                const v = readCellValue(ws, coord).trim();
                if (!v) continue;

                const vKey = normalizeLabel(v);
                if (XLSX_LABEL_MAP[vKey]) continue;
                if (looksLikePlaceholder(v)) continue;

                out[field] = v;
                break;
            }
        });
    });

    return out;
};

// --------------------------------------------------------------
// Extract metadata from uploaded files.
// --------------------------------------------------------------
// Reads bytes from either a local File (pre-upload) or a remote URL (post-upload).
const readFileBuffer = async (f) => {
    if (f && f.file && typeof f.file.arrayBuffer === 'function') {
        return f.file.arrayBuffer();
    }
    const response = await fetch(f.fileUrl);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const buffer = await response.arrayBuffer();
    if (!buffer || buffer.byteLength === 0) {
        throw new Error(`O arquivo "${f.fileName || f.fileUrl}" retornou vazio.`);
    }
    return buffer;
};

const extractFolderUploadMetadata = async (files, rootFolder = '') => {
    const empty = {
        projectNo: '',
        clientName: '',
        clientNameExport: '',
        countryOfImport: '',
        inspectorName: '',
        dateOfInspection: '',
        dateOfReport: '',
        inspectionValidTill: '',
        arriveInspectionLocation: '',
        containerNo: '',
        inspectionResult: '',
        placeOfInspection: '',
        unitName: '',
    };

    if (!files || files.length === 0) {
        const folderContainer = extractContainerFromFolder(rootFolder);
        if (folderContainer) {
            return sanitizeExtractedMetadata({
                ...empty,
                containerNo: folderContainer,
            });
        }
        return empty;
    }

    const rank = (f) => {
        const name = (f.fileName || '').toLowerCase();
        const path = (f.relativePath || '').toLowerCase();
        const full = `${path}/${name}`;
        let score = 0;

        if (full.includes('empty_inspection_report') || full.includes('empty inspection report')) {
            score -= 200;
        }
        if (full.includes('inspection_report') || full.includes('inspection report')) {
            score -= 100;
        }
        if (full.includes('tuv')) score -= 10;
        if (full.includes('report')) score -= 5;

        const depth = (f.relativePath || f.fileName || '').split('/').length;
        score += depth * 5;

        if (/\.xlsx?$/i.test(name)) score -= 30;
        else if (/\.docx?$/i.test(name)) score -= 20;
        else if (/\.pdf$/i.test(name)) score -= 10;

        return score;
    };

    const candidates = [...files].sort((a, b) => rank(a) - rank(b));

    const tryXlsx = async (file) => {
        const arrayBuffer = await readFileBuffer(file);

        // Bail out early with a clear message if the file isn't a valid ZIP
        // (e.g. HTML error page, truncated download, .xls renamed to .xlsx).
        assertValidZipBuffer(arrayBuffer, file.fileName || 'planilha .xlsx');

        const wb = new ExcelJS.Workbook();
        await wb.xlsx.load(arrayBuffer);
        const ws = wb.getWorksheet('Sheet1') || wb.worksheets[0];
        if (!ws) throw new Error('Worksheet não encontrada');

        const ROW_OFFSET = 5;
        const offsetCoord = (coord) => {
            const match = coord.match(/^([A-Z]+)(\d+)$/);
            if (!match) return coord;
            return `${match[1]}${parseInt(match[2], 10) + ROW_OFFSET}`;
        };
        const readOffset = (coord) => readCellValue(ws, offsetCoord(coord)).trim();

        const meta = { ...empty };

        meta.projectNo = readOffset('G1');
        meta.countryOfImport = readOffset('T1');
        meta.clientName = readOffset('G2');
        meta.clientAddress = readOffset('T2');

        const afterColon = (t) => {
            const i = t.indexOf(':');
            return i >= 0 ? t.substring(i + 1).trim() : '';
        };
        meta.dateOfInspection = afterColon(readOffset('B5'));
        meta.dateOfReport = afterColon(readOffset('H5'));
        meta.inspectionValidTill = afterColon(readOffset('B6'));
        meta.arriveInspectionLocation = afterColon(readOffset('B7'));

        meta.placeOfInspection = readOffset('O5').replace(/^Location \(Address\):\s*/i, '').trim();
        meta.containerNo = readOffset('B17');
        meta.inspectorName = readOffset('B61');

        const b59 = readOffset('B59');
        if (b59.includes('☒')) {
            if (/☒\s*Satisfactory/i.test(b59)) meta.inspectionResult = 'Satisfactory';
            else if (/☒\s*Unsatisfactory/i.test(b59)) meta.inspectionResult = 'Unsatisfactory';
            else if (/☒\s*Deferred/i.test(b59)) meta.inspectionResult = 'Deferred';
        }

        const scanned = scanSheetForLabels(ws);
        for (const k of Object.keys(meta)) {
            if (!meta[k] && scanned[k]) {
                meta[k] = scanned[k];
            }
        }

        return sanitizeExtractedMetadata(meta);
    };

    const tryDocx = async (file) => {
        const arrayBuffer = await readFileBuffer(file);
        const text = await extractDocxText(arrayBuffer);
        if (!text) return null;
        return extractMetadataFromText(text);
    };

    const tryPdf = async (file) => {
        const arrayBuffer = await readFileBuffer(file);
        const text = await extractPdfText(arrayBuffer);
        if (!text) return null;
        return extractMetadataFromText(text);
    };

    const merged = { ...empty };
    const fieldPriority = {};

    const fillFrom = (source, priority) => {
        if (!source) return 0;
        let filled = 0;
        for (const k of Object.keys(merged)) {
            if (!source[k]) continue;
            const currentPriority = fieldPriority[k];
            const isNew = !merged[k];
            const isBetter = currentPriority === undefined || priority < currentPriority;
            if (isNew || isBetter) {
                merged[k] = source[k];
                fieldPriority[k] = priority;
                filled++;
            }
        }
        return filled;
    };

    for (let i = 0; i < candidates.length; i++) {
        const file = candidates[i];
        const name = (file.fileName || '').toLowerCase();
        let partial = null;

        try {
            if (/\.xlsx?$/i.test(name)) {
                partial = await tryXlsx(file);
            } else if (/\.docx?$/i.test(name)) {
                partial = await tryDocx(file);
            } else if (/\.pdf$/i.test(name)) {
                partial = await tryPdf(file);
            }
        } catch (err) {
            console.warn(`Falha ao extrair metadados de ${name}:`, err);
        }

        const filled = fillFrom(partial, i);
        if (filled > 0) {
            console.log(`📋 Metadados parciais de ${name}:`, partial);
        }
    }

    const folderContainer = extractContainerFromFolder(rootFolder);
    if (folderContainer) {
        const inFileOk = looksLikeContainerNo(merged.containerNo);
        if (!inFileOk) {
            merged.containerNo = folderContainer;
            console.log(`📦 Container derivado do nome da pasta: ${folderContainer}`);
        }
    }

    const finalMeta = sanitizeExtractedMetadata(merged);
    console.log('📋 Metadados finais extraídos da pasta:', finalMeta);
    return finalMeta;
};

// --------------------------------------------------------------
// Firebase Storage & Firestore API service
// --------------------------------------------------------------
const reportApi = {
    getCurrentUser: () => {
        const user = auth.currentUser;
        if (user) {
            return {
                id: user.uid,
                name: user.displayName || user.email || 'Usuário',
                email: user.email || ''
            };
        }
        return null;
    },

    isAuthenticated: () => auth.currentUser !== null,

    getReports: async () => {
        if (!reportApi.isAuthenticated()) return [];
        try {
            const [reportsSnap, folderSnap] = await Promise.all([
                getDocs(query(collection(db, 'tuv_reports'), orderBy('createdAt', 'desc')))
                    .catch(err => { console.error('Erro ao carregar tuv_reports:', err); return null; }),
                getDocs(query(collection(db, 'tuv_folder_uploads'), orderBy('createdAt', 'desc')))
                    .catch(err => { console.error('Erro ao carregar tuv_folder_uploads:', err); return null; }),
            ]);

            const reports = reportsSnap
                ? reportsSnap.docs.map(d => ({
                    id: d.id,
                    ...d.data(),
                    _source: 'report',
                }))
                : [];

            const folderUploads = folderSnap
                ? folderSnap.docs.map(d => {
                    const data = d.data();
                    return {
                        id: d.id,
                        ...data,
                        _source: 'folderUpload',
                        reportFolder: data.reportFolder || data.rootFolder || 'Pasta',
                        zipRoot: data.zipRoot || `${data.rootFolder || 'Pasta'}/`,
                        createdBy: data.createdBy || data.uploadedBy || '',
                        createdByEmail: data.createdByEmail || data.uploadedByEmail || '',
                        photos: data.photos || [],
                        files: data.files || [],
                    };
                })
                : [];

            return [...reports, ...folderUploads].sort((a, b) => {
                const ta = new Date(a.createdAt || 0).getTime();
                const tb = new Date(b.createdAt || 0).getTime();
                return tb - ta;
            });
        } catch (error) {
            console.error('Error getting reports:', error);
            return [];
        }
    },

    getFolderUploads: async () => {
        if (!reportApi.isAuthenticated()) return [];
        try {
            const q = query(
                collection(db, 'tuv_folder_uploads'),
                orderBy('createdAt', 'desc')
            );
            const snap = await getDocs(q);
            return snap.docs.map(d => ({
                id: d.id,
                ...d.data(),
                _source: 'folderUpload',
            }));
        } catch (error) {
            console.error('Error getting folder uploads:', error);
            return [];
        }
    },

    findExistingReports: async (projectNo, containerNo) => {
        if (!reportApi.isAuthenticated()) return [];

        const cleanProject = String(projectNo || '').trim();
        const cleanContainer = String(containerNo || '').trim();

        if (!cleanProject && !cleanContainer) return [];

        try {
            const q = query(collection(db, 'tuv_reports'));
            const snap = await getDocs(q);

            const matches = snap.docs.filter(docSnap => {
                const d = docSnap.data();
                return (
                    String(d.projectNo || '').trim() === cleanProject &&
                    String(d.containerNo || '').trim() === cleanContainer
                );
            });

            return matches.map(match => ({ id: match.id, ...match.data() }));
        } catch (error) {
            console.error('Erro ao verificar duplicatas:', error);
            return [];
        }
    },

    // Looks for an already-registered report (folder uploads AND generated
    // reports) BEFORE anything is sent to Storage. Throws on failure so the
    // caller can fail closed instead of uploading unchecked.
    //   exact   -> same projectNo + same containerNo
    //   partial -> same projectNo/containerNo but the other key is missing
    findDuplicateUploads: async (projectNo, containerNo) => {
        if (!reportApi.isAuthenticated()) throw new Error('Usuário não autenticado');

        const normKey = (v) => String(v || '').trim().toUpperCase().replace(/\s+/g, '');
        const p = String(projectNo || '').trim();
        const c = normKey(containerNo);

        if (!p && !c) return { exact: [], partial: [], checked: false };

        const field = p ? 'projectNo' : 'containerNo';
        const value = p ? p : String(containerNo).trim();

        const exact = [];
        const partial = [];

        for (const [collName, source] of [['tuv_folder_uploads', 'folderUpload'], ['tuv_reports', 'report']]) {
            const snap = await getDocs(query(collection(db, collName), where(field, '==', value)));
            snap.docs.forEach(d => {
                const data = d.data();
                const entry = { id: d.id, _source: source, ...data };
                const docContainer = normKey(data.containerNo);

                if (p && c) {
                    if (docContainer === c) exact.push(entry);
                    else if (!docContainer) partial.push(entry);
                } else {
                    partial.push(entry);
                }
            });
        }

        return { exact, partial, checked: true };
    },

    deleteReport: async (reportId, source = 'report') => {
        if (!reportApi.isAuthenticated()) throw new Error('Usuário não autenticado');

        const collectionName = source === 'folderUpload'
            ? 'tuv_folder_uploads'
            : 'tuv_reports';

        const docSnap = await getDoc(doc(db, collectionName, reportId));
        if (docSnap.exists()) {
            const data = docSnap.data();
            const prefix = data.storagePrefix;

            if (prefix) {
                const deleteFolderRecursive = async (folderRef) => {
                    const listing = await listAll(folderRef);
                    await Promise.all(listing.items.map(item =>
                        deleteObject(item).catch(err => {
                            console.warn(`Falha ao deletar ${item.fullPath}:`, err.message);
                        })
                    ));
                    await Promise.all(listing.prefixes.map(sub =>
                        deleteFolderRecursive(sub)
                    ));
                };

                try {
                    const folderRef = ref(storage, prefix);
                    await deleteFolderRecursive(folderRef);
                } catch (err) {
                    console.warn('Erro ao apagar prefixo:', err.message);
                }
            } else {
                const allFiles = [...(data.files || []), ...(data.photos || [])];
                for (const file of allFiles) {
                    if (file.filePath) {
                        await deleteObject(ref(storage, file.filePath)).catch(() => { });
                    }
                }
            }
        }

        await deleteDoc(doc(db, collectionName, reportId));
        return true;
    },

    downloadReport: async (report, format = 'excel', onProgress = () => { }) => {
        const fetchAsBlob = async (fileUrl, filePath) => {
            if (fileUrl) {
                try {
                    const res = await fetch(fileUrl, { cache: 'force-cache' });
                    if (res.ok) return await res.blob();
                } catch {
                    // fall through to SDK
                }
            }
            const fileRef = ref(storage, filePath || fileUrl);
            return await getBlob(fileRef);
        };

        const blobToBase64 = (blob) =>
            new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    const dataUrl = reader.result;
                    resolve(dataUrl.split(',')[1]);
                };
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });

        const convertBlobToPdf = async (blob, fileName) => {
            const base64 = await blobToBase64(blob);
            const convertFunc = httpsCallable(functions, 'convertDocumentToPdf');
            const result = await convertFunc({ fileContent: base64, fileName });

            const byteCharacters = atob(result.data.pdfContent);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            return new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
        };

        try {
            const zip = new JSZip();

            let storagePrefix = report.storagePrefix;
            let zipRoot = report.zipRoot;

            if (!storagePrefix || !zipRoot) {
                const d = new Date(report.createdAt || Date.now());
                const year = d.getFullYear().toString();
                const month = (d.getMonth() + 1).toString().padStart(2, '0');
                const invoice = String(report.projectNo || 'unknown').replace(/[^a-z0-9]/gi, '_');
                const container = String(report.containerNo || 'unknown').replace(/[^a-z0-9]/gi, '_');
                const reportFolder = report.reportFolder || `Report_${invoice}_${container}`;

                storagePrefix = storagePrefix ||
                    `Reports/${year}/${month}/${reportFolder}/`;
                zipRoot = zipRoot || `${reportFolder}/`;
            }

            const allFiles = [
                ...(report.files || []),
                ...(report.photos || [])
            ];

            if (allFiles.length === 0) {
                throw new Error('Nenhum arquivo encontrado para download.');
            }

            const total = allFiles.length;
            let completed = 0;

            const filesNeedingPdf = format === 'pdf'
                ? allFiles.filter(f => /\.xlsx?$/i.test(f.fileName || ''))
                : [];
            const pdfTotal = filesNeedingPdf.length;

            const FETCH_CONCURRENCY = 8;

            const fetched = await mapWithConcurrency(
                allFiles,
                FETCH_CONCURRENCY,
                async (file) => {
                    const blob = await fetchAsBlob(file.fileUrl, file.filePath);
                    completed++;

                    const fetchShare = pdfTotal > 0 ? 0.7 : 0.95;
                    const percent = Math.round((completed / total) * 100 * fetchShare);

                    onProgress({
                        stage: 'fetching',
                        completed,
                        total,
                        percent,
                        currentFile: file.fileName || '',
                    });

                    return { file, blob };
                }
            );

            const PDF_CONCURRENCY = 3;
            let pdfCompleted = 0;

            const converted = await mapWithConcurrency(
                fetched,
                PDF_CONCURRENCY,
                async ({ file, blob }) => {
                    const isSpreadsheet = /\.xlsx?$/i.test(file.fileName || '');
                    if (format !== 'pdf' || !isSpreadsheet) {
                        return { file, blob };
                    }

                    const pdfBlob = await convertBlobToPdf(blob, file.fileName);
                    pdfCompleted++;

                    const percent = Math.round(
                        70 + (pdfCompleted / Math.max(1, pdfTotal)) * 25
                    );

                    onProgress({
                        stage: 'converting',
                        completed: pdfCompleted,
                        total: pdfTotal,
                        percent,
                        currentFile: file.fileName || '',
                    });

                    return { file, blob: pdfBlob };
                }
            );

            onProgress({ stage: 'zipping', completed: 0, total, percent: 95 });

            let filesCount = 0;
            let photosCount = 0;

            for (const { file, blob } of converted) {
                let finalFileName = file.fileName;
                let finalPath = file.filePath;

                if (file.type === 'photo') {
                    photosCount++;
                } else {
                    filesCount++;
                }

                if (format === 'pdf' && /\.xlsx?$/i.test(finalFileName)) {
                    finalFileName = finalFileName.replace(/\.xlsx?$/i, '.pdf');
                    finalPath = finalPath.replace(/\.xlsx?$/i, '.pdf');
                }

                const relative = finalPath.startsWith(storagePrefix)
                    ? finalPath.substring(storagePrefix.length)
                    : finalPath;

                zip.file(`${zipRoot}${relative}`, blob);
            }

            const zipBlob = await zip.generateAsync({ type: 'blob' });

            onProgress({ stage: 'saving', completed: 1, total: 1, percent: 100 });

            const invoice = String(report.projectNo || 'unknown').replace(/[^a-z0-9]/gi, '_');
            const container = String(report.containerNo || 'unknown').replace(/[^a-z0-9]/gi, '_');
            const zipName = `TUV_IR_SDS_${invoice}_${container}.zip`;
            saveAs(zipBlob, zipName);

            return {
                success: true,
                fileName: zipName,
                filesCount,
                photosCount
            };
        } catch (error) {
            console.error('Erro ao gerar ZIP:', error);
            throw error;
        }
    },

    logDownload: async (report, format, fileName, filesCount, photosCount) => {
        const user = auth.currentUser;

        const logEntry = {
            reportId: report.id,
            projectNo: report.projectNo || '',
            containerNo: report.containerNo || '',
            clientName: report.clientName || '',
            countryOfImport: report.countryOfImport || '',
            inspectorName: report.inspectorName || '',

            downloadedBy: user?.displayName || user?.email || 'Desconhecido',
            downloadedByEmail: user?.email || '',
            downloadedByUid: user?.uid || '',

            downloadedAt: new Date().toISOString(),
            format: format,
            fileName: fileName || '',
            filesCount: filesCount || 0,
            photosCount: photosCount || 0,
            reportFolder: report.reportFolder || '',
            source: report._source || 'report',
        };

        try {
            const docRef = await addDoc(collection(db, 'tuv_report_downloads'), logEntry);
            console.log('✅ Download registrado:', docRef.id, logEntry);
            return { id: docRef.id, ...logEntry };
        } catch (error) {
            console.error('❌ Falha ao registrar download:', error);
            return null;
        }
    },

    getDownloadLogs: async (filters = {}) => {
        if (!reportApi.isAuthenticated()) return [];
        try {
            const q = query(
                collection(db, 'tuv_report_downloads'),
                orderBy('downloadedAt', 'desc')
            );
            const snap = await getDocs(q);
            let logs = snap.docs.map(d => ({ id: d.id, ...d.data() }));

            if (filters.reportId) {
                logs = logs.filter(l => l.reportId === filters.reportId);
            }
            if (filters.userEmail) {
                logs = logs.filter(l => l.downloadedByEmail === filters.userEmail);
            }
            if (filters.startDate) {
                const start = new Date(filters.startDate).getTime();
                logs = logs.filter(l => new Date(l.downloadedAt).getTime() >= start);
            }
            if (filters.endDate) {
                const end = new Date(filters.endDate);
                end.setHours(23, 59, 59, 999);
                logs = logs.filter(l => new Date(l.downloadedAt).getTime() <= end.getTime());
            }
            return logs;
        } catch (error) {
            console.error('Erro ao carregar logs de download:', error);
            return [];
        }
    },

    getMonthlyReportData: async (year, month) => {
        if (!reportApi.isAuthenticated()) throw new Error('Usuário não autenticado');

        const allReports = await reportApi.getReports();

        const parseAnyDate = (raw) => {
            if (!raw) return null;
            let d = new Date(raw);
            if (!isNaN(d.getTime())) return d;
            const m = String(raw).match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
            if (m) {
                const yr = parseInt(m[3].length === 2 ? '20' + m[3] : m[3], 10);
                return new Date(yr, parseInt(m[2], 10) - 1, parseInt(m[1], 10));
            }
            return null;
        };

        const monthReports = allReports.filter(r => {
            const d = parseAnyDate(r.dateOfInspection) ||
                parseAnyDate(r.dateOfReport) ||
                parseAnyDate(r.createdAt);
            if (!d) return false;
            return d.getFullYear() === year && (d.getMonth() + 1) === month;
        });

        const q = query(collection(db, 'tuv_report_downloads'));
        const snap = await getDocs(q);
        const logsByReport = new Map();
        snap.docs.forEach(docSnap => {
            const log = { id: docSnap.id, ...docSnap.data() };
            if (!log.reportId) return;
            if (!logsByReport.has(log.reportId)) logsByReport.set(log.reportId, []);
            logsByReport.get(log.reportId).push(log);
        });

        return monthReports.map(r => {
            const logs = logsByReport.get(r.id) || [];
            logs.sort((a, b) =>
                new Date(b.downloadedAt).getTime() - new Date(a.downloadedAt).getTime()
            );
            const last = logs[0] || null;

            const filesCount = r.files?.length || 0;
            const photosCount = r.photos?.length || 0;

            return {
                ...r,
                downloaded: logs.length > 0,
                downloadCount: logs.length,
                lastDownloadAt: last?.downloadedAt || null,
                lastDownloadBy: last?.downloadedBy || null,
                lastDownloadFormat: last?.format || null,
                filesCount,
                photosCount,
            };
        }).sort((a, b) => {
            const da = parseAnyDate(a.dateOfInspection) || parseAnyDate(a.createdAt);
            const dbb = parseAnyDate(b.dateOfInspection) || parseAnyDate(b.createdAt);
            return (dbb?.getTime() || 0) - (da?.getTime() || 0);
        });
    },

    getDateRangeReportData: async (startDateStr, endDateStr) => {
        if (!reportApi.isAuthenticated()) throw new Error('Usuário não autenticado');

        const start = new Date(startDateStr);
        start.setHours(0, 0, 0, 0);
        const end = new Date(endDateStr);
        end.setHours(23, 59, 59, 999);

        if (isNaN(start.getTime()) || isNaN(end.getTime())) {
            throw new Error('Data inicial ou final inválida.');
        }
        if (start > end) {
            throw new Error('A data inicial não pode ser maior que a data final.');
        }

        const startMs = start.getTime();
        const endMs = end.getTime();

        const allReports = await reportApi.getReports();

        const rangeReports = allReports.filter(r => {
            if (!r.createdAt) return false;
            const t = new Date(r.createdAt).getTime();
            if (isNaN(t)) return false;
            return t >= startMs && t <= endMs;
        });

        const q = query(collection(db, 'tuv_report_downloads'));
        const snap = await getDocs(q);
        const logsByReport = new Map();
        snap.docs.forEach(docSnap => {
            const log = { id: docSnap.id, ...docSnap.data() };
            if (!log.reportId) return;
            if (!logsByReport.has(log.reportId)) logsByReport.set(log.reportId, []);
            logsByReport.get(log.reportId).push(log);
        });

        return rangeReports.map(r => {
            const logs = logsByReport.get(r.id) || [];
            logs.sort((a, b) =>
                new Date(b.downloadedAt).getTime() - new Date(a.downloadedAt).getTime()
            );
            const last = logs[0] || null;

            const filesCount = r.files?.length || 0;
            const photosCount = r.photos?.length || 0;

            return {
                ...r,
                downloaded: logs.length > 0,
                downloadCount: logs.length,
                lastDownloadAt: last?.downloadedAt || null,
                lastDownloadBy: last?.downloadedBy || null,
                lastDownloadFormat: last?.format || null,
                filesCount,
                photosCount,
            };
        }).sort((a, b) => {
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        });
    },
};

// --------------------------------------------------------------
// Skeleton Loader Component
// --------------------------------------------------------------
const SkeletonLoader = () => (
    <tbody>
        {[1, 2, 3, 4, 5].map(i => (
            <tr key={i} className={styles.skeletonRow}>
                <td><div className={styles.skeletonCell} style={{ width: '80px' }}></div></td>
                <td><div className={styles.skeletonCell} style={{ width: '80px' }}></div></td>
                <td><div className={styles.skeletonCell} style={{ width: '150px' }}></div></td>
                <td><div className={styles.skeletonCell} style={{ width: '120px' }}></div></td>
                <td><div className={styles.skeletonCell} style={{ width: '100px' }}></div></td>
                <td><div className={styles.skeletonCell} style={{ width: '100px' }}></div></td>
                <td><div className={styles.skeletonCell} style={{ width: '120px' }}></div></td>
                <td><div className={styles.skeletonCell} style={{ width: '100px' }}></div></td>
                <td><div className={styles.skeletonCell} style={{ width: '100px' }}></div></td>
                <td><div className={styles.skeletonCell} style={{ width: '100px' }}></div></td>
                <td><div className={styles.skeletonCell} style={{ width: '130px' }}></div></td>
            </tr>
        ))}
    </tbody>
);

// --------------------------------------------------------------
// Search helper: lower-cases and drops everything except letters/digits
// --------------------------------------------------------------
const compactSearchText = (value) =>
    String(value ?? '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]/g, '');

// --------------------------------------------------------------
// Reports Table Component
// --------------------------------------------------------------
const ReportsTable = ({
    reports, loading, onDownload, onDelete, onRefresh, onShowHistory, onShowFolderFiles,
    canDelete = false, canAttach = false, onAttach
}) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const [searchTerm, setSearchTerm] = useState('');
    const [filterResult, setFilterResult] = useState('all');
    const [filterCountry, setFilterCountry] = useState('all');
    const [filterInspector, setFilterInspector] = useState('all');
    const [filterSource, setFilterSource] = useState('all');
    const [filterDateRange, setFilterDateRange] = useState({ start: '', end: '' });
    const [showFilters, setShowFilters] = useState(false);

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        try {
            const date = new Date(dateStr);
            if (isNaN(date.getTime())) return String(dateStr);
            return date.toLocaleDateString('pt-BR') + ' ' + date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
        } catch { return String(dateStr); }
    };

    const safeString = (value, maxLength = null) => {
        if (value === undefined || value === null) return '-';
        let str = String(value);
        if (maxLength && str.length > maxLength) str = str.substring(0, maxLength - 3) + '...';
        return str;
    };

    const countryOptions = useMemo(() => {
        const countries = new Set();
        reports.forEach(r => {
            if (r.countryOfImport) countries.add(r.countryOfImport);
        });
        return ['all', ...Array.from(countries).sort()];
    }, [reports]);

    const inspectorOptions = useMemo(() => {
        const inspectors = new Set();
        reports.forEach(r => {
            if (r.inspectorName) inspectors.add(r.inspectorName);
        });
        return ['all', ...Array.from(inspectors).sort()];
    }, [reports]);

    const resultOptions = useMemo(() => {
        const results = new Set();
        reports.forEach(r => {
            if (r.inspectionResult) results.add(r.inspectionResult);
        });
        return ['all', ...Array.from(results).sort()];
    }, [reports]);

    const filteredReports = useMemo(() => {
        let filtered = [...reports];

        if (searchTerm.trim()) {
            const term = searchTerm.trim().toLowerCase();
            // Compact form (letters+digits only) so "MNBU 123456 7", "MNBU-1234567"
            // and just "1234567" all find container "MNBU1234567".
            const compactTerm = compactSearchText(term);
            const matches = (value) => {
                if (value === undefined || value === null || value === '') return false;
                const raw = String(value).toLowerCase();
                if (raw.includes(term)) return true;
                return compactTerm.length > 0 && compactSearchText(raw).includes(compactTerm);
            };
            filtered = filtered.filter(r =>
                matches(r.projectNo) ||
                matches(r.unitName) ||
                matches(r.clientName) ||
                matches(r.clientNameExport) ||
                matches(r.countryOfImport) ||
                matches(r.inspectorName) ||
                matches(r.createdBy) ||
                matches(r.containerNo) ||
                matches(r.rootFolder)
            );
        }

        if (filterResult !== 'all') {
            filtered = filtered.filter(r =>
                r.inspectionResult && r.inspectionResult.toLowerCase() === filterResult.toLowerCase()
            );
        }

        if (filterCountry !== 'all') {
            filtered = filtered.filter(r => r.countryOfImport === filterCountry);
        }

        if (filterInspector !== 'all') {
            filtered = filtered.filter(r => r.inspectorName === filterInspector);
        }

        if (filterSource !== 'all') {
            filtered = filtered.filter(r => (r._source || 'report') === filterSource);
        }

        if (filterDateRange.start) {
            const startDate = new Date(filterDateRange.start);
            startDate.setHours(0, 0, 0, 0);
            filtered = filtered.filter(r => {
                if (!r.createdAt) return false;
                const date = new Date(r.createdAt);
                return date >= startDate;
            });
        }
        if (filterDateRange.end) {
            const endDate = new Date(filterDateRange.end);
            endDate.setHours(23, 59, 59, 999);
            filtered = filtered.filter(r => {
                if (!r.createdAt) return false;
                const date = new Date(r.createdAt);
                return date <= endDate;
            });
        }

        return filtered;
    }, [reports, searchTerm, filterResult, filterCountry, filterInspector, filterSource, filterDateRange]);

    const totalPages = Math.ceil(filteredReports.length / pageSize);
    const paginatedReports = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        const end = start + pageSize;
        return filteredReports.slice(start, end);
    }, [filteredReports, currentPage, pageSize]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filterResult, filterCountry, filterInspector, filterSource, filterDateRange]);

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const handlePageSizeChange = (e) => {
        setPageSize(Number(e.target.value));
        setCurrentPage(1);
    };

    const clearAllFilters = () => {
        setSearchTerm('');
        setFilterResult('all');
        setFilterCountry('all');
        setFilterInspector('all');
        setFilterSource('all');
        setFilterDateRange({ start: '', end: '' });
        setCurrentPage(1);
    };

    const hasActiveFilters = searchTerm.trim() || filterResult !== 'all' || filterCountry !== 'all' || filterInspector !== 'all' || filterSource !== 'all' || filterDateRange.start || filterDateRange.end;

    if (loading) {
        return (
            <div className={styles.tableContainer}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th style={{ width: '8%' }}>Status</th>
                            <th style={{ width: '8%' }}>Tipo</th>
                            <th style={{ width: '10%' }}>Projeto</th>
                            <th style={{ width: '14%' }}>Cliente</th>
                            <th style={{ width: '8%' }}>País</th>
                            <th style={{ width: '8%' }}>Container</th>
                            <th style={{ width: '10%' }}>Inspetor</th>
                            <th style={{ width: '10%' }}>Resultado</th>
                            <th style={{ width: '8%' }}>Data Inspeção</th>
                            <th style={{ width: '8%' }}>Data Relatório</th>
                            <th style={{ width: '14%' }}>Ações</th>
                        </tr>
                    </thead>
                    <SkeletonLoader />
                </table>
            </div>
        );
    }

    if (!reports || reports.length === 0) {
        return (
            <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>📄</div>
                <p>Nenhum relatório gerado ainda</p>
                <span>Clique em "Novo Relatório" para começar</span>
            </div>
        );
    }

    return (
        <div>
            <div className={styles.filterBar} style={{
                background: '#fff',
                borderRadius: '12px',
                padding: '16px 20px',
                marginBottom: '16px',
                border: '1px solid #e9ecef',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}>
                <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '12px',
                    alignItems: 'center'
                }}>
                    <div style={{ flex: '1', minWidth: '200px' }}>
                        <input
                            type="text"
                            placeholder="Buscar por projeto, cliente, container, inspetor..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '8px 14px',
                                border: '2px solid #e2e8f0',
                                borderRadius: '8px',
                                fontSize: '14px',
                                transition: 'all 0.2s',
                                outline: 'none',
                                background: '#f8fafc'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#0d47a1'}
                            onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                        />
                    </div>

                    <button
                        onClick={() => setShowFilters(!showFilters)}
                        style={{
                            padding: '8px 16px',
                            background: showFilters ? '#0d47a1' : '#f1f5f9',
                            color: showFilters ? 'white' : '#475569',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            fontWeight: 500,
                            transition: 'all 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {showFilters ? '▲ Ocultar Filtros' : '▼ Mostrar Filtros'}
                        {hasActiveFilters && (
                            <span style={{
                                background: '#0d47a1',
                                color: 'white',
                                borderRadius: '50%',
                                width: '18px',
                                height: '18px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '11px',
                                fontWeight: 'bold'
                            }}>
                                {filteredReports.length}
                            </span>
                        )}
                    </button>

                    {hasActiveFilters && (
                        <button
                            onClick={clearAllFilters}
                            style={{
                                padding: '8px 14px',
                                background: 'transparent',
                                color: '#dc2626',
                                border: '1px solid #dc2626',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '13px',
                                fontWeight: 500,
                                transition: 'all 0.2s',
                                whiteSpace: 'nowrap'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.background = '#fee2e2';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.background = 'transparent';
                            }}
                        >
                            ✕ Limpar Filtros
                        </button>
                    )}

                    <span style={{
                        fontSize: '13px',
                        color: '#64748b',
                        marginLeft: 'auto',
                        whiteSpace: 'nowrap'
                    }}>
                        {filteredReports.length} {filteredReports.length === 1 ? 'resultado' : 'resultados'}
                    </span>
                </div>

                {showFilters && (
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                        gap: '16px',
                        marginTop: '16px',
                        paddingTop: '16px',
                        borderTop: '1px solid #e9ecef'
                    }}>
                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: '12px',
                                fontWeight: 600,
                                color: '#64748b',
                                marginBottom: '4px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}>
                                Resultado
                            </label>
                            <select
                                value={filterResult}
                                onChange={(e) => setFilterResult(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '8px 12px',
                                    border: '2px solid #e2e8f0',
                                    borderRadius: '8px',
                                    fontSize: '13px',
                                    outline: 'none',
                                    background: '#f8fafc'
                                }}
                            >
                                {resultOptions.map(opt => (
                                    <option key={opt} value={opt}>
                                        {opt === 'all' ? 'Todos' : opt}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: '12px',
                                fontWeight: 600,
                                color: '#64748b',
                                marginBottom: '4px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}>
                                País
                            </label>
                            <select
                                value={filterCountry}
                                onChange={(e) => setFilterCountry(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '8px 12px',
                                    border: '2px solid #e2e8f0',
                                    borderRadius: '8px',
                                    fontSize: '13px',
                                    outline: 'none',
                                    background: '#f8fafc'
                                }}
                            >
                                {countryOptions.map(opt => (
                                    <option key={opt} value={opt}>
                                        {opt === 'all' ? 'Todos' : opt}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: '12px',
                                fontWeight: 600,
                                color: '#64748b',
                                marginBottom: '4px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}>
                                Inspetor
                            </label>
                            <select
                                value={filterInspector}
                                onChange={(e) => setFilterInspector(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '8px 12px',
                                    border: '2px solid #e2e8f0',
                                    borderRadius: '8px',
                                    fontSize: '13px',
                                    outline: 'none',
                                    background: '#f8fafc'
                                }}
                            >
                                {inspectorOptions.map(opt => (
                                    <option key={opt} value={opt}>
                                        {opt === 'all' ? 'Todos' : opt}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: '12px',
                                fontWeight: 600,
                                color: '#64748b',
                                marginBottom: '4px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}>
                                Tipo
                            </label>
                            <select
                                value={filterSource}
                                onChange={(e) => setFilterSource(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '8px 12px',
                                    border: '2px solid #e2e8f0',
                                    borderRadius: '8px',
                                    fontSize: '13px',
                                    outline: 'none',
                                    background: '#f8fafc'
                                }}
                            >
                                <option value="all">Todos</option>
                                <option value="report">Relatórios gerados</option>
                                <option value="folderUpload">Pastas enviadas</option>
                            </select>
                        </div>

                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: '12px',
                                fontWeight: 600,
                                color: '#64748b',
                                marginBottom: '4px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}>
                                Data Início
                            </label>
                            <input
                                type="date"
                                value={filterDateRange.start}
                                onChange={(e) => setFilterDateRange(prev => ({ ...prev, start: e.target.value }))}
                                style={{
                                    width: '100%',
                                    padding: '8px 12px',
                                    border: '2px solid #e2e8f0',
                                    borderRadius: '8px',
                                    fontSize: '13px',
                                    outline: 'none',
                                    background: '#f8fafc'
                                }}
                            />
                        </div>

                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: '12px',
                                fontWeight: 600,
                                color: '#64748b',
                                marginBottom: '4px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}>
                                Data Fim
                            </label>
                            <input
                                type="date"
                                value={filterDateRange.end}
                                onChange={(e) => setFilterDateRange(prev => ({ ...prev, end: e.target.value }))}
                                style={{
                                    width: '100%',
                                    padding: '8px 12px',
                                    border: '2px solid #e2e8f0',
                                    borderRadius: '8px',
                                    fontSize: '13px',
                                    outline: 'none',
                                    background: '#f8fafc'
                                }}
                            />
                        </div>
                    </div>
                )}
            </div>

            <div className={styles.tableContainer}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th style={{ width: '7%' }}>Container</th>
                            <th style={{ width: '9%' }}>Invoice</th>
                            <th style={{ width: '12%' }}>Client (Exporter)</th>
                            <th style={{ width: '12%' }}>Client (Importer)</th>
                            <th style={{ width: '7%' }}>Country</th>
                            <th style={{ width: '11%' }}>SIF - Unit</th>
                            <th style={{ width: '9%' }}>Inspector</th>
                            <th style={{ width: '9%' }}>Result</th>
                            <th style={{ width: '7%' }}>Date of Inspection</th>
                            <th style={{ width: '7%' }}>Report Date</th>
                            <th style={{ width: '10%' }}>Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedReports.length === 0 ? (
                            <tr>
                                <td colSpan="11" style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
                                    Nenhum relatório encontrado com os filtros selecionados
                                </td>
                            </tr>
                        ) : (
                            paginatedReports.map(report => {
                                const result = report.inspectionResult || '';
                                const isFolderUpload = report._source === 'folderUpload';
                                const folderHasMetadata = isFolderUpload && !!(report.projectNo || report.containerNo);
                                const resultColor = result.toLowerCase() === 'satisfactory' ? '#16a34a' :
                                    result.toLowerCase() === 'unsatisfactory' ? '#dc2626' : '#f59e0b';
                                const resultBg = result.toLowerCase() === 'satisfactory' ? '#dcfce7' :
                                    result.toLowerCase() === 'unsatisfactory' ? '#fee2e2' : '#fef3c7';

                                return (
                                    <tr key={`${report._source || 'report'}-${report.id}`} className={styles.tableRow}>
                                        <td>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <span>{safeString(report.containerNo, 15)}</span>
                                            </div>
                                        </td>
                                        <td title={safeString(report.projectNo)}>
                                            <strong>{safeString(report.projectNo, 20)}</strong>
                                        </td>
                                        <td title={safeString(report.clientNameExport)}>
                                            {safeString(report.clientNameExport, 25)}
                                        </td>
                                        <td title={safeString(report.clientName)}>
                                            {safeString(report.clientName, 25)}
                                        </td>
                                        <td>{safeString(report.countryOfImport)}</td>
                                        <td title={safeString(report.unitName)}>
                                            {safeString(report.unitName, 25)}
                                        </td>
                                        <td>{safeString(report.inspectorName)}</td>
                                        <td>
                                            {result ? (
                                                <span style={{
                                                    display: 'inline-block',
                                                    padding: '2px 12px',
                                                    borderRadius: '20px',
                                                    fontSize: '12px',
                                                    fontWeight: 600,
                                                    background: resultBg,
                                                    color: resultColor
                                                }}>
                                                    {result}
                                                </span>
                                            ) : '-'}
                                        </td>
                                        <td>{formatDate(report.dateOfInspection)}</td>
                                        <td>{formatDate(report.dateOfReport || report.createdAt)}</td>
                                        <td>
                                            <div className={styles.actionsContainer}>
                                                <button
                                                    onClick={() => onDownload(report)}
                                                    className={`${styles.actionButton} ${styles.downloadButton}`}
                                                    title="Baixar (Excel ou PDF)"
                                                >
                                                    <HiOutlineDocumentDownload size={14} />
                                                </button>
                                                {isFolderUpload ? (
                                                    <button
                                                        onClick={() => onShowFolderFiles && onShowFolderFiles(report)}
                                                        className={`${styles.actionButton}`}
                                                        style={{ background: '#f3e8ff', color: '#6d28d9' }}
                                                        title="Ver arquivos da pasta"
                                                    >
                                                        📂
                                                    </button>
                                                ) : (
                                                    <button
                                                        onClick={() => onShowHistory(report)}
                                                        className={`${styles.actionButton}`}
                                                        style={{ background: '#eff6ff', color: '#1e40af' }}
                                                        title="Ver histórico de downloads"
                                                    >
                                                        <HiOutlineClock size={14} />
                                                    </button>
                                                )}
                                                {canAttach && (
                                                    <button
                                                        onClick={() => onAttach && onAttach(report)}
                                                        className={`${styles.actionButton}`}
                                                        style={{ background: '#ecfdf5', color: '#047857' }}
                                                        title="Anexar documentos"
                                                    >
                                                        📎
                                                    </button>
                                                )}
                                                {canDelete && (
                                                    <button
                                                        onClick={() => onDelete(report.id, report._source || 'report')}
                                                        className={`${styles.actionButton} ${styles.deleteButton}`}
                                                        title="Excluir"
                                                    >
                                                        <HiOutlineTrash size={14} />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {filteredReports.length > 0 && (
                <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '16px 20px',
                    background: '#fff',
                    borderRadius: '12px',
                    border: '1px solid #e9ecef',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                    marginTop: '16px',
                    gap: '12px'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        flexWrap: 'wrap'
                    }}>
                        <span style={{ fontSize: '14px', color: '#64748b' }}>
                            Mostrando {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, filteredReports.length)} de {filteredReports.length}
                        </span>
                        <select
                            value={pageSize}
                            onChange={handlePageSizeChange}
                            style={{
                                padding: '6px 10px',
                                border: '2px solid #e2e8f0',
                                borderRadius: '8px',
                                fontSize: '13px',
                                outline: 'none',
                                background: '#f8fafc'
                            }}
                        >
                            <option value={5}>5 por página</option>
                            <option value={10}>10 por página</option>
                            <option value={25}>25 por página</option>
                            <option value={50}>50 por página</option>
                        </select>
                    </div>

                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                    }}>
                        <button
                            onClick={() => handlePageChange(1)}
                            disabled={currentPage === 1}
                            style={{
                                padding: '6px 12px',
                                border: '1px solid #e2e8f0',
                                borderRadius: '6px',
                                background: currentPage === 1 ? '#f1f5f9' : 'white',
                                color: currentPage === 1 ? '#94a3b8' : '#475569',
                                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                fontSize: '13px',
                                transition: 'all 0.2s'
                            }}
                        >
                            «
                        </button>
                        <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            style={{
                                padding: '6px 12px',
                                border: '1px solid #e2e8f0',
                                borderRadius: '6px',
                                background: currentPage === 1 ? '#f1f5f9' : 'white',
                                color: currentPage === 1 ? '#94a3b8' : '#475569',
                                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                fontSize: '13px',
                                transition: 'all 0.2s'
                            }}
                        >
                            ‹
                        </button>

                        <span style={{
                            padding: '6px 16px',
                            fontSize: '14px',
                            fontWeight: 600,
                            color: '#0d47a1',
                            background: '#eff6ff',
                            borderRadius: '6px',
                            minWidth: '40px',
                            textAlign: 'center'
                        }}>
                            {currentPage} / {totalPages}
                        </span>

                        <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            style={{
                                padding: '6px 12px',
                                border: '1px solid #e2e8f0',
                                borderRadius: '6px',
                                background: currentPage === totalPages ? '#f1f5f9' : 'white',
                                color: currentPage === totalPages ? '#94a3b8' : '#475569',
                                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                                fontSize: '13px',
                                transition: 'all 0.2s'
                            }}
                        >
                            ›
                        </button>
                        <button
                            onClick={() => handlePageChange(totalPages)}
                            disabled={currentPage === totalPages}
                            style={{
                                padding: '6px 12px',
                                border: '1px solid #e2e8f0',
                                borderRadius: '6px',
                                background: currentPage === totalPages ? '#f1f5f9' : 'white',
                                color: currentPage === totalPages ? '#94a3b8' : '#475569',
                                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                                fontSize: '13px',
                                transition: 'all 0.2s'
                            }}
                        >
                            »
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

// --------------------------------------------------------------
// Auth Guard Component
// --------------------------------------------------------------
const AuthGuard = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [checking, setChecking] = useState(true);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            setIsAuthenticated(!!user);
            setChecking(false);
        });
        return () => unsubscribe();
    }, []);

    if (checking) return <div className={styles.loadingContainer}>Verificando autenticação...</div>;

    if (!isAuthenticated) {
        return (
            <div className={styles.authErrorContainer}>
                <div className={styles.authErrorIcon}>🔒</div>
                <h3>Você precisa estar logado para acessar esta página</h3>
                <p>Por favor, faça login no sistema para gerar e gerenciar relatórios TÜV.</p>
                <button onClick={() => window.location.href = '/login'} className={styles.primaryButton}>Ir para Login</button>
            </div>
        );
    }

    return children;
};

// --------------------------------------------------------------
// Download History Modal Component
// --------------------------------------------------------------
const DownloadHistoryModal = ({ report, onClose }) => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        (async () => {
            setLoading(true);
            const history = await reportApi.getDownloadLogs({ reportId: report.id });
            if (mounted) {
                setLogs(history);
                setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, [report.id]);

    const formatDateTime = (iso) => {
        if (!iso) return '-';
        try {
            const d = new Date(iso);
            return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
        } catch { return iso; }
    };

    return (
        <div className={styles.modalBackdrop} onClick={onClose}>
            <div
                className={styles.formContainer}
                style={{ maxWidth: '720px', padding: '24px 28px', borderRadius: '16px' }}
                onClick={(e) => e.stopPropagation()}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                    <div>
                        <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#1e293b' }}>
                            📥 Histórico de Downloads
                        </h3>
                        <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '13px' }}>
                            {report.projectNo || '-'} · {report.clientName || '-'} · Container {report.containerNo || '-'}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            background: '#f1f5f9',
                            border: 'none',
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            fontSize: '20px',
                            cursor: 'pointer',
                            color: '#64748b',
                            fontWeight: 300
                        }}
                    >
                        ×
                    </button>
                </div>

                {loading ? (
                    <p style={{ textAlign: 'center', color: '#64748b', padding: '20px' }}>Carregando...</p>
                ) : logs.length === 0 ? (
                    <div style={{
                        textAlign: 'center',
                        padding: '40px 20px',
                        background: '#f8fafc',
                        borderRadius: '12px',
                        color: '#94a3b8'
                    }}>
                        <div style={{ fontSize: '36px', marginBottom: '8px' }}>📭</div>
                        <p style={{ margin: 0 }}>Nenhum download registrado para este relatório</p>
                    </div>
                ) : (
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                            <thead>
                                <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                                    <th style={{ padding: '10px 12px', fontWeight: 600, color: '#475569' }}>Usuário</th>
                                    <th style={{ padding: '10px 12px', fontWeight: 600, color: '#475569' }}>Formato</th>
                                    <th style={{ padding: '10px 12px', fontWeight: 600, color: '#475569' }}>Arquivos</th>
                                    <th style={{ padding: '10px 12px', fontWeight: 600, color: '#475569' }}>Data/Hora</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.map(log => (
                                    <tr key={log.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: '10px 12px' }}>
                                            <div style={{ fontWeight: 600, color: '#1e293b' }}>{log.downloadedBy}</div>
                                            <div style={{ fontSize: '11px', color: '#94a3b8' }}>{log.downloadedByEmail}</div>
                                        </td>
                                        <td style={{ padding: '10px 12px' }}>
                                            <span style={{
                                                padding: '2px 10px',
                                                borderRadius: '20px',
                                                fontSize: '11px',
                                                fontWeight: 600,
                                                background: log.format === 'pdf' ? '#fee2e2' : '#dbeafe',
                                                color: log.format === 'pdf' ? '#991b1b' : '#1e40af'
                                            }}>
                                                {log.format?.toUpperCase()}
                                            </span>
                                        </td>
                                        <td style={{ padding: '10px 12px', color: '#475569' }}>
                                            {log.filesCount} doc(s) · {log.photosCount} foto(s)
                                        </td>
                                        <td style={{ padding: '10px 12px', color: '#475569' }}>
                                            {formatDateTime(log.downloadedAt)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

// --------------------------------------------------------------
// Folder Upload Detail Modal
// --------------------------------------------------------------
const FolderUploadDetailModal = ({ report, onClose }) => {
    const files = report.files || [];
    const photos = report.photos || [];
    const allFiles = [...files, ...photos];

    const formatBytes = (bytes) => {
        if (!bytes && bytes !== 0) return '-';
        const units = ['B', 'KB', 'MB', 'GB'];
        let i = 0;
        let n = Number(bytes);
        while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
        return `${n.toFixed(n >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
    };

    const formatDateTime = (iso) => {
        if (!iso) return '-';
        try {
            const d = new Date(iso);
            return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
        } catch { return iso; }
    };

    return (
        <div className={styles.modalBackdrop} onClick={onClose}>
            <div
                className={styles.formContainer}
                style={{ maxWidth: '820px', padding: '24px 28px', borderRadius: '16px' }}
                onClick={(e) => e.stopPropagation()}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
                    <div>
                        <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#1e293b' }}>
                            📁 Arquivos da Pasta
                        </h3>
                        <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '13px' }}>
                            {report.rootFolder || '-'} · {allFiles.length} arquivo(s)
                        </p>
                        <p style={{ margin: '2px 0 0 0', color: '#94a3b8', fontSize: '12px' }}>
                            Enviado por {report.uploadedBy || report.createdBy || '-'} em {formatDateTime(report.createdAt)}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            background: '#f1f5f9',
                            border: 'none',
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            fontSize: '20px',
                            cursor: 'pointer',
                            color: '#64748b',
                            fontWeight: 300
                        }}
                    >
                        ×
                    </button>
                </div>

                {allFiles.length === 0 ? (
                    <div style={{
                        textAlign: 'center',
                        padding: '40px 20px',
                        background: '#f8fafc',
                        borderRadius: '12px',
                        color: '#94a3b8'
                    }}>
                        <div style={{ fontSize: '36px', marginBottom: '8px' }}>📭</div>
                        <p style={{ margin: 0 }}>Nenhum arquivo registrado</p>
                    </div>
                ) : (
                    <div style={{ maxHeight: '450px', overflowY: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                            <thead>
                                <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                                    <th style={{ padding: '10px 12px', fontWeight: 600, color: '#475569' }}>Caminho</th>
                                    <th style={{ padding: '10px 12px', fontWeight: 600, color: '#475569', width: '90px' }}>Tamanho</th>
                                    <th style={{ padding: '10px 12px', fontWeight: 600, color: '#475569', width: '80px' }}>Ação</th>
                                </tr>
                            </thead>
                            <tbody>
                                {allFiles.map((file, idx) => (
                                    <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: '10px 12px' }}>
                                            <div style={{ fontWeight: 600, color: '#1e293b' }}>
                                                {file.relativePath || file.fileName}
                                            </div>
                                        </td>
                                        <td style={{ padding: '10px 12px', color: '#475569' }}>
                                            {formatBytes(file.fileSize)}
                                        </td>
                                        <td style={{ padding: '10px 12px' }}>
                                            <a
                                                href={file.fileUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{
                                                    padding: '4px 10px',
                                                    borderRadius: '6px',
                                                    background: '#eff6ff',
                                                    color: '#1e40af',
                                                    fontSize: '12px',
                                                    fontWeight: 600,
                                                    textDecoration: 'none'
                                                }}
                                            >
                                                Abrir
                                            </a>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

// --------------------------------------------------------------
// Download Progress Bar (inline component)
// --------------------------------------------------------------
const DownloadProgress = ({ progress, format }) => {
    const percent = Math.max(0, Math.min(100, progress?.percent || 0));
    const stage = progress?.stage || 'starting';

    const stageLabel = {
        starting: 'Preparando...',
        fetching: 'Baixando arquivos',
        converting: 'Convertendo para PDF',
        zipping: 'Compactando arquivos',
        saving: 'Salvando arquivo',
    }[stage] || 'Processando...';

    const stageEmoji = {
        starting: '⏳',
        fetching: '⬇️',
        converting: '📄',
        zipping: '🗜️',
        saving: '💾',
    }[stage] || '⏳';

    const detail = (() => {
        if (!progress) return '';
        if (stage === 'fetching' && progress.total) {
            return `${progress.completed}/${progress.total} arquivos`;
        }
        if (stage === 'converting' && progress.total) {
            return `${progress.completed}/${progress.total} planilhas`;
        }
        return '';
    })();

    return (
        <div style={{ marginTop: '20px' }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px',
                fontSize: '13px',
                fontWeight: 500,
                color: '#1e293b'
            }}>
                <span>
                    {stageEmoji} {stageLabel}{detail ? ` · ${detail}` : ''}
                </span>
                <span style={{ color: '#0d47a1', fontWeight: 700 }}>{percent}%</span>
            </div>

            <div style={{
                width: '100%',
                height: '10px',
                background: '#e2e8f0',
                borderRadius: '999px',
                overflow: 'hidden',
                position: 'relative'
            }}>
                <div style={{
                    width: `${percent}%`,
                    height: '100%',
                    background: format === 'pdf'
                        ? 'linear-gradient(90deg, #b91c1c, #ef4444)'
                        : 'linear-gradient(90deg, #0d47a1, #3b82f6)',
                    borderRadius: '999px',
                    transition: 'width 220ms ease-out',
                    boxShadow: '0 0 10px rgba(13,71,161,0.25)'
                }} />
            </div>

            {progress?.currentFile && (
                <p style={{
                    margin: '8px 0 0 0',
                    fontSize: '11px',
                    color: '#94a3b8',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                }} title={progress.currentFile}>
                    {progress.currentFile}
                </p>
            )}
        </div>
    );
};

// --------------------------------------------------------------
// Folder Upload Modal Component
// --------------------------------------------------------------
const FolderUploadModal = ({ onClose, onUploadComplete }) => {
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState({ current: 0, total: 0, percent: 0, currentFile: '' });
    const [status, setStatus] = useState({ message: '', type: '' });
    const fileInputRef = useRef(null);
    const uploadLockRef = useRef(false);

    const handleFolderSelect = (event) => {
        const files = Array.from(event.target.files);
        if (files.length === 0) return;

        const firstPath = files[0].webkitRelativePath || files[0].name;
        const rootFolder = firstPath.split('/')[0] || 'pasta_selecionada';

        const mapped = files.map(file => ({
            file,
            relativePath: file.webkitRelativePath || file.name,
            rootFolder
        }));

        setSelectedFiles(mapped);
        setStatus({
            message: `📁 ${mapped.length} arquivo(s) selecionado(s) da pasta "${rootFolder}"`,
            type: 'success'
        });
    };

    const handleUpload = async () => {
        if (selectedFiles.length === 0) return;
        if (uploadLockRef.current) return; // blocks double-clicks / re-entry

        const user = auth.currentUser;
        if (!user) {
            setStatus({ message: 'Você precisa estar autenticado.', type: 'error' });
            return;
        }

        uploadLockRef.current = true;
        setUploading(true);
        setStatus({ message: '⏳ Iniciando upload...', type: 'info' });

        const rootFolder = selectedFiles[0].rootFolder;
        const timestamp = Date.now();
        const basePrefix = `FolderUploads/${user.uid}/${timestamp}_${sanitizeFileNamePart(rootFolder)}/`;

        const uploadedFiles = [];
        const failedFiles = [];
        let completed = 0;

        try {
            // ---- PRE-UPLOAD DUPLICATE CHECK (nothing is sent before this passes) ----
            setStatus({ message: '🔍 Verificando se este relatório já foi enviado...', type: 'info' });

            const localCandidates = selectedFiles
                .map(({ file, relativePath }) => ({
                    file,
                    relativePath,
                    fileName: relativePath.split('/').pop(),
                }))
                .filter(f => {
                    const name = (f.fileName || '').toLowerCase();
                    if (name.startsWith('~$') || name.endsWith('.ds_store') || name.endsWith('.tmp')) return false;
                    return /\.(xlsx?|docx?|pdf)$/i.test(name);
                });

            const preMeta = await extractFolderUploadMetadata(localCandidates, rootFolder);

            const describeDup = (d) => {
                const who = d.uploadedBy || d.createdBy || 'usuário desconhecido';
                const when = d.createdAt ? new Date(d.createdAt).toLocaleString('pt-BR') : 'data desconhecida';
                return `Projeto ${d.projectNo || '—'} / Container ${d.containerNo || '—'} (enviado por ${who} em ${when})`;
            };

            const dup = await reportApi.findDuplicateUploads(preMeta.projectNo, preMeta.containerNo);

            if (dup.exact.length > 0) {
                setStatus({
                    message: `⛔ Upload cancelado: este relatório já foi enviado. ${describeDup(dup.exact[0])}` +
                        `${dup.exact.length > 1 ? ` (+${dup.exact.length - 1} registro(s))` : ''}. ` +
                        'Se precisar reenviar, exclua o registro anterior primeiro.',
                    type: 'error'
                });
                return;
            }

            if (!dup.checked) {
                const goOn = window.confirm(
                    'Não foi possível identificar Project No nem Container No nos arquivos, ' +
                    'então não dá para verificar duplicidade. Enviar mesmo assim?'
                );
                if (!goOn) {
                    setStatus({ message: 'Upload cancelado.', type: 'info' });
                    return;
                }
            } else if (dup.partial.length > 0) {
                const goOn = window.confirm(
                    'Possível duplicidade (mesmo Project No/Container, mas falta o outro campo em um dos lados):\n\n' +
                    dup.partial.slice(0, 3).map(describeDup).join('\n') +
                    '\n\nEnviar mesmo assim?'
                );
                if (!goOn) {
                    setStatus({ message: 'Upload cancelado por possível duplicidade.', type: 'info' });
                    return;
                }
            }
            // ---- end of duplicate check ----

            const uploadOne = async (item) => {
                const { file, relativePath } = item;
                const storagePath = `${basePrefix}${relativePath}`;
                const storageRef = ref(storage, storagePath);

                try {
                    await uploadBytes(storageRef, file, {
                        contentType: file.type || 'application/octet-stream'
                    });
                    const downloadURL = await getDownloadURL(storageRef);

                    uploadedFiles.push({
                        type: 'folderUpload',
                        fileName: relativePath.split('/').pop(),
                        filePath: storagePath,
                        fileUrl: downloadURL,
                        fileSize: file.size,
                        relativePath
                    });
                } catch (err) {
                    console.error(`Falha no upload de ${relativePath}:`, err);
                    failedFiles.push({ relativePath, error: err.message });
                }

                completed++;
                setProgress({
                    current: completed,
                    total: selectedFiles.length,
                    percent: Math.round((completed / selectedFiles.length) * 100),
                    currentFile: relativePath
                });
            };

            await mapWithConcurrency(selectedFiles, 4, uploadOne);

            setStatus({ message: '🔍 Lendo arquivos para extrair informações...', type: 'info' });

            const metadataCandidates = uploadedFiles.filter(f => {
                const name = (f.fileName || '').toLowerCase();
                if (name.startsWith('~$')) return false;
                if (name.endsWith('.ds_store')) return false;
                if (name.endsWith('.tmp')) return false;
                return /\.(xlsx?|docx?|pdf)$/i.test(name);
            });

            // Metadata was already extracted locally in the pre-upload check.
            const extractedMeta = preMeta;

            const record = {
                documentType: 'folderUpload',
                rootFolder,
                storagePrefix: basePrefix,
                files: uploadedFiles,
                photos: [],
                filesCount: uploadedFiles.length,
                failedCount: failedFiles.length,
                uploadedBy: user.displayName || user.email || 'Usuário',
                uploadedByEmail: user.email || '',
                uploadedByUid: user.uid,
                createdAt: new Date().toISOString(),

                projectNo: extractedMeta.projectNo || '',
                clientName: extractedMeta.clientName || '',
                clientNameExport: extractedMeta.clientNameExport || '',
                countryOfImport: extractedMeta.countryOfImport || '',
                inspectorName: extractedMeta.inspectorName || '',
                dateOfInspection: extractedMeta.dateOfInspection || '',
                dateOfReport: extractedMeta.dateOfReport || '',
                inspectionValidTill: extractedMeta.inspectionValidTill || '',
                arriveInspectionLocation: extractedMeta.arriveInspectionLocation || '',
                containerNo: extractedMeta.containerNo || '',
                inspectionResult: extractedMeta.inspectionResult || '',
                placeOfInspection: extractedMeta.placeOfInspection || '',
                unitName: extractedMeta.unitName || '',

                reportFolder: `Report_${sanitizeFileNamePart(extractedMeta.projectNo || 'unknown')}_${sanitizeFileNamePart(extractedMeta.containerNo || 'unknown')}`,
                zipRoot: `${rootFolder}/`,

                metadata: {
                    totalSelected: selectedFiles.length,
                    totalUploaded: uploadedFiles.length,
                    totalFailed: failedFiles.length,
                    failedFiles: failedFiles.slice(0, 20),
                    extractedFromExcel: Object.values(extractedMeta).some(v => v),
                    metadataCandidatesCount: metadataCandidates.length,
                }
            };

            const docRef = await addDoc(collection(db, 'tuv_folder_uploads'), record);

            const metaFound = Object.values(extractedMeta).filter(v => v).length;
            setStatus({
                message: `✅ Upload concluído! ${uploadedFiles.length} arquivo(s) enviado(s).${failedFiles.length > 0 ? ` ${failedFiles.length} falharam.` : ''}${metaFound > 0 ? ` Metadados extraídos (${metaFound} campos).` : ' Nenhum metadado extraído — verifique se a pasta contém um relatório .xlsx, .docx ou .pdf preenchido.'}`,
                type: failedFiles.length > 0 ? 'info' : 'success'
            });

            if (onUploadComplete) {
                onUploadComplete({ id: docRef.id, ...record });
            }
        } catch (err) {
            console.error('Erro no upload da pasta:', err);
            setStatus({ message: `Erro: ${err.message}`, type: 'error' });
        } finally {
            uploadLockRef.current = false;
            setUploading(false);
        }
    };

    const resetSelection = () => {
        setSelectedFiles([]);
        setProgress({ current: 0, total: 0, percent: 0, currentFile: '' });
        setStatus({ message: '', type: '' });
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    return (
        <div className={styles.modalBackdrop} onClick={() => !uploading && onClose()}>
            <div
                className={styles.formContainer}
                style={{ maxWidth: '640px', padding: '24px 28px', borderRadius: '16px' }}
                onClick={(e) => e.stopPropagation()}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                    <div>
                        <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#1e293b' }}>
                            📁 Upload de Pasta
                        </h3>
                        <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '13px' }}>
                            Selecione uma pasta do seu computador (incluindo subpastas). Os arquivos serão enviados para o Firebase Storage preservando a estrutura de diretórios. Se houver um relatório de inspeção (.xlsx, .docx ou .pdf), os metadados serão extraídos automaticamente.
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        disabled={uploading}
                        style={{
                            background: '#f1f5f9',
                            border: 'none',
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            fontSize: '20px',
                            cursor: uploading ? 'not-allowed' : 'pointer',
                            color: '#64748b',
                            fontWeight: 300
                        }}
                    >
                        ×
                    </button>
                </div>

                {status.message && (
                    <div style={{
                        padding: '10px 14px',
                        borderRadius: '8px',
                        marginBottom: '16px',
                        background: status.type === 'error' ? '#fef3f2' :
                            status.type === 'success' ? '#ecfdf5' : '#eff6ff',
                        border: `1px solid ${status.type === 'error' ? '#fecaca' :
                            status.type === 'success' ? '#a7f3d0' : '#bfdbfe'}`,
                        color: status.type === 'error' ? '#dc2626' :
                            status.type === 'success' ? '#065f46' : '#1e40af',
                        fontSize: '13px'
                    }}>
                        {status.message}
                    </div>
                )}

                <input
                    ref={fileInputRef}
                    type="file"
                    webkitdirectory=""
                    directory=""
                    multiple
                    onChange={handleFolderSelect}
                    disabled={uploading}
                    style={{ display: 'none' }}
                />

                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '32px 20px',
                    background: '#f8fafc',
                    borderRadius: '12px',
                    border: '2px dashed #cbd5e1',
                    marginBottom: '16px'
                }}>
                    <div style={{ fontSize: '40px' }}>📂</div>
                    <p style={{ margin: 0, color: '#475569', fontSize: '14px', textAlign: 'center' }}>
                        {selectedFiles.length === 0
                            ? 'Nenhuma pasta selecionada'
                            : `${selectedFiles.length} arquivo(s) pronto(s) para upload`}
                    </p>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploading}
                            style={{
                                padding: '10px 20px',
                                background: '#0d47a1',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                fontSize: '14px',
                                fontWeight: 600,
                                cursor: uploading ? 'not-allowed' : 'pointer'
                            }}
                        >
                            {selectedFiles.length === 0 ? 'Selecionar Pasta' : 'Trocar Pasta'}
                        </button>
                        {selectedFiles.length > 0 && !uploading && (
                            <button
                                onClick={resetSelection}
                                style={{
                                    padding: '10px 20px',
                                    background: 'transparent',
                                    color: '#64748b',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    cursor: 'pointer'
                                }}
                            >
                                Limpar
                            </button>
                        )}
                    </div>
                </div>

                {uploading && (
                    <div style={{ marginBottom: '16px' }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            fontSize: '13px',
                            fontWeight: 500,
                            color: '#1e293b',
                            marginBottom: '6px'
                        }}>
                            <span>Enviando {progress.current}/{progress.total}</span>
                            <span style={{ color: '#0d47a1', fontWeight: 700 }}>{progress.percent}%</span>
                        </div>
                        <div style={{
                            width: '100%',
                            height: '10px',
                            background: '#e2e8f0',
                            borderRadius: '999px',
                            overflow: 'hidden'
                        }}>
                            <div style={{
                                width: `${progress.percent}%`,
                                height: '100%',
                                background: 'linear-gradient(90deg, #0d47a1, #3b82f6)',
                                transition: 'width 220ms ease-out'
                            }} />
                        </div>
                        {progress.currentFile && (
                            <p style={{
                                margin: '6px 0 0 0',
                                fontSize: '11px',
                                color: '#94a3b8',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            }} title={progress.currentFile}>
                                {progress.currentFile}
                            </p>
                        )}
                    </div>
                )}

                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button
                        onClick={onClose}
                        disabled={uploading}
                        style={{
                            padding: '10px 20px',
                            background: 'transparent',
                            color: '#64748b',
                            border: '1px solid #e2e8f0',
                            borderRadius: '10px',
                            fontSize: '14px',
                            cursor: uploading ? 'not-allowed' : 'pointer'
                        }}
                    >
                        {uploading ? 'Aguarde...' : 'Fechar'}
                    </button>
                    <button
                        onClick={handleUpload}
                        disabled={uploading || selectedFiles.length === 0}
                        style={{
                            padding: '10px 24px',
                            background: uploading || selectedFiles.length === 0
                                ? '#94a3b8'
                                : 'linear-gradient(135deg, #0d47a1, #1a237e)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '10px',
                            fontSize: '14px',
                            fontWeight: 600,
                            cursor: uploading || selectedFiles.length === 0 ? 'not-allowed' : 'pointer',
                            boxShadow: uploading || selectedFiles.length === 0
                                ? 'none'
                                : '0 4px 12px rgba(13,71,161,0.3)'
                        }}
                    >
                        {uploading ? '⏳ Enviando...' : '🚀 Fazer Upload'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// --------------------------------------------------------------
// TUV report permissions (reads module_permissions/<role>_luxury)
//   tuv_relatorios_import -> folder upload + attach documents
//   tuv_relatorios_delete -> delete report
// Download and download-history stay open to everyone, as before.
// Fails closed: while loading, or if anything goes wrong, nothing is allowed.
// --------------------------------------------------------------
const TUV_PERMISSION_SYSTEM = 'luxury'; // same value as SYSTEM_TYPES.LUXURY in PermissionsPage

// ASSUMPTION: the user's role lives on users/<uid> (or a doc with the same email).
// If your app already exposes the role elsewhere (context/hook), change only this function.
const resolveCurrentUserRole = async (user) => {
    const pickRole = (data) => data?.role || data?.cargo || data?.funcao || data?.userRole || '';

    const byUid = await getDoc(doc(db, 'users', user.uid));
    if (byUid.exists()) {
        const role = pickRole(byUid.data());
        if (role) return role;
    }
    if (user.email) {
        const snap = await getDocs(query(collection(db, 'users'), where('email', '==', user.email)));
        for (const d of snap.docs) {
            const role = pickRole(d.data());
            if (role) return role;
        }
    }
    return '';
};

const useTuvReportPermissions = (currentUser) => {
    const [perms, setPerms] = useState({ canImport: false, canDelete: false, loaded: false });

    useEffect(() => {
        let cancelled = false;
        if (!currentUser) {
            setPerms({ canImport: false, canDelete: false, loaded: false });
            return undefined;
        }

        (async () => {
            try {
                const role = await resolveCurrentUserRole(currentUser);
                if (!role) throw new Error('Cargo do usuário não encontrado');

                const snap = await getDoc(doc(db, 'module_permissions', `${role}_${TUV_PERMISSION_SYSTEM}`));
                const data = snap.exists() ? snap.data() : {};
                const moduleOn = data.tuv_relatorios === true;

                if (!cancelled) {
                    setPerms({
                        canImport: moduleOn && data.tuv_relatorios_import === true,
                        canDelete: moduleOn && data.tuv_relatorios_delete === true,
                        loaded: true,
                    });
                }
            } catch (err) {
                console.error('Erro ao carregar permissões do relatório TUV:', err);
                if (!cancelled) setPerms({ canImport: false, canDelete: false, loaded: true });
            }
        })();

        return () => { cancelled = true; };
    }, [currentUser]);

    return perms;
};

// --------------------------------------------------------------
// Attach documents to ONE existing report (goes into its master folder)
// --------------------------------------------------------------
const AttachDocumentsModal = ({ report, onClose, onComplete }) => {
    const [selected, setSelected] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState({ done: 0, total: 0 });
    const [status, setStatus] = useState({ message: '', type: '' });
    const lockRef = useRef(false);
    const inputRef = useRef(null);

    const collectionName = report._source === 'folderUpload' ? 'tuv_folder_uploads' : 'tuv_reports';

    const formatBytes = (bytes) => {
        if (!bytes && bytes !== 0) return '-';
        const units = ['B', 'KB', 'MB', 'GB'];
        let i = 0;
        let n = Number(bytes);
        while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
        return `${n.toFixed(n >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
    };

    const handleSelect = (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;
        setSelected(prev => {
            const known = new Set(prev.map(f => `${f.name}|${f.size}`));
            return [...prev, ...files.filter(f => !known.has(`${f.name}|${f.size}`))];
        });
        setStatus({ message: '', type: '' });
        if (inputRef.current) inputRef.current.value = '';
    };

    const removeSelected = (idx) => setSelected(prev => prev.filter((_, i) => i !== idx));

    const handleUpload = async () => {
        if (selected.length === 0 || lockRef.current) return;

        const user = auth.currentUser;
        if (!user) {
            setStatus({ message: 'Você precisa estar autenticado.', type: 'error' });
            return;
        }

        lockRef.current = true;
        setUploading(true);
        setProgress({ done: 0, total: selected.length });
        setStatus({ message: '⏳ Enviando documentos...', type: 'info' });

        try {
            // Always work on the freshest copy of the record
            const docRef = doc(db, collectionName, report.id);
            const snap = await getDoc(docRef);
            if (!snap.exists()) throw new Error('Relatório não encontrado (pode ter sido excluído).');
            const current = snap.data();

            let storagePrefix = current.storagePrefix;
            if (!storagePrefix) {
                const d = new Date(current.createdAt || Date.now());
                const invoice = String(current.projectNo || 'unknown').replace(/[^a-z0-9]/gi, '_');
                const container = String(current.containerNo || 'unknown').replace(/[^a-z0-9]/gi, '_');
                const reportFolder = current.reportFolder || `Report_${invoice}_${container}`;
                storagePrefix = `Reports/${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${reportFolder}/`;
            }

            const stamp = Date.now();
            const uploaded = [];
            const failed = [];
            let done = 0;

            await mapWithConcurrency(selected, 3, async (file, i) => {
                const safeName = file.name.replace(/[\\/]/g, '_');
                const relativePath = `Anexos/${stamp}_${i}_${safeName}`;
                const storagePath = `${storagePrefix}${relativePath}`;
                try {
                    const storageRef = ref(storage, storagePath);
                    await uploadBytes(storageRef, file, {
                        contentType: file.type || 'application/octet-stream'
                    });
                    const fileUrl = await getDownloadURL(storageRef);
                    uploaded.push({
                        type: 'attachment',
                        fileName: safeName,
                        filePath: storagePath,
                        fileUrl,
                        fileSize: file.size,
                        relativePath,
                        attachedBy: user.displayName || user.email || 'Usuário',
                        attachedByEmail: user.email || '',
                        attachedAt: new Date().toISOString(),
                    });
                } catch (err) {
                    console.error(`Falha ao anexar ${file.name}:`, err);
                    failed.push(file.name);
                }
                done++;
                setProgress({ done, total: selected.length });
            });

            if (uploaded.length > 0) {
                // Re-read right before writing to minimise overwriting concurrent changes
                const latest = (await getDoc(docRef)).data() || current;
                const files = [...(latest.files || []), ...uploaded];
                await updateDoc(docRef, { files, filesCount: files.length });
            }

            if (failed.length > 0) {
                setStatus({
                    message: `${uploaded.length} anexado(s); ${failed.length} falharam: ${failed.join(', ')}`,
                    type: 'error'
                });
                if (uploaded.length > 0 && onComplete) onComplete(uploaded.length);
            } else {
                setStatus({ message: `✅ ${uploaded.length} documento(s) anexado(s).`, type: 'success' });
                if (onComplete) onComplete(uploaded.length);
                setTimeout(onClose, 900);
            }
        } catch (err) {
            console.error('Erro ao anexar documentos:', err);
            setStatus({ message: `Erro: ${err.message}`, type: 'error' });
        } finally {
            lockRef.current = false;
            setUploading(false);
        }
    };

    const statusColors = {
        success: { bg: '#dcfce7', fg: '#166534' },
        error: { bg: '#fee2e2', fg: '#991b1b' },
        info: { bg: '#eff6ff', fg: '#1e40af' },
    };
    const sc = statusColors[status.type] || statusColors.info;

    return (
        <div className={styles.modalBackdrop} onClick={uploading ? undefined : onClose}>
            <div
                className={styles.formContainer}
                style={{ maxWidth: '640px', padding: '24px 28px', borderRadius: '16px' }}
                onClick={(e) => e.stopPropagation()}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                    <div>
                        <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#1e293b' }}>📎 Anexar documentos</h3>
                        <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: '13px' }}>
                            {report.projectNo || '-'} · Container {report.containerNo || '-'}
                        </p>
                        <p style={{ margin: '2px 0 0 0', color: '#94a3b8', fontSize: '12px' }}>
                            Os arquivos são salvos na pasta "Anexos" deste relatório.
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        disabled={uploading}
                        style={{
                            background: '#f1f5f9', border: 'none', width: '32px', height: '32px',
                            borderRadius: '50%', fontSize: '20px', cursor: uploading ? 'not-allowed' : 'pointer',
                            color: '#64748b', fontWeight: 300
                        }}
                    >
                        ×
                    </button>
                </div>

                <input
                    ref={inputRef}
                    type="file"
                    multiple
                    onChange={handleSelect}
                    disabled={uploading}
                    style={{ display: 'none' }}
                />
                <button
                    type="button"
                    onClick={() => inputRef.current && inputRef.current.click()}
                    disabled={uploading}
                    style={{
                        width: '100%', padding: '18px', border: '2px dashed #cbd5e1', borderRadius: '12px',
                        background: '#f8fafc', color: '#475569', fontSize: '14px', fontWeight: 600,
                        cursor: uploading ? 'not-allowed' : 'pointer'
                    }}
                >
                    + Selecionar arquivos
                </button>

                {selected.length > 0 && (
                    <div style={{ maxHeight: '240px', overflowY: 'auto', marginTop: '14px' }}>
                        {selected.map((f, idx) => (
                            <div
                                key={`${f.name}-${f.size}-${idx}`}
                                style={{
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                    padding: '8px 12px', borderBottom: '1px solid #f1f5f9', fontSize: '13px', gap: '12px'
                                }}
                            >
                                <span style={{ color: '#1e293b', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {f.name}
                                </span>
                                <span style={{ color: '#64748b', whiteSpace: 'nowrap' }}>
                                    {formatBytes(f.size)}
                                    {!uploading && (
                                        <button
                                            type="button"
                                            onClick={() => removeSelected(idx)}
                                            style={{ marginLeft: '10px', background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '16px' }}
                                            title="Remover"
                                        >
                                            ×
                                        </button>
                                    )}
                                </span>
                            </div>
                        ))}
                    </div>
                )}

                {uploading && (
                    <div style={{ marginTop: '14px' }}>
                        <div style={{ height: '8px', background: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                            <div style={{
                                height: '100%', background: '#0d47a1',
                                width: `${progress.total ? Math.round((progress.done / progress.total) * 100) : 0}%`,
                                transition: 'width 0.2s'
                            }} />
                        </div>
                        <p style={{ margin: '6px 0 0 0', fontSize: '12px', color: '#64748b' }}>
                            {progress.done} / {progress.total}
                        </p>
                    </div>
                )}

                {status.message && (
                    <div style={{
                        marginTop: '14px', padding: '10px 14px', borderRadius: '8px',
                        background: sc.bg, color: sc.fg, fontSize: '13px'
                    }}>
                        {status.message}
                    </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
                    <button
                        onClick={onClose}
                        disabled={uploading}
                        style={{
                            padding: '9px 18px', border: '1px solid #e2e8f0', borderRadius: '8px',
                            background: 'white', color: '#475569', cursor: uploading ? 'not-allowed' : 'pointer', fontSize: '14px'
                        }}
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={handleUpload}
                        disabled={uploading || selected.length === 0}
                        style={{
                            padding: '9px 18px', border: 'none', borderRadius: '8px',
                            background: uploading || selected.length === 0 ? '#94a3b8' : '#0d47a1',
                            color: 'white', cursor: uploading || selected.length === 0 ? 'not-allowed' : 'pointer',
                            fontSize: '14px', fontWeight: 600
                        }}
                    >
                        {uploading ? 'Enviando...' : `Anexar${selected.length ? ` (${selected.length})` : ''}`}
                    </button>
                </div>
            </div>
        </div>
    );
};

// --------------------------------------------------------------
// Header Actions Dropdown
// Consolidates secondary header buttons into a single menu to
// reduce visual clutter. "Novo Relatório" stays as a primary
// button because it's the most frequent action.
// --------------------------------------------------------------
const DropdownItem = ({ icon, label, description, onClick }) => (
    <button
        type="button"
        role="menuitem"
        onClick={onClick}
        className={styles.dropdownItem}
    >
        <span className={styles.dropdownItemIcon}>{icon}</span>
        <span className={styles.dropdownItemText}>
            <span className={styles.dropdownItemLabel}>{label}</span>
            {description && (
                <span className={styles.dropdownItemDescription}>
                    {description}
                </span>
            )}
        </span>
    </button>
);

const HeaderActionsDropdown = ({
    onNewReport,
    onFolderUpload,
    onMonthlyReport,
    onRangeReport,
    canImport = false,
    disabled = false,
}) => {
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);

    // Close on outside click
    useEffect(() => {
        if (!open) return;
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    // Close on Escape
    useEffect(() => {
        if (!open) return;
        const handleKey = (event) => {
            if (event.key === 'Escape') setOpen(false);
        };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [open]);

    const runAndClose = (fn) => () => {
        setOpen(false);
        fn?.();
    };

    return (
        <div ref={containerRef} className={styles.headerActionsGroup}>
            <button
                onClick={onNewReport}
                className={styles.addButton}
                disabled={disabled}
            >
                + Novo Relatório
            </button>

            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                disabled={disabled}
                aria-haspopup="menu"
                aria-expanded={open}
                title="Mais ações"
                className={`${styles.dropdownTrigger} ${open ? styles.open : ''}`}
            >
                <span>Mais Ações</span>
                <span className={`${styles.dropdownCaret} ${open ? styles.open : ''}`}>
                    ▼
                </span>
            </button>

            {open && (
                <div role="menu" className={styles.dropdownMenu}>
                    {canImport && (
                        <DropdownItem
                            icon="📁"
                            label="Upload de Pasta"
                            description="Enviar pasta com arquivos"
                            onClick={runAndClose(onFolderUpload)}
                        />
                    )}
                    <DropdownItem
                        icon="📅"
                        label="Relatório Mensal"
                        description="Resumo de um mês específico"
                        onClick={runAndClose(onMonthlyReport)}
                    />
                    <DropdownItem
                        icon="📆"
                        label="Relatório por Período"
                        description="Resumo entre duas datas"
                        onClick={runAndClose(onRangeReport)}
                    />
                </div>
            )}
        </div>
    );
};

// --------------------------------------------------------------
// Main Component
// --------------------------------------------------------------
const TUVReportPage = () => {
    const [extractedData, setExtractedData] = useState({});
    const [isDataLoaded, setIsDataLoaded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [status, setStatus] = useState({ message: '', type: '' });
    const [evaluationId, setEvaluationId] = useState('');
    const [reports, setReports] = useState([]);
    const [reportsLoading, setReportsLoading] = useState(true);
    const [currentUser, setCurrentUser] = useState(null);
    const [searchMode, setSearchMode] = useState('list');

    const generatingRef = useRef(false);

    const [showDownloadModal, setShowDownloadModal] = useState(false);
    const [reportToDownload, setReportToDownload] = useState(null);
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadProgress, setDownloadProgress] = useState(null);
    const [downloadFormat, setDownloadFormat] = useState('excel');

    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [reportToViewHistory, setReportToViewHistory] = useState(null);

    const [showFolderDetailModal, setShowFolderDetailModal] = useState(false);
    const [reportToViewFiles, setReportToViewFiles] = useState(null);

    const [showMonthlyModal, setShowMonthlyModal] = useState(false);
    const [monthlyYear, setMonthlyYear] = useState(new Date().getFullYear());
    const [monthlyMonth, setMonthlyMonth] = useState(new Date().getMonth() + 1);
    const [isGeneratingMonthly, setIsGeneratingMonthly] = useState(false);

    const [showRangeModal, setShowRangeModal] = useState(false);
    const [rangeStartDate, setRangeStartDate] = useState('');
    const [rangeEndDate, setRangeEndDate] = useState('');
    const [isGeneratingRange, setIsGeneratingRange] = useState(false);

    const [showFolderUploadModal, setShowFolderUploadModal] = useState(false);

    const [showAttachModal, setShowAttachModal] = useState(false);
    const [reportToAttach, setReportToAttach] = useState(null);

    const { canImport, canDelete } = useTuvReportPermissions(currentUser);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(setCurrentUser);
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (currentUser) loadReports();
    }, [currentUser]);

    const loadReports = async () => {
        setReportsLoading(true);
        try {
            const loadedReports = await reportApi.getReports();
            setReports(loadedReports);
        } catch (error) {
            console.error('Error loading reports:', error);
            setStatus({ message: 'Erro ao carregar relatórios', type: 'error' });
            setTimeout(() => setStatus({ message: '', type: '' }), 3000);
        } finally {
            setReportsLoading(false);
        }
    };

    const fetchAndExtractData = useCallback(async (id, forceRefresh = false) => {
        setLoading(true);

        try {
            const apiData = await fetchEvaluationData(id, forceRefresh);
            const parsedData = parseNewApiFormat(apiData);

            const groups = groupSamplingRowsByDate(parsedData.samplingRows || []);
            parsedData.samplingGroupsCount = groups.length;

            setExtractedData(parsedData);
            setIsDataLoaded(true);
            setStatus({ message: `✅ Dados carregados! ${parsedData.perishableProducts?.length || 0} produto(s) encontrado(s). ${groups.length} grupo(s) de amostragem.`, type: 'success' });
            setTimeout(() => setStatus({ message: '', type: '' }), 3000);
            return parsedData;
        } catch (error) {
            console.error('Error fetching evaluation:', error);
            setStatus({ message: error.message || 'Erro ao buscar dados da avaliação', type: 'error' });
            setTimeout(() => setStatus({ message: '', type: '' }), 5000);
            throw error;
        } finally {
            setLoading(false);
        }
    }, []);

    const handleSelectPending = async (pendingDocId) => {
        try {
            setLoading(true);
            setStatus({ message: '⏳ Buscando dados completos via API da Checklist Fácil...', type: 'info' });

            const firestoreData = await webhookService.getEvaluationData(pendingDocId);
            const realEvaluationId = firestoreData?.evaluationId || pendingDocId;

            if (!realEvaluationId) {
                throw new Error('ID da avaliação não encontrado nos dados do Firestore.');
            }

            await fetchAndExtractData(realEvaluationId, false);
            await webhookService.markAsProcessed(pendingDocId);

        } catch (error) {
            console.error('Erro ao carregar avaliação automática:', error);
            setStatus({ message: error.message || 'Erro ao carregar dados da avaliação', type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const generateReport = useCallback(async () => {
        if (generatingRef.current) {
            console.warn('generateReport already running — ignoring duplicate invocation.');
            return;
        }

        if (!currentUser) {
            setStatus({ message: 'Você precisa estar logado para gerar relatórios', type: 'error' });
            return;
        }

        if (!extractedData.projectNo && !extractedData.clientName) {
            setStatus({ message: 'Nenhum dado carregado. Por favor, busque uma avaliação primeiro.', type: 'error' });
            return;
        }

        generatingRef.current = true;
        setLoading(true);

        try {
            setStatus({ message: '🔎 Verificando duplicatas...', type: 'info' });

            let existingReports = [];
            try {
                existingReports = await reportApi.findExistingReports(
                    extractedData.projectNo,
                    extractedData.containerNo
                );
            } catch (checkErr) {
                console.warn('Falha ao verificar duplicata (prosseguindo):', checkErr);
            }

            if (existingReports.length > 0) {
                const first = existingReports[0];
                const createdAt = first.createdAt
                    ? new Date(first.createdAt).toLocaleString('pt-BR')
                    : 'data desconhecida';

                const dupeWarning = existingReports.length > 1
                    ? `\n\n⚠️ Foram encontrados ${existingReports.length} relatórios duplicados. Todos serão removidos.`
                    : '';

                const proceed = window.confirm(
                    `⚠️ Já existe um relatório para este projeto/container.\n\n` +
                    `Projeto: ${extractedData.projectNo || '-'}\n` +
                    `Container: ${extractedData.containerNo || '-'}\n` +
                    `Criado por: ${first.createdBy || '-'}\n` +
                    `Criado em: ${createdAt}${dupeWarning}\n\n` +
                    `Deseja substituir o relatório existente?\n` +
                    `(O(s) relatório(s) antigo(s) e seus arquivos serão removidos.)`
                );

                if (!proceed) {
                    setStatus({
                        message: 'Geração cancelada — relatório duplicado evitado.',
                        type: 'info'
                    });
                    setTimeout(() => setStatus({ message: '', type: '' }), 4000);
                    return;
                }

                setStatus({
                    message: `🗑️ Removendo ${existingReports.length} relatório(s) anterior(es)...`,
                    type: 'info'
                });

                for (const existing of existingReports) {
                    try {
                        await reportApi.deleteReport(existing.id, 'report');
                    } catch (delErr) {
                        console.error(`Falha ao deletar relatório ${existing.id}:`, delErr);
                    }
                }
            }

            setStatus({ message: '📊 Gerando relatórios...', type: 'info' });

            const user = auth.currentUser;
            if (!user) throw new Error('Usuário não autenticado');

            const timestamp = new Date();
            const year = timestamp.getFullYear().toString();
            const month = (timestamp.getMonth() + 1).toString().padStart(2, '0');
            const invoice = String(extractedData.projectNo || 'unknown').replace(/[^a-z0-9]/gi, '_');
            const container = String(extractedData.containerNo || 'unknown').replace(/[^a-z0-9]/gi, '_');

            const reportFolder = `Report_${invoice}_${container}`;
            const storagePrefix = `Reports/${year}/${month}/${reportFolder}/`;
            const zipRoot = `${reportFolder}/`;

            const attachments = [];

            setStatus({ message: '📊 Gerando Inspection Report...', type: 'info' });
            const inspectionBuffer = await withTimeout(generateInspectionXlsx(extractedData), 120000, 'Inspection Report');
            const inspectionBlob = new Blob([inspectionBuffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            attachments.push({
                type: 'inspection',
                blob: inspectionBlob
            });

            setStatus({ message: '📊 Gerando Sampling Data Sheet...', type: 'info' });
            const samplingWorkbooks = await withTimeout(generateSamplingDataSheetWorkbooks(extractedData), 120000, 'Sampling Data Sheet');
            const samplingGroupsCount = samplingWorkbooks.length;
            extractedData.samplingGroupsCount = samplingGroupsCount;

            for (const { date, wb } of samplingWorkbooks) {
                const samplingBuffer = await wb.xlsx.writeBuffer();
                attachments.push({
                    type: 'sampling',
                    dateLabel: dateLabelForFileName(date),
                    blob: new Blob([samplingBuffer], {
                        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                    })
                });
            }

            setStatus({ message: '📊 Gerando Request for Certification...', type: 'info' });
            const rfcBuffer = await withTimeout(generateRequestForCertificationXlsx(extractedData), 120000, 'Request for Certification');
            attachments.push({
                type: 'rfc',
                blob: new Blob([rfcBuffer], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                })
            });

            const uploadedFiles = [];

            for (const attachment of attachments) {
                const subfolder = attachment.type === 'sampling'
                    ? 'Sampling_Data_Sheet'
                    : attachment.type === 'rfc'
                        ? 'Request_For_Certification'
                        : 'Inspection_Report';

                const dateSuffix = attachment.dateLabel ? `_${attachment.dateLabel}` : '';
                const fileName = `TUV_${subfolder}_${invoice}${dateSuffix}_${timestamp.getTime()}.xlsx`;
                const storagePath = `${storagePrefix}${subfolder}/${fileName}`;

                setStatus({ message: `☁️ Enviando ${fileName}...`, type: 'info' });
                const storageRef = ref(storage, storagePath);
                await uploadBytes(storageRef, attachment.blob, {
                    contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });
                const downloadURL = await getDownloadURL(storageRef);

                uploadedFiles.push({
                    type: attachment.type,
                    fileName,
                    filePath: storagePath,
                    fileUrl: downloadURL,
                    fileSize: attachment.blob.size
                });
            }

            const photoAttachments = extractedData.photoAttachments || [];
            const uploadedPhotos = [];
            let photosFailed = 0;

            if (photoAttachments.length > 0) {
                setStatus({
                    message: `📸 Baixando 0/${photoAttachments.length} fotos anexadas...`,
                    type: 'info'
                });

                const { results, failed } = await fetchPhotoAttachments(
                    photoAttachments,
                    (done, total) => setStatus({
                        message: `📸 Baixando ${done}/${total} fotos anexadas...`,
                        type: 'info'
                    })
                );

                for (const { path: relativePath, blob } of results) {
                    const storagePath = `${storagePrefix}${relativePath}`;

                    const photoRef = ref(storage, storagePath);
                    await uploadBytes(photoRef, blob, {
                        contentType: blob.type || 'image/jpeg'
                    });
                    const photoUrl = await getDownloadURL(photoRef);

                    uploadedPhotos.push({
                        type: 'photo',
                        fileName: relativePath.split('/').pop(),
                        filePath: storagePath,
                        fileUrl: photoUrl,
                        fileSize: blob.size
                    });
                }

                photosFailed = failed.length;

                if (failed.length > 0) {
                    console.warn(`${failed.length} foto(s) não puderam ser baixadas:`, failed);
                }
            }

            const reportRecord = {
                documentType: 'combined',
                projectNo: String(extractedData.projectNo || ''),
                unitName: String(extractedData.unitName || ''),
                clientName: String(extractedData.clientName || ''),
                clientNameExport: String(extractedData.clientNameExport || ''),
                countryOfImport: String(extractedData.countryOfImport || ''),
                inspectorName: String(extractedData.inspectorName || ''),
                dateOfInspection: extractedData.dateOfInspection || null,
                dateOfReport: extractedData.dateOfReport || null,
                inspectionResult: String(extractedData.inspectionResult || ''),
                containerNo: String(extractedData.containerNo || ''),
                placeOfInspection: String(extractedData.placeOfInspection || ''),

                reportFolder,
                storagePrefix,
                zipRoot,

                files: uploadedFiles,
                photos: uploadedPhotos,

                createdBy: user.displayName || user.email || 'Usuário',
                createdByEmail: user.email || '',
                createdByUid: user.uid,
                createdAt: timestamp.toISOString(),

                metadata: {
                    clientAddress: String(extractedData.clientAddress || ''),
                    applicableAirCaiNumber: String(extractedData.applicableAirCaiNumber || ''),
                    totalTime: String(extractedData.totalTime || ''),
                    conditionOfGoods: String(extractedData.conditionOfGoods || ''),
                    shipmentType: String(extractedData.shipmentType || ''),
                    boardingType: String(extractedData.boardingType || ''),
                    containerType: String(extractedData.containerType || ''),
                    productsCount: (extractedData.perishableProducts || []).length,
                    samplingGroups: samplingGroupsCount,
                    filesCount: uploadedFiles.length,
                    photosCount: uploadedPhotos.length,
                    photosFailed
                }
            };

            const reportDocId = buildReportDocId(
                extractedData.projectNo,
                extractedData.containerNo
            );
            await setDoc(doc(db, 'tuv_reports', reportDocId), reportRecord, { merge: false });

            await loadReports();

            const photosMessage = uploadedPhotos.length > 0
                ? ` ${uploadedPhotos.length} foto(s) armazenada(s) em ${storagePrefix}Fotos/.${photosFailed > 0 ? ` (${photosFailed} falharam)` : ''}`
                : '';

            setStatus({
                message: `✅ Relatório gerado com sucesso! ${samplingGroupsCount} grupo(s) de amostragem.${photosMessage}`,
                type: 'success'
            });

            setTimeout(() => {
                setStatus({ message: '', type: '' });
                setIsModalOpen(false);
                setEvaluationId('');
                setExtractedData({});
                setIsDataLoaded(false);
                setSearchMode('list');
            }, 2000);

        } catch (error) {
            console.error('Generate report error:', error);
            setStatus({ message: `Erro: ${error.message}`, type: 'error' });
        } finally {
            generatingRef.current = false;
            setLoading(false);
        }
    }, [extractedData, currentUser]);

    const handleFetchEvaluation = async () => {
        if (!evaluationId || evaluationId.trim() === '') {
            setStatus({ message: 'Por favor, informe o ID da avaliação', type: 'error' });
            return;
        }
        await fetchAndExtractData(evaluationId.trim(), false);
    };

    const openModal = () => {
        setExtractedData({});
        setEvaluationId('');
        setIsDataLoaded(false);
        setSearchMode('list');
        setStatus({ message: '', type: '' });
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setExtractedData({});
        setEvaluationId('');
        setIsDataLoaded(false);
        setSearchMode('list');
        setStatus({ message: '', type: '' });
    };

    const handleDownloadClick = (report) => {
        setReportToDownload(report);
        setDownloadProgress(null);
        setDownloadFormat('excel');
        setShowDownloadModal(true);
    };

    const handleConfirmDownload = async (format) => {
        if (!reportToDownload) return;
        setIsDownloading(true);
        setDownloadFormat(format);
        setDownloadProgress({ stage: 'starting', completed: 0, total: 0, percent: 0 });
        setStatus({ message: '📥 Preparando arquivos...', type: 'info' });

        try {
            const result = await reportApi.downloadReport(
                reportToDownload,
                format,
                (progress) => setDownloadProgress(progress)
            );

            await reportApi.logDownload(
                reportToDownload,
                format,
                result?.fileName || '',
                result?.filesCount || 0,
                result?.photosCount || 0
            );

            setStatus({ message: '📥 Download concluído!', type: 'success' });
        } catch (error) {
            console.error('Download error:', error);
            setStatus({ message: `Erro ao baixar: ${error.message}`, type: 'error' });
        } finally {
            setIsDownloading(false);
            setTimeout(() => {
                setShowDownloadModal(false);
                setReportToDownload(null);
                setDownloadProgress(null);
                setTimeout(() => setStatus({ message: '', type: '' }), 500);
            }, 600);
        }
    };

    const handleDeleteReport = async (reportId, source = 'report') => {
        if (!canDelete) {
            setStatus({ message: 'Você não tem permissão para excluir relatórios.', type: 'error' });
            setTimeout(() => setStatus({ message: '', type: '' }), 3000);
            return;
        }
        if (window.confirm('Tem certeza que deseja excluir este relatório? Todos os arquivos em Storage serão removidos.')) {
            try {
                await reportApi.deleteReport(reportId, source);
                await loadReports();
                setStatus({ message: '🗑️ Relatório excluído com sucesso!', type: 'success' });
            } catch (error) {
                console.error('Delete error:', error);
                setStatus({ message: `Erro ao excluir: ${error.message}`, type: 'error' });
            }
            setTimeout(() => setStatus({ message: '', type: '' }), 2000);
        }
    };

    const handleAttachClick = (report) => {
        if (!canImport) return;
        setReportToAttach(report);
        setShowAttachModal(true);
    };

    const handleShowHistory = (report) => {
        setReportToViewHistory(report);
        setShowHistoryModal(true);
    };

    const handleShowFolderFiles = (report) => {
        setReportToViewFiles(report);
        setShowFolderDetailModal(true);
    };

    const handleGenerateMonthlyReport = async () => {
        setIsGeneratingMonthly(true);
        setStatus({ message: '⏳ Buscando relatórios do mês...', type: 'info' });
        try {
            const data = await reportApi.getMonthlyReportData(monthlyYear, monthlyMonth);

            if (data.length === 0) {
                setStatus({
                    message: `Nenhum relatório encontrado para ${String(monthlyMonth).padStart(2, '0')}/${monthlyYear}.`,
                    type: 'error'
                });
                setIsGeneratingMonthly(false);
                setTimeout(() => setStatus({ message: '', type: '' }), 4000);
                return;
            }

            setStatus({ message: '📊 Gerando planilha...', type: 'info' });

            const buffer = await generateMonthlyReportXlsx(data, monthlyYear, monthlyMonth);
            const blob = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            const m = String(monthlyMonth).padStart(2, '0');
            saveAs(blob, `TUV_Monthly_Report_${monthlyYear}_${m}.xlsx`);

            setStatus({
                message: `✅ Relatório mensal gerado com ${data.length} relatório(s).`,
                type: 'success'
            });
            setShowMonthlyModal(false);
        } catch (err) {
            console.error('Monthly report error:', err);
            setStatus({ message: `Erro: ${err.message}`, type: 'error' });
        } finally {
            setIsGeneratingMonthly(false);
            setTimeout(() => setStatus({ message: '', type: '' }), 4000);
        }
    };

    const handleGenerateRangeReport = async () => {
        if (!rangeStartDate || !rangeEndDate) {
            setStatus({ message: 'Selecione a data inicial e a data final.', type: 'error' });
            setTimeout(() => setStatus({ message: '', type: '' }), 3000);
            return;
        }
        if (new Date(rangeStartDate) > new Date(rangeEndDate)) {
            setStatus({ message: 'A data inicial não pode ser maior que a data final.', type: 'error' });
            setTimeout(() => setStatus({ message: '', type: '' }), 3000);
            return;
        }

        setIsGeneratingRange(true);
        setStatus({ message: '⏳ Buscando relatórios do período...', type: 'info' });
        try {
            const data = await reportApi.getDateRangeReportData(rangeStartDate, rangeEndDate);

            if (data.length === 0) {
                const fmt = (s) => new Date(s).toLocaleDateString('pt-BR');
                setStatus({
                    message: `Nenhum relatório encontrado entre ${fmt(rangeStartDate)} e ${fmt(rangeEndDate)}.`,
                    type: 'error'
                });
                setIsGeneratingRange(false);
                setTimeout(() => setStatus({ message: '', type: '' }), 4000);
                return;
            }

            setStatus({ message: '📊 Gerando planilha...', type: 'info' });

            const buffer = await generateDateRangeReportXlsx(data, rangeStartDate, rangeEndDate);
            const blob = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            const s = rangeStartDate.replace(/-/g, '');
            const e = rangeEndDate.replace(/-/g, '');
            saveAs(blob, `TUV_Report_${s}_to_${e}.xlsx`);

            setStatus({
                message: `✅ Relatório gerado com ${data.length} relatório(s).`,
                type: 'success'
            });
            setShowRangeModal(false);
        } catch (err) {
            console.error('Date range report error:', err);
            setStatus({ message: `Erro: ${err.message}`, type: 'error' });
        } finally {
            setIsGeneratingRange(false);
            setTimeout(() => setStatus({ message: '', type: '' }), 4000);
        }
    };

    return (
        <AuthGuard>
            <div className={styles.pageContainer}>
                {isModalOpen && (
                    <div className={styles.modalBackdrop} onClick={closeModal}>
                        <div
                            className={styles.formContainer}
                            style={{
                                maxWidth: '900px',
                                maxHeight: '90vh',
                                overflowY: 'auto',
                                position: 'relative',
                                borderRadius: '16px',
                                padding: '0',
                                boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div style={{
                                padding: '24px 32px',
                                borderBottom: '1px solid #e9ecef',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                borderRadius: '16px 16px 0 0',
                            }}>
                                <div>
                                    <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 700 }}>
                                        Novo Relatório TÜV AUSTRIA
                                    </h2>
                                    <p style={{ margin: '4px 0 0 0', opacity: 0.8, fontSize: '14px' }}>
                                        Selecione uma avaliação ou insira o ID manualmente
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    disabled={loading}
                                    style={{
                                        background: 'rgba(255,255,255,0.15)',
                                        border: 'none',
                                        color: 'white',
                                        width: '36px',
                                        height: '36px',
                                        borderRadius: '50%',
                                        fontSize: '24px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        transition: 'all 0.2s',
                                        fontWeight: 300
                                    }}
                                    onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.25)'}
                                    onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.15)'}
                                >
                                    ×
                                </button>
                            </div>

                            <div style={{ padding: '24px 32px' }}>
                                {status.message && (
                                    <div style={{
                                        padding: '12px 16px',
                                        borderRadius: '10px',
                                        marginBottom: '20px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        background: status.type === 'error' ? '#fef3f2' :
                                            status.type === 'success' ? '#ecfdf5' : '#eff6ff',
                                        border: `1px solid ${status.type === 'error' ? '#fecaca' :
                                            status.type === 'success' ? '#a7f3d0' : '#bfdbfe'
                                            }`,
                                        color: status.type === 'error' ? '#dc2626' :
                                            status.type === 'success' ? '#065f46' : '#1e40af'
                                    }}>
                                        <span style={{ fontSize: '20px' }}>
                                            {status.type === 'error' ? '❌' : status.type === 'success' ? '✅' : 'ℹ️'}
                                        </span>
                                        <span style={{ fontSize: '14px', fontWeight: 500 }}>{status.message}</span>
                                    </div>
                                )}

                                <div style={{
                                    display: 'flex',
                                    gap: '0',
                                    background: '#f1f5f9',
                                    borderRadius: '12px',
                                    padding: '4px',
                                    marginBottom: '24px',
                                    position: 'relative'
                                }}>
                                    <button
                                        onClick={() => setSearchMode('list')}
                                        style={{
                                            flex: 1,
                                            padding: '10px 20px',
                                            border: 'none',
                                            borderRadius: '10px',
                                            background: searchMode === 'list' ? 'white' : 'transparent',
                                            color: searchMode === 'list' ? '#0d47a1' : '#64748b',
                                            cursor: 'pointer',
                                            fontWeight: 600,
                                            fontSize: '14px',
                                            transition: 'all 0.3s',
                                            boxShadow: searchMode === 'list' ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '8px'
                                        }}
                                    >
                                        <span>📋</span> Avaliações Recentes
                                    </button>
                                    <button
                                        onClick={() => setSearchMode('manual')}
                                        style={{
                                            flex: 1,
                                            padding: '10px 20px',
                                            border: 'none',
                                            borderRadius: '10px',
                                            background: searchMode === 'manual' ? 'white' : 'transparent',
                                            color: searchMode === 'manual' ? '#0d47a1' : '#64748b',
                                            cursor: 'pointer',
                                            fontWeight: 600,
                                            fontSize: '14px',
                                            transition: 'all 0.3s',
                                            boxShadow: searchMode === 'manual' ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '8px'
                                        }}
                                    >
                                        <span>🔍</span> Buscar por ID
                                    </button>
                                </div>

                                {searchMode === 'list' ? (
                                    <div style={{
                                        background: '#fafbfc',
                                        borderRadius: '12px',
                                        border: '1px solid #e9ecef',
                                        padding: '4px',
                                        maxHeight: '400px',
                                        overflow: 'auto'
                                    }}>
                                        <PendingEvaluationsList onSelect={handleSelectPending} />
                                    </div>
                                ) : (
                                    <div style={{
                                        background: '#fafbfc',
                                        borderRadius: '12px',
                                        border: '1px solid #e9ecef',
                                        padding: '24px'
                                    }}>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '14px',
                                            fontWeight: 600,
                                            color: '#1e293b',
                                            marginBottom: '8px'
                                        }}>
                                            ID da Avaliação (Evaluation ID)
                                        </label>
                                        <div style={{ display: 'flex', gap: '12px' }}>
                                            <input
                                                type="number"
                                                value={evaluationId}
                                                onChange={(e) => setEvaluationId(e.target.value)}
                                                placeholder="Ex: 12345"
                                                disabled={loading}
                                                style={{
                                                    flex: 1,
                                                    padding: '12px 16px',
                                                    border: '2px solid #e2e8f0',
                                                    borderRadius: '10px',
                                                    fontSize: '16px',
                                                    transition: 'all 0.2s',
                                                    outline: 'none',
                                                    background: 'white'
                                                }}
                                                onFocus={(e) => e.target.style.borderColor = '#0d47a1'}
                                                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                                            />
                                            <button
                                                type="button"
                                                onClick={handleFetchEvaluation}
                                                disabled={loading || !evaluationId}
                                                style={{
                                                    padding: '12px 28px',
                                                    background: loading || !evaluationId ? '#94a3b8' : '#0d47a1',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '10px',
                                                    fontSize: '14px',
                                                    fontWeight: 600,
                                                    cursor: loading || !evaluationId ? 'not-allowed' : 'pointer',
                                                    transition: 'all 0.2s',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    whiteSpace: 'nowrap'
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (!loading && evaluationId) {
                                                        e.target.style.background = '#1a237e';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (!loading && evaluationId) {
                                                        e.target.style.background = '#0d47a1';
                                                    }
                                                }}
                                            >
                                                {loading ? '⏳ Buscando...' : '🔍 Buscar'}
                                            </button>
                                        </div>
                                        <p style={{
                                            marginTop: '12px',
                                            fontSize: '12px',
                                            color: '#94a3b8'
                                        }}>
                                            Insira o ID da avaliação concluída no Checklist Fácil para extrair os dados automaticamente.
                                        </p>
                                    </div>
                                )}

                                {isDataLoaded && (
                                    <div style={{ marginTop: '24px' }}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '10px',
                                            marginBottom: '16px'
                                        }}>
                                            <span style={{ fontSize: '20px' }}>📋</span>
                                            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#1e293b' }}>
                                                Dados Extraídos da Avaliação
                                            </h3>
                                            <span style={{
                                                fontSize: '12px',
                                                padding: '2px 12px',
                                                borderRadius: '20px',
                                                background: '#dbeafe',
                                                color: '#1e40af',
                                                fontWeight: 500
                                            }}>
                                                {extractedData.perishableProducts?.length || 0} produtos
                                            </span>
                                            {extractedData.samplingGroupsCount > 0 && (
                                                <span style={{
                                                    fontSize: '12px',
                                                    padding: '2px 12px',
                                                    borderRadius: '20px',
                                                    background: '#e0e7ff',
                                                    color: '#4338ca',
                                                    fontWeight: 500
                                                }}>
                                                    {extractedData.samplingGroupsCount} grupos de amostragem
                                                </span>
                                            )}
                                            {extractedData.photoAttachments?.length > 0 && (
                                                <span style={{
                                                    fontSize: '12px',
                                                    padding: '2px 12px',
                                                    borderRadius: '20px',
                                                    background: '#dcfce7',
                                                    color: '#166534',
                                                    fontWeight: 500
                                                }}>
                                                    {extractedData.photoAttachments.length} fotos
                                                </span>
                                            )}
                                        </div>

                                        <div style={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                                            gap: '12px',
                                            marginBottom: '16px'
                                        }}>
                                            {[
                                                { label: 'Projeto', value: extractedData.projectNo || '-' },
                                                { label: 'Cliente', value: extractedData.clientName || '-' },
                                                { label: 'Cliente (Export)', value: extractedData.clientNameExport || '-' },
                                                { label: 'País', value: extractedData.countryOfImport || '-' },
                                                { label: 'Inspetor', value: extractedData.inspectorName || '-' },
                                                { label: 'Data Inspeção', value: extractedData.dateOfInspection || '-' },
                                                { label: 'Data Relatório', value: extractedData.dateOfReport || '-' },
                                                {
                                                    label: 'Resultado', value: extractedData.inspectionResult || '-',
                                                    isResult: true
                                                }
                                            ].map((item, idx) => (
                                                <div key={idx} style={{
                                                    background: '#f8fafc',
                                                    padding: '12px 16px',
                                                    borderRadius: '10px',
                                                    border: '1px solid #e9ecef'
                                                }}>
                                                    <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                        {item.label}
                                                    </div>
                                                    <div style={{
                                                        fontSize: '14px',
                                                        fontWeight: 600,
                                                        color: '#1e293b',
                                                        marginTop: '4px',
                                                        ...(item.isResult && item.value !== '-' ? {
                                                            display: 'inline-block',
                                                            padding: '2px 12px',
                                                            borderRadius: '20px',
                                                            fontSize: '12px',
                                                            background: item.value.toLowerCase() === 'satisfactory' ? '#dcfce7' :
                                                                item.value.toLowerCase() === 'unsatisfactory' ? '#fee2e2' : '#fef3c7',
                                                            color: item.value.toLowerCase() === 'satisfactory' ? '#166534' :
                                                                item.value.toLowerCase() === 'unsatisfactory' ? '#991b1b' : '#92400e'
                                                        } : {})
                                                    }}>
                                                        {item.value}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        <div style={{
                                            display: 'flex',
                                            gap: '12px',
                                            justifyContent: 'flex-end',
                                            borderTop: '1px solid #e9ecef',
                                            paddingTop: '20px'
                                        }}>
                                            <button
                                                type="button"
                                                onClick={closeModal}
                                                disabled={loading}
                                                style={{
                                                    padding: '10px 24px',
                                                    background: 'transparent',
                                                    color: '#64748b',
                                                    border: '1px solid #e2e8f0',
                                                    borderRadius: '10px',
                                                    fontSize: '14px',
                                                    fontWeight: 500,
                                                    cursor: loading ? 'not-allowed' : 'pointer',
                                                    transition: 'all 0.2s'
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (!loading) {
                                                        e.target.style.background = '#f1f5f9';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (!loading) {
                                                        e.target.style.background = 'transparent';
                                                    }
                                                }}
                                            >
                                                Cancelar
                                            </button>
                                            <button
                                                type="button"
                                                onClick={generateReport}
                                                disabled={loading}
                                                style={{
                                                    padding: '10px 28px',
                                                    background: loading ? '#94a3b8' : 'linear-gradient(135deg, #0d47a1, #1a237e)',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '10px',
                                                    fontSize: '14px',
                                                    fontWeight: 600,
                                                    cursor: loading ? 'not-allowed' : 'pointer',
                                                    transition: 'all 0.2s',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    boxShadow: loading ? 'none' : '0 4px 12px rgba(13,71,161,0.3)'
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (!loading) {
                                                        e.target.style.transform = 'translateY(-2px)';
                                                        e.target.style.boxShadow = '0 6px 20px rgba(13,71,161,0.4)';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (!loading) {
                                                        e.target.style.transform = 'translateY(0)';
                                                        e.target.style.boxShadow = '0 4px 12px rgba(13,71,161,0.3)';
                                                    }
                                                }}
                                            >
                                                {loading ? '⏳ Gerando...' : '📊 Gerar Relatório'}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {showDownloadModal && (
                    <div className={styles.modalBackdrop} onClick={() => !isDownloading && setShowDownloadModal(false)}>
                        <div
                            className={styles.formContainer}
                            style={{ maxWidth: '520px', padding: '24px 28px', borderRadius: '16px' }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: 700, color: '#1e293b' }}>
                                Choose Download Format
                            </h3>
                            <p style={{ margin: '0 0 20px 0', color: '#64748b', fontSize: '14px', lineHeight: 1.5 }}>
                                Select the format for the reports in the ZIP archive.
                                The ZIP will contain the folder{' '}
                                <code style={{ background: '#f1f5f9', padding: '1px 6px', borderRadius: '4px' }}>
                                    {reportToDownload?.reportFolder || 'Report_<INVOICE>_<CONTAINER>'}
                                </code>{' '}
                                with subfolders <strong>Inspection_Report</strong>,{' '}
                                <strong>Sampling_Data_Sheet</strong>,{' '}
                                <strong>Request_For_Certification</strong>, and <strong>Fotos</strong>.
                            </p>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                <button
                                    disabled={isDownloading}
                                    onClick={() => handleConfirmDownload('excel')}
                                    style={{
                                        padding: '12px 20px',
                                        background: isDownloading ? '#94a3b8' : 'linear-gradient(135deg, #0d47a1, #1a237e)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '10px',
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        cursor: isDownloading ? 'not-allowed' : 'pointer',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    📊 Download as Excel
                                </button>
                                <button
                                    disabled={isDownloading}
                                    onClick={() => handleConfirmDownload('pdf')}
                                    style={{
                                        padding: '12px 20px',
                                        background: isDownloading ? '#94a3b8' : 'linear-gradient(135deg, #b91c1c, #7f1d1d)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '10px',
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        cursor: isDownloading ? 'not-allowed' : 'pointer',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    📄 Download as PDF
                                </button>
                                {!isDownloading && (
                                    <button
                                        onClick={() => setShowDownloadModal(false)}
                                        style={{
                                            padding: '10px 20px',
                                            background: 'transparent',
                                            color: '#64748b',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '10px',
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            cursor: 'pointer',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        Cancel
                                    </button>
                                )}
                            </div>

                            {isDownloading && downloadProgress && (
                                <DownloadProgress
                                    progress={downloadProgress}
                                    format={downloadFormat}
                                />
                            )}
                        </div>
                    </div>
                )}

                {showHistoryModal && reportToViewHistory && (
                    <DownloadHistoryModal
                        report={reportToViewHistory}
                        onClose={() => {
                            setShowHistoryModal(false);
                            setReportToViewHistory(null);
                        }}
                    />
                )}

                {showFolderDetailModal && reportToViewFiles && (
                    <FolderUploadDetailModal
                        report={reportToViewFiles}
                        onClose={() => {
                            setShowFolderDetailModal(false);
                            setReportToViewFiles(null);
                        }}
                    />
                )}

                {showMonthlyModal && (
                    <div
                        className={styles.modalBackdrop}
                        onClick={() => !isGeneratingMonthly && setShowMonthlyModal(false)}
                    >
                        <div
                            className={styles.formContainer}
                            style={{ maxWidth: '460px', padding: '24px 28px', borderRadius: '16px' }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <h3 style={{
                                margin: '0 0 8px 0', fontSize: '20px',
                                fontWeight: 700, color: '#1e293b'
                            }}>
                                📅 Relatório Mensal
                            </h3>
                            <p style={{ margin: '0 0 20px 0', color: '#64748b', fontSize: '14px', lineHeight: 1.5 }}>
                                Selecione o mês e o ano. Será gerada uma planilha Excel com
                                todos os relatórios feitos nesse período, incluindo status de
                                download e informações principais.
                            </p>

                            <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
                                <div style={{ flex: 1 }}>
                                    <label style={{
                                        display: 'block', fontSize: '12px', fontWeight: 600,
                                        color: '#64748b', marginBottom: '6px',
                                        textTransform: 'uppercase', letterSpacing: '0.5px'
                                    }}>Mês</label>
                                    <select
                                        value={monthlyMonth}
                                        onChange={(e) => setMonthlyMonth(Number(e.target.value))}
                                        disabled={isGeneratingMonthly}
                                        style={{
                                            width: '100%', padding: '10px 12px',
                                            border: '2px solid #e2e8f0', borderRadius: '8px',
                                            fontSize: '14px', background: '#f8fafc', outline: 'none'
                                        }}
                                    >
                                        {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                                            <option key={m} value={m}>
                                                {new Date(2000, m - 1, 1)
                                                    .toLocaleString('pt-BR', { month: 'long' })}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label style={{
                                        display: 'block', fontSize: '12px', fontWeight: 600,
                                        color: '#64748b', marginBottom: '6px',
                                        textTransform: 'uppercase', letterSpacing: '0.5px'
                                    }}>Ano</label>
                                    <select
                                        value={monthlyYear}
                                        onChange={(e) => setMonthlyYear(Number(e.target.value))}
                                        disabled={isGeneratingMonthly}
                                        style={{
                                            width: '100%', padding: '10px 12px',
                                            border: '2px solid #e2e8f0', borderRadius: '8px',
                                            fontSize: '14px', background: '#f8fafc', outline: 'none'
                                        }}
                                    >
                                        {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i)
                                            .map(y => <option key={y} value={y}>{y}</option>)}
                                    </select>
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button
                                    disabled={isGeneratingMonthly}
                                    onClick={() => setShowMonthlyModal(false)}
                                    style={{
                                        flex: 1, padding: '10px 20px', background: 'transparent',
                                        color: '#64748b', border: '1px solid #e2e8f0',
                                        borderRadius: '10px', fontSize: '14px',
                                        cursor: isGeneratingMonthly ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    Cancelar
                                </button>
                                <button
                                    disabled={isGeneratingMonthly}
                                    onClick={handleGenerateMonthlyReport}
                                    style={{
                                        flex: 1, padding: '10px 20px',
                                        background: isGeneratingMonthly
                                            ? '#94a3b8'
                                            : 'linear-gradient(135deg, #b45309, #78350f)',
                                        color: 'white', border: 'none', borderRadius: '10px',
                                        fontSize: '14px', fontWeight: 600,
                                        cursor: isGeneratingMonthly ? 'not-allowed' : 'pointer',
                                        boxShadow: isGeneratingMonthly
                                            ? 'none'
                                            : '0 4px 12px rgba(180,83,9,0.3)'
                                    }}
                                >
                                    {isGeneratingMonthly ? '⏳ Gerando...' : '📊 Gerar Relatório'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {showRangeModal && (
                    <div
                        className={styles.modalBackdrop}
                        onClick={() => !isGeneratingRange && setShowRangeModal(false)}
                    >
                        <div
                            className={styles.formContainer}
                            style={{ maxWidth: '460px', padding: '24px 28px', borderRadius: '16px' }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <h3 style={{
                                margin: '0 0 8px 0', fontSize: '20px',
                                fontWeight: 700, color: '#1e293b'
                            }}>
                                📆 Relatório por Período
                            </h3>
                            <p style={{ margin: '0 0 20px 0', color: '#64748b', fontSize: '14px', lineHeight: 1.5 }}>
                                Selecione a data inicial e a data final. Será gerada uma planilha
                                Excel com todos os relatórios feitos nesse intervalo, incluindo
                                status de download e informações principais.
                            </p>

                            <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
                                <div style={{ flex: 1 }}>
                                    <label style={{
                                        display: 'block', fontSize: '12px', fontWeight: 600,
                                        color: '#64748b', marginBottom: '6px',
                                        textTransform: 'uppercase', letterSpacing: '0.5px'
                                    }}>Data Inicial</label>
                                    <input
                                        type="date"
                                        value={rangeStartDate}
                                        onChange={(e) => setRangeStartDate(e.target.value)}
                                        disabled={isGeneratingRange}
                                        style={{
                                            width: '100%', padding: '10px 12px',
                                            border: '2px solid #e2e8f0', borderRadius: '8px',
                                            fontSize: '14px', background: '#f8fafc', outline: 'none'
                                        }}
                                    />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label style={{
                                        display: 'block', fontSize: '12px', fontWeight: 600,
                                        color: '#64748b', marginBottom: '6px',
                                        textTransform: 'uppercase', letterSpacing: '0.5px'
                                    }}>Data Final</label>
                                    <input
                                        type="date"
                                        value={rangeEndDate}
                                        onChange={(e) => setRangeEndDate(e.target.value)}
                                        disabled={isGeneratingRange}
                                        style={{
                                            width: '100%', padding: '10px 12px',
                                            border: '2px solid #e2e8f0', borderRadius: '8px',
                                            fontSize: '14px', background: '#f8fafc', outline: 'none'
                                        }}
                                    />
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button
                                    disabled={isGeneratingRange}
                                    onClick={() => setShowRangeModal(false)}
                                    style={{
                                        flex: 1, padding: '10px 20px', background: 'transparent',
                                        color: '#64748b', border: '1px solid #e2e8f0',
                                        borderRadius: '10px', fontSize: '14px',
                                        cursor: isGeneratingRange ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    Cancelar
                                </button>
                                <button
                                    disabled={isGeneratingRange}
                                    onClick={handleGenerateRangeReport}
                                    style={{
                                        flex: 1, padding: '10px 20px',
                                        background: isGeneratingRange
                                            ? '#94a3b8'
                                            : 'linear-gradient(135deg, #047857, #064e3b)',
                                        color: 'white', border: 'none', borderRadius: '10px',
                                        fontSize: '14px', fontWeight: 600,
                                        cursor: isGeneratingRange ? 'not-allowed' : 'pointer',
                                        boxShadow: isGeneratingRange
                                            ? 'none'
                                            : '0 4px 12px rgba(4,120,87,0.3)'
                                    }}
                                >
                                    {isGeneratingRange ? '⏳ Gerando...' : '📊 Gerar Relatório'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {showAttachModal && reportToAttach && canImport && (
                    <AttachDocumentsModal
                        report={reportToAttach}
                        onClose={() => { setShowAttachModal(false); setReportToAttach(null); }}
                        onComplete={() => {
                            setStatus({ message: '📎 Documentos anexados com sucesso!', type: 'success' });
                            setTimeout(() => setStatus({ message: '', type: '' }), 3000);
                            loadReports();
                        }}
                    />
                )}

                {showFolderUploadModal && canImport && (
                    <FolderUploadModal
                        onClose={() => setShowFolderUploadModal(false)}
                        onUploadComplete={() => {
                            setStatus({
                                message: '✅ Pasta enviada com sucesso! Os arquivos estão disponíveis no Firebase Storage.',
                                type: 'success'
                            });
                            setTimeout(() => setStatus({ message: '', type: '' }), 4000);
                            loadReports();
                        }}
                    />
                )}

                <div className={styles.header}>
                    <div>
                        <h1 className={styles.headerTitle}>Relatório de Inspeção TÜV AUSTRIA</h1>
                        <p style={{ color: '#666', marginTop: '4px' }}>Gerar relatório a partir de avaliação concluída no Checklist Fácil</p>
                    </div>
                    <HeaderActionsDropdown
                        disabled={loading}
                        canImport={canImport}
                        onNewReport={openModal}
                        onFolderUpload={() => setShowFolderUploadModal(true)}
                        onMonthlyReport={() => setShowMonthlyModal(true)}
                        onRangeReport={() => {
                            setRangeStartDate('');
                            setRangeEndDate('');
                            setShowRangeModal(true);
                        }}
                    />
                </div>

                <div className={styles.reportsSection}>
                    <ReportsTable
                        reports={reports}
                        loading={reportsLoading}
                        onDownload={handleDownloadClick}
                        onDelete={handleDeleteReport}
                        onRefresh={loadReports}
                        onShowHistory={handleShowHistory}
                        onShowFolderFiles={handleShowFolderFiles}
                        canDelete={canDelete}
                        canAttach={canImport}
                        onAttach={handleAttachClick}
                    />
                </div>
            </div>
        </AuthGuard>
    );
};

export default TUVReportPage;